// packages/web/src/lib/api.ts — A2.1b: the PWA's one HTTP client. Every
// fetch in this app goes through here, same-origin (packages/server's
// staticWeb plugin serves this app from the SAME origin as /api|/auth —
// see that plugin's own header for why: it's what makes the session
// cookie "just work" with zero CORS wiring). `credentials: 'same-origin'`
// is technically the browser default already for a same-origin fetch, but
// stated explicitly here since it's the one property this whole
// architecture leans on.
//
// Endpoints wired this chunk (mapped against the server's actual routes —
// see packages/server/src/plugins/auth.ts, attention.ts, proposals.ts):
//   POST /auth/magic-link/request  {email}          -> 202 {ok:true}
//   POST /auth/magic-link/verify   {token}          -> {user, isNewUser, recovered}
//   GET  /auth/whoami                               -> {user} | 401
//   POST /auth/logout                                -> {ok:true}
//   GET  /api/attention            (X-Tenant-Id)    -> {attentionItems: [...]}
//   GET  /api/proposals?status=    (X-Tenant-Id)    -> {proposals: [...], limit, offset}
// Approve/reject exist server-side (POST /api/proposals/:id/approve|reject)
// but are NOT called yet — this chunk renders proposals read-only (the
// WRITE loop is the next chunk); Today.tsx wires those buttons to a no-op.
//
// Error shape: two conventions coexist on the server (auth.ts's own header
// notes this) — /auth/* still uses the OLDER flat `{error: string,
// message?}` shape, while every /api/* route uses the newer `{error:
// {code, message}}` shape. ApiError below normalizes both into one shape
// so a caller never has to know which namespace it hit.
export class ApiError extends Error {
  status: number;
  code: string;
  /** C6b: the parsed response body, when there was one. The authority
   * gate's 409 is a ROUTING INSTRUCTION, not an error report — its body
   * ({authority, routeTo, missingCapability, fileAsk…}) carries no
   * `error` envelope at all, so a caller rendering the offer needs the
   * body itself, not a code this normalizer had to invent for it. */
  body?: unknown;
  /** ⚑ THE SERVER'S OWN SENTENCE, AND ONLY WHEN THERE WAS ONE (2026-09-16).
   *
   * `message` above always holds something renderable — it falls back to the
   * error CODE, and then to "request failed", so that a caller logging it
   * never logs `undefined`. That fallback is exactly what makes `message`
   * unsafe to put in front of a person: "invalid_invite" and "request failed"
   * are not sentences anyone should read.
   *
   * This field is set ONLY when the response body actually carried a written
   * message. It is what the voice canon means by "show the server's own
   * sentence, never a vaguer second copy" — and its absence is how a caller
   * knows to fall back to its own words instead. */
  serverMessage?: string;
  constructor(status: number, code: string, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

interface FlatErrorBody {
  error?: string;
  message?: string;
}
interface NestedErrorBody {
  error?: { code?: string; message?: string };
}

function writtenSentence(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function extractError(status: number, body: unknown): ApiError {
  if (body && typeof body === 'object') {
    const nested = (body as NestedErrorBody).error;
    if (nested && typeof nested === 'object') {
      const err = new ApiError(status, nested.code ?? 'unknown_error', nested.message ?? 'request failed', body);
      err.serverMessage = writtenSentence(nested.message);
      return err;
    }
    const flat = body as FlatErrorBody;
    if (typeof flat.error === 'string') {
      const err = new ApiError(status, flat.error, flat.message ?? flat.error, body);
      err.serverMessage = writtenSentence(flat.message);
      return err;
    }
  }
  return new ApiError(status, 'unknown_error', 'request failed', body ?? undefined);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  tenantId?: string | null;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers = new Headers();
  // Declare a JSON body ONLY when there actually is one. This used to be set
  // unconditionally, which made every bodyless POST through this helper fail:
  // Fastify's JSON parser rejects an empty body carrying
  // `Content-Type: application/json` with "Body cannot be empty when
  // content-type is set to 'application/json'". Approving a proposal is
  // exactly that shape — POST, no body — so the Knot could capture and
  // propose but never file (found on a real device 2026-08-04).
  // rejectProposal had been carrying `body: {}` to dodge the same thing at
  // one call site; that is the symptom, this is the cause.
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json');
  if (opts.tenantId) headers.set('X-Tenant-Id', opts.tenantId);

  const resp = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'same-origin',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;

  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as T;
}

export interface ApiUser {
  id: string;
  email: string;
  name: string | null;
  /** True when a password credential EXISTS for this account (legacy Auth-2
   * accounts hold one even on packs that no longer mint passwords). Optional:
   * an older server omits it, and absent must read as "unknown", not "no". */
  hasPassword?: boolean;
  /** A beta-door account's performer name, from its invite request — the
   *  onboarding name step's pre-fill only. Never the account holder's own
   *  name: on CallToWrap the holder is often a parent signing up a minor. */
  performerNameHint?: string;
}

export async function requestMagicLink(email: string): Promise<void> {
  await request('/auth/magic-link/request', { method: 'POST', body: { email } });
}

export async function verifyMagicLink(token: string): Promise<{ user: ApiUser; isNewUser: boolean }> {
  return request('/auth/magic-link/verify', { method: 'POST', body: { token } });
}

// ---------------------------------------------------------------------------
// B1 — the beta door. Backend: packages/server/src/plugins/betaDoor.ts.
// ---------------------------------------------------------------------------

/** POST /beta/request — always 202, same no-oracle posture as
 * requestMagicLink above (Login.tsx's request stage shows the same receipt
 * whatever the address). */
export async function requestBetaInvite(email: string): Promise<void> {
  await request('/beta/request', { method: 'POST', body: { email } });
}

/** GET /beta/invite/:token — the invite screen's own prefill read. Throws
 * ApiError with code `invite_invalid`/`invite_expired`/`invite_used` for a
 * bad link. */
export async function getBetaInvite(token: string): Promise<{ email: string }> {
  return request(`/beta/invite/${encodeURIComponent(token)}`);
}

export interface ConfirmBetaInviteResult {
  accountMinted: boolean;
  /** Only present when `accountMinted` is false — the different-address
   * branch's own receipt (B1 §5), the server's own sentence. */
  receipt?: string;
}

export async function confirmBetaInvite(
  token: string,
  args: { heardFrom: string; performerName: string; accountEmail: string }
): Promise<ConfirmBetaInviteResult> {
  return request(`/beta/invite/${encodeURIComponent(token)}/confirm`, { method: 'POST', body: args });
}

/** Never throws on 401 — an absent session is an expected, ordinary
 * outcome for this call (it's how the app tells "logged out" from "logged
 * in"), not an error. Any OTHER failure (network, 5xx) still throws. */
export async function whoami(): Promise<ApiUser | null> {
  try {
    const result = await request<{ user: ApiUser }>('/auth/whoami');
    return result.user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

/** PATCH /auth/name — the one identity fact a person owns about themselves.
 *
 * ⚠ NOT tenant-scoped, deliberately: a name belongs to the PERSON, not to one
 * of their households, and the server takes the id from the SESSION rather
 * than from the body. Until this existed, only the first-run script could set
 * a name — so any pack declaring no first run (actor_assist declares none)
 * left every member displayed as their email local part, permanently. */
export async function updateMyName(name: string): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>('/auth/name', { method: 'PATCH', body: { name } });
  return result.user;
}

/** B1 — Onboarding.tsx's own setup-age step ("Is {performerName} 18 or
 * over?"), for actor_assist. Records against the SIGNED-IN user (the
 * account holder — an account is one career, migration 0142), same "the
 * session names whose fact this is" posture updateMyName takes. */
export async function recordAgeAttestation(isAdult: boolean): Promise<void> {
  await request('/auth/age-attestation', { method: 'POST', body: { isAdult } });
}

export async function logout(): Promise<void> {
  await request('/auth/logout', { method: 'POST' });
}

/** POST /auth/password/change — the caller is already signed in and proves
 * the CURRENT password; a password-less account is refused server-side. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await request('/auth/password/change', { method: 'POST', body: { currentPassword, newPassword } });
}

// ---------------------------------------------------------------------------
// The sign-in + onboarding surfaces. C6a replaced the Auth-2 wall of options
// with the wf05 email-first door: the pack-declared entry copy, the
// which-door probe (POST /auth/methods), passkeys (Face/Touch ID) and the
// magic-link door above, plus tenant resolution/creation. The password
// ROUTES still exist server-side (the door changed, not the locks —
// changePassword above still talks to them); the client functions for
// password login/register/reset left with the old Login screen, their only
// consumer. Backend: packages/server/src/plugins/auth.ts.
// ---------------------------------------------------------------------------

/** C6a — the front door's pack-declared copy (GET /auth/entry). The shape
 * mirrors @vowric/shared's EntryPackConfig, duplicated here because this
 * package deliberately doesn't depend on the backend workspace (the same
 * convention as App.tsx's MAGIC_LINK_VERIFY_PATH). null = the deployment's
 * pack declares no entry voice; the caller renders neutral defaults. Only
 * `welcome` is guaranteed when present; troubleLine/reassurance/recordNotice
 * render ONLY when a pack actually declared them. */
export interface EntryConfig {
  productName?: string;
  byline?: string;
  welcome: string;
  emailLabel?: string;
  troubleLine?: string;
  reassurance?: string;
  recordNotice?: string;
  /** ⚠ ABSENT MEANS NO PASSWORD DOOR, and that is the truth on every build
   * today — C6a's front door is a magic link and then a passkey. A form that
   * changes a password nobody has can only answer "Current password is
   * incorrect". */
  passwordSignIn?: boolean;
  /** The pack's voice on the preferences screen. Mirrors @vowric/shared's
   * SettingsPackConfig for the same reason the block above mirrors
   * EntryPackConfig: this package has no backend workspace dependency. */
  settings?: {
    pushFallbackNote?: string;
    notify?: {
      chat?: { label: string; sub: string };
      work?: { label: string; sub: string };
      attention?: { label: string; sub: string };
    };
    /** ⚑ `seats` ARRIVED 2026-09-11 (G-18) AND `label` BECAME OPTIONAL WITH
     * IT. The door used to be ONE row for ONE seat, which is why a pack
     * seating two kinds of person — actor_assist's second adult and its
     * helper — could only ever offer the first, and `helper` (a declared job
     * role, with its own rail and its own read scope) appeared nowhere in the
     * app. A pack that declares `seats` gets a row each, in ITS OWN WORDS for
     * each one; a pack that declares `label` alone keeps the single row it
     * always had, byte for byte. */
    invite?: {
      section: string;
      mini?: string;
      label?: string;
      sub?: string;
      seats?: { jobRole: string; label: string; sub?: string }[];
    };
    memberRecord?: boolean;
  };
}

export async function getEntryConfig(): Promise<EntryConfig | null> {
  const result = await request<{ entry: EntryConfig | null }>('/auth/entry');
  return result.entry;
}

/** The deployment's own vertical (packs[0].key), served beside the entry
 * copy since the 2026-08-10 walk found signup onboarding hardcoding
 * 'actors' onto a Rexx build. null = a pre-packs build; callers keep their
 * legacy behavior on null, never guess. */
export async function getDeploymentVertical(): Promise<string | null> {
  const result = await request<{ vertical?: string | null }>('/auth/entry');
  return result.vertical ?? null;
}

/**
 * Does this DEPLOYMENT have inbound mail wired at all?
 *
 * ⚑ ASKED PUBLICLY, ONCE, BEFORE ANY TENANT-SCOPED CALL. `/auth/entry` already
 * reports the mail-in domain, and a deployment with none cannot give any
 * household an address — so there is no point asking `/api/mailbox` about a
 * particular household first. Onboarding used to do exactly that, which put a
 * network call into a flow that had no reason to make one.
 */
export async function getMailInDomain(): Promise<string | null> {
  const result = await request<{ mailInDomain?: string | null }>('/auth/entry');
  return result.mailInDomain ?? null;
}

/** B1 — is this deployment's sign-in screen in 'beta' mode (a request door,
 * hand-approved) or 'open' (today's behaviour, every deployment that has
 * never set the `signup.mode` lever)? Never hardcoded to a product name —
 * Login.tsx's request link renders on whatever deployment this answers
 * 'beta' for. */
export async function getSignupMode(): Promise<'open' | 'beta'> {
  const result = await request<{ signupMode?: 'open' | 'beta' }>('/auth/entry');
  return result.signupMode ?? 'open';
}

/** B16 §6 — does this DEPLOYMENT have a knot intent door at all? `false` on
 * every build whose pack declares no `knotIntent` config (Actor/CallToWrap
 * today), which is exactly when `POST /api/knot/intent` 404s
 * (packages/server/src/app.ts registers that plugin only when the config is
 * present). useKnotPushToTalk reads this once so a voice hold on a build
 * with no such door goes straight to the transcript-in-sheet fallback
 * instead of learning the 404 itself, every single hold. */
export async function getKnotIntentEnabled(): Promise<boolean> {
  const result = await request<{ knotIntent?: boolean }>('/auth/entry');
  return result.knotIntent ?? false;
}

/** GAP 57 readiness (2026-09-29) — the record types a capture may land ON
 * in this deployment's pack (the keys of its `captureSubjects`). The Knot's
 * page context names one of these as its `subjectType`; the server keeps a
 * subject only when that type equals the record's real type, so a shell that
 * guesses another pack's type drops every capture to the inbox. null = the
 * deployment declares none (or predates this field) — lib/captureSubject.ts
 * decides what to send then. */
export async function getCaptureSubjectTypes(): Promise<string[] | null> {
  const result = await request<{ captureSubjects?: string[] | null }>('/auth/entry');
  return Array.isArray(result.captureSubjects) ? result.captureSubjects : null;
}

/** C6a — POST /auth/methods {email}: the which-door probe (wf05: "one field,
 * then the best door for that person"). ONE bit comes back: offer the passkey
 * ceremony first, or go straight to the magic-link door. The server shapes
 * this so it is NOT an account-existence oracle (unknown email and known-
 * email-without-passkey answer byte-identically {passkey:false}); the caller
 * must keep that property by showing the SAME magic-link door with the SAME
 * copy for every {passkey:false}. */
export async function emailAuthMethods(email: string): Promise<{ passkey: boolean }> {
  return request<{ passkey: boolean }>('/auth/methods', { method: 'POST', body: { email } });
}

/** POST /auth/password/login — sets the session on success, exactly like
 * passkeyLoginVerify/verifyMagicLink. Throws ApiError(401,
 * 'invalid_credentials') on a wrong email/password; the server's message is
 * the same generic sentence either way (anti-enumeration — see the route's
 * own comment), so the caller shows it verbatim rather than composing a
 * second one. Added 2026-09-20: the password DOOR never left (this route
 * has answered 200 all along), only the front door's UI stopped offering
 * it — C6a (wf05 §01, blessed 2026-08-06) replaced the old four-method wall
 * with email-first + passkey/magic-link, and dropped the password field in
 * the same pass. This brings back a path for anyone who was HANDED a
 * password (a demo account, an invite) and has nowhere else to type it;
 * magic link stays the default door for everyone else. */
export async function passwordLogin(email: string, password: string): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>('/auth/password/login', {
    method: 'POST',
    body: { email, password },
  });
  return result.user;
}

/** One tenant membership the signed-in caller belongs to (GET /auth/tenants). */
export interface TenantMembership {
  tenantId: string;
  name: string;
  slug: string | null;
  vertical: string;
  role: string;
  /** ⚑ IS THIS HOUSEHOLD STILL BEING SET UP? (2026-09-16) — the server's
   * `tenants.settings.setup_in_progress`, on from the moment POST
   * /auth/tenants creates the tenant until the onboarding screen reaches its
   * end and calls `markSetupFinished`. The boot path reads it from THIS list
   * (a request every signed-in boot already makes) and resumes the walk
   * instead of assuming a reload meant "finished".
   *
   * Optional: an older server omits it, and absent must read as "not in
   * progress" — never resume a walk on a server that cannot say. */
  setupInProgress?: boolean;
}

export async function listTenants(): Promise<TenantMembership[]> {
  const result = await request<{ tenants: TenantMembership[] }>('/auth/tenants');
  return result.tenants;
}

/** POST /auth/tenants/setup-finished — "setting up is over", said out loud.
 *
 * Called by Onboarding at every one of its exits, so that a later reload can
 * tell a finished household from an interrupted one. See the route's own
 * header for why nothing else in the product could answer that question.
 * Never blocks the person: a caller treats a failure as "ask again next
 * time", which is the safe direction. */
export async function markSetupFinished(tenantId: string): Promise<void> {
  await request('/auth/tenants/setup-finished', { method: 'POST', tenantId });
}

/** POST /auth/tenants — create a workspace. `vertical` is set for the caller
 * (the actor never sees the word); returns the new tenantId to store + send as
 * X-Tenant-Id thereafter. */
export async function createTenant(
  name: string,
  vertical: string,
  slug?: string
): Promise<{ tenantId: string; membershipId: string; vertical?: string }> {
  return request<{ tenantId: string; membershipId: string; vertical?: string }>('/auth/tenants', {
    method: 'POST',
    body: slug ? { name, vertical, slug } : { name, vertical },
  });
}

/** PUT /api/actor/profile — upsert the singleton profile (name + union here). */
export async function updateActorProfile(
  tenantId: string,
  fields: Record<string, unknown>
): Promise<{ id: string; fields: Record<string, unknown> }> {
  const result = await request<{ profile: { id: string; fields: Record<string, unknown> } }>(
    '/api/actor/profile',
    { method: 'PUT', tenantId, body: { fields } }
  );
  return result.profile;
}

// ---- Passkeys (Face/Touch ID). The server hands back WebAuthn ceremony
// options; the browser (@simplewebauthn/browser) drives the authenticator and
// posts the response back for verification. `options` is passed through
// verbatim to startAuthentication/startRegistration. ---------------------------

/** POST /auth/passkey/login/options — begin an authentication ceremony. */
export async function passkeyLoginOptions(
  email: string
): Promise<{ ceremonyId: string; options: unknown }> {
  return request<{ ceremonyId: string; options: unknown }>('/auth/passkey/login/options', {
    method: 'POST',
    body: { email },
  });
}

/** POST /auth/passkey/login/verify — finish authentication; sets the session. */
export async function passkeyLoginVerify(ceremonyId: string, response: unknown): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>('/auth/passkey/login/verify', {
    method: 'POST',
    body: { ceremonyId, response },
  });
  return result.user;
}

/** POST /auth/passkey/register/options — begin a registration ceremony (the
 * caller must already be signed in). */
export async function passkeyRegisterOptions(): Promise<{ ceremonyId: string; options: unknown }> {
  return request<{ ceremonyId: string; options: unknown }>('/auth/passkey/register/options', {
    method: 'POST',
    body: {},
  });
}

/** POST /auth/passkey/register/verify — finish registration. */
export async function passkeyRegisterVerify(ceremonyId: string, response: unknown): Promise<void> {
  await request('/auth/passkey/register/verify', { method: 'POST', body: { ceremonyId, response } });
}

export type AttentionSeverity = 'info' | 'warning' | 'critical';

export interface AttentionItem {
  id: string;
  userId: string | null;
  itemType: string;
  objectType: string | null;
  objectId: string | null;
  severity: AttentionSeverity;
  reason: string | null;
  /** Fix tranche 6, item 6 (walk S7.10): the server's own short, product-worded
   * title for this item TYPE — the same table an email subject already reads
   * from (core's `attentionTitleFor`). `reason` is written for MOST types as a
   * good, specific single-line sentence ("Commitment 'X' is 3 days overdue")
   * and stays the Today tile's title for those. A few types (the file-scanning
   * trio: malware_detected, scan_unavailable, parse_unavailable) write `reason`
   * in an operator/audit voice instead — this field is what a household member
   * reads there. Optional only because older/hand-built fixtures in this
   * codebase's own tests predate the field — a live server always sends it,
   * resolving every item_type to at least a generic fallback ("Something
   * needs attention"). */
  title?: string;
  sourceRef: Record<string, unknown> | null;
  dedupKey: string;
  createdAt: string;
  resolvedAt: string | null;
  lastRaisedAt: string;
}

export async function listAttentionItems(tenantId: string): Promise<AttentionItem[]> {
  const result = await request<{ attentionItems: AttentionItem[] }>('/api/attention', { tenantId });
  return result.attentionItems;
}

/** POST /api/attention/:id/resolve — mark one attention item handled.
 * S8: the tech screen's ticket verify clears its expiry item through
 * this, the SAME door Today's list resolves through — no bespoke path. */
export async function resolveAttentionItem(tenantId: string, id: string): Promise<AttentionItem> {
  const result = await request<{ attentionItem: AttentionItem }>(
    `/api/attention/${encodeURIComponent(id)}/resolve`,
    { method: 'POST', tenantId }
  );
  return result.attentionItem;
}

export type ProposalStatus = 'pending' | 'approved' | 'rejected' | 'expired';

export interface Proposal {
  id: string;
  kind: string;
  targetType: string;
  targetId: string | null;
  proposed: Record<string, unknown>;
  rationale: string | null;
  sourceRef: Record<string, unknown> | null;
  confidence: number | null;
  status: ProposalStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
  createdAt: string;
}

export async function listProposals(tenantId: string, status: ProposalStatus = 'pending'): Promise<Proposal[]> {
  const result = await request<{ proposals: Proposal[] }>(`/api/proposals?status=${status}`, { tenantId });
  return result.proposals;
}

/** GET /api/proposals/:id — the single staged proposal, viewer-gated and
 * RLS-filtered exactly like the list above (packages/server/src/plugins/
 * proposals.ts). B16 §1: the capture sheet reads this the moment
 * `captureKnot` returns, so its own card can render the SAME words Review
 * does (lib/proposals.ts's helpers) about the SAME proposal, rather than a
 * second, thinner vocabulary built from `CaptureResult.preview` alone.
 * Throws ApiError(404) when the id is unknown or belongs to another
 * tenant — the caller decides what "could not load" says. */
export async function getProposal(tenantId: string, id: string): Promise<Proposal> {
  const result = await request<{ proposal: Proposal }>(`/api/proposals/${id}`, { tenantId });
  return result.proposal;
}

/** POST /api/proposals/:id/approve — the propose->approve primitive resolved
 * INLINE from a Today card. Materializes the proposal's commitment and returns
 * its identity (packages/server/src/plugins/proposals.ts), so the card can
 * refresh straight to the settled state without a second round-trip. */
export interface ApproveProposalResult {
  proposalId: string;
  commitment: { targetType: string; id: string | null };
}

export async function approveProposal(tenantId: string, id: string): Promise<ApproveProposalResult> {
  return request<ApproveProposalResult>(`/api/proposals/${id}/approve`, { method: 'POST', tenantId });
}

/** POST /api/proposals/:id/reject — the "Decline" side of the same inline
 * decision. reason is optional (the server's rejectBodySchema accepts an empty
 * body). */
export async function rejectProposal(
  tenantId: string,
  id: string,
  reason?: string
): Promise<{ proposalId: string }> {
  return request<{ proposalId: string }>(`/api/proposals/${id}/reject`, {
    method: 'POST',
    tenantId,
    body: reason ? { reason } : {},
  });
}

// ---------------------------------------------------------------------------
// Publish PC2 — the approval queue's `post_document` card. A candidate is a
// `proposal` (targetType 'post_document') carrying the RENDERED text, its
// per-sentence citations, its compliance fields, its similarity reading and
// its subject's consent standing — everything the card needs is already on
// `proposed`, no second fetch. See packages/core/src/publish/candidates.ts
// and the PC2 spec's design §1/§6 for the shape's own authority.
// ---------------------------------------------------------------------------

export interface PostDocumentCitationApi {
  id: string;
  tenant_id: string;
  artifact_type: string;
  artifact_id: string;
  tier: 'instruction' | 'framing' | 'world' | 'live';
  source_kind: 'document' | 'media' | 'record' | 'url';
  source_ref: string;
  span: string | null;
  captured_at: string | null;
  expires_at: string | null;
  permission: 'public' | 'granted' | 'none';
  jurisdiction: string | null;
  added_by: string | null;
  created_at: string;
}

export interface PostDocumentSentenceApi {
  text: string;
  tier: 'instruction' | 'framing' | 'world' | 'live';
  paraphrase?: boolean;
  jurisdictionRequired?: boolean;
  quotedPrivate?: boolean;
  /** Indices into the SAME proposal's `citations` array — absent means
   * every citation on the candidate counts (publish/rail.ts's own rule). */
  citationIndexes?: number[];
}

export interface PostDocumentSubjectApi {
  record_type: string;
  record_id: string;
  /** The record the post is about, in its own words — the card's "About"
   * line (design §6). Always present: the route already has the record in
   * hand from its own 404 check before a candidate is ever built. */
  record_display_name: string;
  consent: 'given' | 'missing' | 'expired' | 'not_applicable';
  subject_name: string | null;
}

export interface PostDocumentProposedApi {
  channel: string;
  text: string;
  sentences: PostDocumentSentenceApi[];
  citations: PostDocumentCitationApi[];
  disclosure: 'none' | 'gifted' | 'paid' | 'affiliate';
  ai_visual: boolean;
  minors_flag: boolean;
  similarity: { near_duplicates_30d: number; of: string[] };
  lint: { personal_attributes: string[] };
  subject: PostDocumentSubjectApi;
}

// ---------------------------------------------------------------------------
// Publish PC3 — connected accounts + the account picker + deliveries.
// ---------------------------------------------------------------------------

export interface ConnectedAccountApi {
  id: string;
  platform: string;
  handle: string;
  provider: string;
  providerAccountRef: string;
  providerProfileRef: string | null;
  status: 'connected' | 'audited_public' | 'sandbox_private' | 'revoked';
  auditedAt: string | null;
  capabilities: Record<string, unknown>;
  connectedAt: string;
  revokedAt: string | null;
}

/** GET /api/publish/accounts — the card's own picker source. */
export async function listPublishAccounts(tenantId: string): Promise<ConnectedAccountApi[]> {
  const result = await request<{ accounts: ConnectedAccountApi[] }>('/api/publish/accounts', { tenantId });
  return result.accounts;
}

/** POST /api/publish/accounts/connect — mints the provider's own hosted
 * connect URL; opening it (a new tab) and finishing the platform's own
 * flow is what actually adds the account, discovered by the next sync. */
export async function connectPublishAccount(tenantId: string, platform: string): Promise<{ url: string }> {
  return request('/api/publish/accounts/connect', { method: 'POST', tenantId, body: { platform } });
}

/** POST /api/publish/accounts/sync — asks the provider what it has and
 * upserts. */
export async function syncPublishAccounts(
  tenantId: string
): Promise<{ synced: number; accounts: ConnectedAccountApi[]; skipped?: Array<{ platform: string; handle: string }> }> {
  return request('/api/publish/accounts/sync', { method: 'POST', tenantId, body: {} });
}

/** POST /api/publish/accounts/:id/revoke — disconnects, and fails every
 * queued delivery to it. */
export async function revokePublishAccount(
  tenantId: string,
  id: string
): Promise<{ account: ConnectedAccountApi; failedDeliveryIds: string[] }> {
  return request(`/api/publish/accounts/${id}/revoke`, { method: 'POST', tenantId, body: {} });
}

export interface PublishDeliveryApi {
  id: string;
  channel: string;
  toAddress: string;
  sentAt: string | null;
  failedAt: string | null;
  lastError: string | null;
  providerRef: string | null;
  publicUrl: string | null;
  notBefore: string | null;
  heldReason: string | null;
  createdAt: string;
}

export interface ApprovePostDocumentOptions {
  /** The card's own account picker — absent/empty approves with nothing
   * queued to leave, exactly PC2's behaviour. */
  accountIds?: string[];
  /** The card's own schedule field. Absent = now. */
  notBefore?: string;
}

export interface ApprovePostDocumentResultApi {
  proposalId: string;
  post: {
    documentId: string;
    channel: string;
    recordDisplayName: string;
    deliveries: { deliveryId: string; accountId: string; platform: string; status: 'queued' | 'held' }[];
  };
}

/** POST /api/proposals/:id/approve on a `post_document` card — stamps the
 * rendered document's own `metadata.approval`, and (PC3) queues one
 * delivery per chosen account: sent (or held, when `publish.enabled` is
 * off) rather than delivered nowhere. */
export async function approvePostDocument(
  tenantId: string,
  proposalId: string,
  options: ApprovePostDocumentOptions = {}
): Promise<ApprovePostDocumentResultApi> {
  return request(`/api/proposals/${proposalId}/approve`, { method: 'POST', tenantId, body: options });
}

/** POST /api/proposals/:id/reject on a `post_document` card — a KILL, not a
 * plain decline: the server requires `reason` for this target type and
 * remembers it (`publish_kills`), so the identical candidate is refused if
 * it is ever resubmitted. Reuses the generic `rejectProposal` door — the
 * server dispatches on target_type, same pattern as approve. */
export const killPostDocument = rejectProposal;

/** The citation shape POST /api/publish/posts takes — a citation the card
 * already holds (from `proposed.citations`, a PostDocumentCitationApi),
 * narrowed to the fields the create door accepts. Used only by the "Save
 * as new candidate" edit path (below): the SAME sources back the edited
 * text, so they travel unchanged onto the new candidate. */
export interface PublishCitationInputApi {
  tier: 'instruction' | 'framing' | 'world' | 'live';
  source_kind: 'document' | 'media' | 'record' | 'url';
  source_ref: string;
  span?: string | null;
  captured_at?: string | null;
  expires_at?: string | null;
  permission?: 'public' | 'granted' | 'none';
  jurisdiction?: string | null;
}

export interface CreatePublishPostBody {
  recordId: string;
  channel: string;
  text: string;
  sentences: PostDocumentSentenceApi[];
  citations: PublishCitationInputApi[];
  disclosure: 'none' | 'gifted' | 'paid' | 'affiliate';
  ai_visual: boolean;
  minors_flag: boolean;
  why?: string;
}

export interface CreatePublishPostResultApi {
  document: { id: string; template_key: string; version: number; metadata: Record<string, unknown> };
  citations: PostDocumentCitationApi[];
  proposal: Proposal;
}

/** POST /api/publish/posts — files a new candidate. The "Save as new
 * candidate" edit path is this door's only caller on the web today (PC2's
 * own scope note: a composer UI is PC7/the book's pipeline, not this
 * chunk) — an edited card re-renders through the SAME rail the original
 * candidate cleared, so an edit that smuggles an uncited claim is refused
 * exactly like any other candidate (422, `rail_refused`, the failures
 * named). */
export async function createPublishPost(
  tenantId: string,
  body: CreatePublishPostBody
): Promise<CreatePublishPostResultApi> {
  return request('/api/publish/posts', { method: 'POST', tenantId, body });
}

/** Splits an edited candidate's text back into sentences the rail can
 * check, matching each new segment against the ORIGINAL sentence it still
 * equals (same tier, same citationIndexes, same paraphrase/jurisdiction/
 * quoted-private marks) so an untouched sentence keeps exactly the
 * citation standing it already cleared. A segment with no exact match —
 * new wording, or reordered so nothing lines up — gets the STRICTEST
 * default (`tier: 'instruction'`, no citationIndexes, so every citation on
 * the candidate is tested): the rail's own substring check on an invented
 * claim almost always fails it, which is the point (spec design §2: "An
 * approver can never smuggle an uncited sentence through"). Exported so it
 * can be unit-tested without mounting the screen. */
export function resplitEditedSentences(
  originalSentences: PostDocumentSentenceApi[],
  editedText: string
): PostDocumentSentenceApi[] {
  const bySentenceText = new Map<string, PostDocumentSentenceApi>();
  for (const s of originalSentences) {
    if (!bySentenceText.has(s.text.trim())) bySentenceText.set(s.text.trim(), s);
  }
  const segments = editedText
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return segments.map((text) => {
    const original = bySentenceText.get(text);
    if (original) return { ...original, text };
    return { text, tier: 'instruction' as const };
  });
}

// ---------------------------------------------------------------------------
// R5b (WS4a): the KNOT CAPTURE "second door" — push the Knot -> capture ->
// propose -> approve. captureKnot posts a free-text note and/or uploaded
// originals (pdf/image/audio) to the R5a backend (plugins/actorCapture.ts),
// which RETAINS every file as an actor_source at capture time and stages a
// deterministic, context-driven proposal in the SAME review queue Today reads.
// The returned proposalId is then resolved through the existing
// approveProposal / rejectProposal above — one approval path, no parallel loop.
// ---------------------------------------------------------------------------

/** The page the Knot was pressed on. `route` is the current hash path;
 * `subjectType`/`subjectId` pre-associate the capture with the record being
 * viewed (project detail -> actor_project, audition detail -> actor_audition)
 * so a file dropped there attaches to it. All fields optional — a capture from
 * a list screen carries just the route, or nothing the backend can act on
 * (it falls back to the inbox). */
export interface CaptureContext {
  route?: string;
  subjectType?: string;
  subjectId?: string;
  /** W4 — a pack-declared document kind already decided by the door that
   * opened this capture (PerformerResume's "Bring an old resume"), rather
   * than left to the classifier to guess from the file's contents. */
  documentKind?: string;
}

/** The preview the backend hands back for the staged proposal (201). `summary`
 * is the human line; `captureKind` is attach_to_subject | note_on_subject |
 * generic_inbox; `sourceIds` are the actor_source ids already retained. */
export interface CapturePreview {
  summary: string;
  captureKind: string;
  sourceIds: string[];
  actionCount: number;
}

export interface CaptureResult {
  proposalId: string;
  preview: CapturePreview;
}

/** POST /api/actor/capture (multipart) — hand the Knot a note and/or files.
 * Bare fetch so the browser sets the multipart boundary itself (same pattern as
 * uploadMedia/uploadCallSheet). `context` rides as a single JSON `context`
 * field the backend also accepts. Throws ApiError on any non-2xx (e.g. the 400
 * when neither text nor a file is present — callers guard before calling). */
export async function captureKnot(
  tenantId: string,
  text: string,
  files: File[],
  context: CaptureContext = {}
): Promise<CaptureResult> {
  const form = new FormData();
  if (text.trim()) form.set('text', text);
  for (const file of files) form.append('files', file, file.name);
  if (context.route || context.subjectType || context.subjectId || context.documentKind) {
    form.set('context', JSON.stringify(context));
  }
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/capture', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as CaptureResult;
}

// B2: the generic Records API (packages/server/src/plugins/records.ts) —
// the raw shape every /records* route hands back verbatim (snake_case,
// straight off the RecordRow the core records engine returns; unlike
// attention/proposals above, this route never re-cases into camelCase).
export interface RecordRow {
  id: string;
  tenant_id: string;
  record_type: string;
  display_name: string;
  fields: Record<string, unknown>;
  lifecycle_state: string | null;
  sensitivity: 'normal' | 'restricted';
  status: 'active' | 'archived' | 'deleted';
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** GET /records?type_key=&lifecycle_state= — reused as-is for the
 * Auditions list/stage-filter (no dedicated actor route needed: the
 * generic list already supports exactly this filter shape). Rows come
 * back oldest-first (records.ts's own ORDER BY); callers wanting
 * reverse-chron reverse the array themselves rather than this function
 * silently reordering a generic primitive's contract. */
export async function listRecordsByType(
  tenantId: string,
  typeKey: string,
  lifecycleState?: string,
  page?: { limit?: number; offset?: number }
): Promise<RecordRow[]> {
  const qs = new URLSearchParams({ type_key: typeKey });
  if (lifecycleState) qs.set('lifecycle_state', lifecycleState);
  if (page?.limit !== undefined) qs.set('limit', String(page.limit));
  if (page?.offset !== undefined) qs.set('offset', String(page.offset));
  const result = await request<{ records: RecordRow[] }>(`/records?${qs.toString()}`, { tenantId });
  return result.records;
}

/** Core gap 11: READ A KNOWN SET OF IDS — GET /records?type_key=&ids=, 200 ids per request, so a
 * screen holding ids from a link sweep never has to read the whole type (or fan out one request per
 * id). `absent` = named but not returned (not found, deleted, or not readable — the server keeps
 * those indistinguishable on purpose); a screen that needs every id should SAY when it is short. */
export async function getRecordsByIds(
  tenantId: string,
  typeKey: string,
  ids: readonly string[]
): Promise<{ records: RecordRow[]; absent: string[] }> {
  const unique = [...new Set(ids)];
  const PER_REQUEST = 200;
  const records: RecordRow[] = [];
  const absent: string[] = [];
  for (let i = 0; i < unique.length; i += PER_REQUEST) {
    const qs = new URLSearchParams({ type_key: typeKey, ids: unique.slice(i, i + PER_REQUEST).join(',') });
    const result = await request<{ records: RecordRow[]; ids_absent: string[] }>(`/records?${qs.toString()}`, { tenantId });
    records.push(...result.records);
    absent.push(...result.ids_absent);
  }
  return { records, absent };
}

/** B17 §2 — GET /records?type_key=aa_source&field.source_note=Knot%20capture...
 * — the `field.` query prefix already reads a field EQUALS filter
 * (records.ts's own `FIELD_QUERY_PREFIX`); no server work. This is the one
 * caller: a Knot note is an `aa_source` with `source_note: 'Knot capture
 * (inbox)'` and, until this screen, nothing listed it. Newest first — same
 * "reverse it yourself" contract `listRecordsByType`'s own doc states. */
export async function listKnotNotes(tenantId: string): Promise<RecordRow[]> {
  const qs = new URLSearchParams({ type_key: 'aa_source' });
  qs.set('field.source_note', 'Knot capture (inbox)');
  const result = await request<{ records: RecordRow[] }>(`/records?${qs.toString()}`, { tenantId });
  return [...result.records].reverse();
}

/** S1: EVERY record of a type — pages through GET /records at the
 * engine's own LIST_MAX_LIMIT (200) until a short page. Exists because a
 * count or a sum built on the default first page (50 rows) would silently
 * lie; the safety cap (20 pages / 4,000 rows) keeps a runaway tenant from
 * hanging the home screen — a tenant past it has outgrown client-side
 * aggregation and needs a real server-side aggregate (say so, loudly, in
 * whatever chunk hits it). */
export async function listAllRecordsByType(
  tenantId: string,
  typeKey: string,
  lifecycleState?: string
): Promise<RecordRow[]> {
  const PAGE = 200;
  const MAX_PAGES = 20;
  // R-9 (2026-08-30, measured on the beta corpus): pages after the first
  // come in WAVES of 6, not one at a time. The server answers each page in
  // ~100ms; the cost of this loop is ROUND TRIPS — at 20 sequential pages
  // over a ~400ms mobile link a single 4,000-row type took ~10s, and every
  // screen that aggregates felt broken. A wave stops the loop the moment it
  // contains a short page; rows are assembled in page order, so callers see
  // exactly the array the sequential loop produced. Request COUNT is
  // unchanged — only concurrency (6 stays well inside the 300/min global
  // rate limit even when a home screen fans out nine of these).
  const WAVE = 8;
  // Adaptive ramp: pages 0 and 1 are fetched one at a time — byte-identical
  // request patterns to the old loop for every type that fits in two pages,
  // which is most of them (and what the S16 request budgets model). Only a
  // type that proves itself BIG (two full pages) graduates to waves.
  const all: RecordRow[] = [];
  for (let page = 0; page < 2; page++) {
    const rows = await listRecordsByType(tenantId, typeKey, lifecycleState, { limit: PAGE, offset: page * PAGE });
    all.push(...rows);
    if (rows.length < PAGE) return all;
  }
  // Doubling waves (2, 4, 8): overshoot past the last real page costs at
  // most wave-1 empty requests, so it stays small exactly where shops are
  // small, while a 20-page type still collapses from 20 round trips to ~5.
  let start = 2;
  let wave = 2;
  while (start < MAX_PAGES) {
    const count = Math.min(wave, MAX_PAGES - start);
    const pages = await Promise.all(
      Array.from({ length: count }, (_, i) =>
        listRecordsByType(tenantId, typeKey, lifecycleState, { limit: PAGE, offset: (start + i) * PAGE })
      )
    );
    for (const rows of pages) all.push(...rows);
    if (pages.some((rows) => rows.length < PAGE)) break;
    start += count;
    wave = Math.min(wave * 2, WAVE);
  }
  return all;
}

/** S15 — ONE READ FOR MANY LINKS. `GET /records?...&include=links` returns
 * this page's records AND their links, keyed by record id, in ONE request.
 *
 * Before this, a screen wanting "these 200 orders and who each is for" had
 * to read links one record at a time: the work-order list spent ~250
 * requests on 204 rows (and 83% of the 300/minute global rate limit with
 * them), the dispatch board capped its fan-out and said so on screen, and
 * the deposit clock could not be built at all.
 *
 * `linkTypes` names the relationships the screen actually renders — two
 * links per record instead of every link a record has, which is also what
 * keeps a busy record's answer clear of the per-record cap. `truncated`
 * names the records whose link set was cut short; it is a fact the server
 * establishes, never a length the caller has to guess from. */
export interface RecordsWithLinks {
  records: RecordRow[];
  /** Keyed by record id. A record present with no links maps to an empty
   * array; an id ABSENT from this map was not answered — see `unreadable`. */
  linksByRecordId: Record<string, LinkRow[]>;
  /** Records whose link set hit the per-record cap: there is more to read. */
  truncated: string[];
  /** Records the server would not answer for (gone, or not this caller's to
   * read). Normally empty; never silently dropped. */
  unreadable: string[];
}

export async function listRecordsWithLinks(
  tenantId: string,
  typeKey: string,
  opts: {
    lifecycleState?: string;
    linkTypes?: string[];
    linkLimit?: number;
    limit?: number;
    offset?: number;
  } = {}
): Promise<RecordsWithLinks> {
  const qs = new URLSearchParams({ type_key: typeKey, include: 'links' });
  if (opts.lifecycleState) qs.set('lifecycle_state', opts.lifecycleState);
  if (opts.linkTypes && opts.linkTypes.length > 0) qs.set('link_type', opts.linkTypes.join(','));
  if (opts.linkLimit !== undefined) qs.set('link_limit', String(opts.linkLimit));
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  const result = await request<{
    records: RecordRow[];
    links?: Record<string, LinkRow[]>;
    links_truncated?: string[];
    links_unreadable?: string[];
  }>(`/records?${qs.toString()}`, { tenantId });
  return {
    records: result.records,
    linksByRecordId: result.links ?? {},
    truncated: result.links_truncated ?? [],
    unreadable: result.links_unreadable ?? [],
  };
}

/** Every record of a type, with its links — the bulk-link twin of
 * listAllRecordsByType, paging at the engine's own LIST_MAX_LIMIT and
 * carrying the same 20-page safety cap for the same reason. One request per
 * PAGE, links included, rather than one request per record. */
export async function listAllRecordsWithLinks(
  tenantId: string,
  typeKey: string,
  opts: { lifecycleState?: string; linkTypes?: string[]; linkLimit?: number } = {}
): Promise<RecordsWithLinks> {
  const PAGE = 200;
  const MAX_PAGES = 20;
  // R-9 — same wave-parallel paging as listAllRecordsByType, same reason,
  // same order-preserving assembly. See that function's comment.
  const WAVE = 8;
  const all: RecordsWithLinks = { records: [], linksByRecordId: {}, truncated: [], unreadable: [] };
  const merge = (chunk: RecordsWithLinks) => {
    all.records.push(...chunk.records);
    Object.assign(all.linksByRecordId, chunk.linksByRecordId);
    all.truncated.push(...chunk.truncated);
    all.unreadable.push(...chunk.unreadable);
  };
  // Same adaptive ramp as listAllRecordsByType — see its comment.
  for (let page = 0; page < 2; page++) {
    const chunk = await listRecordsWithLinks(tenantId, typeKey, { ...opts, limit: PAGE, offset: page * PAGE });
    merge(chunk);
    if (chunk.records.length < PAGE) return all;
  }
  let start = 2;
  let wave = 2;
  while (start < MAX_PAGES) {
    const count = Math.min(wave, MAX_PAGES - start);
    const chunks = await Promise.all(
      Array.from({ length: count }, (_, i) =>
        listRecordsWithLinks(tenantId, typeKey, { ...opts, limit: PAGE, offset: (start + i) * PAGE })
      )
    );
    for (const chunk of chunks) merge(chunk);
    if (chunks.some((chunk) => chunk.records.length < PAGE)) break;
    start += count;
    wave = Math.min(wave * 2, WAVE);
  }
  return all;
}

/** POST /records/:id/transition {to} — reused as-is for stage advance/
 * archive/revive (the lifecycle engine already rejects illegal transitions
 * with a 422 lifecycle_guard ApiError; no dedicated actor route needed). */
/** `fields` rides WITH the move (Day 2 walk, S3.14): the transition door has
 * always taken them and the engine merges them BEFORE it checks what the move
 * requires, so a field typed in the edit sheet and not yet saved is present
 * when the state that needs it is asked for. Omitted = the record as it
 * stands, which is every caller that has no form open. */
export async function transitionRecord(
  tenantId: string,
  id: string,
  to: string,
  fields?: Record<string, unknown>
): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>(`/records/${id}/transition`, {
    method: 'POST',
    tenantId,
    body: fields && Object.keys(fields).length > 0 ? { to, fields } : { to },
  });
  return result.record;
}

// R2: the SCHEMA-DRIVEN record-operations engine. The generic RecordForm /
// RecordActions render entirely from a record type's OWN definition (fields,
// picklist values, relationships, lifecycle) fetched from these endpoints —
// so R3 can roll manual CRUD out to every record type from one system.

export type SchemaFieldType =
  | 'text'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'picklist'
  | 'boolean'
  | 'duration'
  | 'file'
  // ⚑ W3 — `json` WAS MISSING FROM THIS UNION and the server has been serving
  // it since the coach surface landed: `aa_review.timeline_marks` is a json
  // field, and this copy of core's FieldType simply did not name it. Nothing
  // failed — an unnamed variant arrives as a string the switch falls through,
  // which is exactly how RecordForm came to render structured data into a
  // text box (see its own `field.type === 'json'` branch). The web holds no
  // copy of the pack's vocabulary; it does hold a copy of the ENGINE's, and
  // this one had drifted.
  | 'json';

export interface SchemaField {
  name: string;
  type: SchemaFieldType;
  required: boolean;
  label: string;
  picklistValues?: string[];
  default?: unknown;
}

export interface SchemaRelationship {
  key: string;
  targetType: string;
  kind: 'lookup' | 'master_detail';
  cardinality: 'one' | 'many';
  label: string;
}

export interface SchemaLifecycle {
  states: string[];
  /** from-state -> the legal next states (the engine's exact {from,to} pairs). */
  transitions: Record<string, string[]>;
}

/** K2 (console kernel) — a type whose lifecycle is chosen per record by one
 * of its own fields (`keyField`) has no SINGLE `lifecycle` to report at this
 * type-level endpoint; every variant ships instead, keyed by that field's
 * value, so a screen holding a specific record picks the right one itself. */
export interface SchemaLifecycleVariants {
  keyField: string;
  variants: Record<string, SchemaLifecycle>;
}

export interface RecordTypeSchema {
  key: string;
  label: string;
  labelPlural: string;
  nameTemplate: string;
  fields: SchemaField[];
  relationships: SchemaRelationship[];
  lifecycle?: SchemaLifecycle;
  lifecycleVariants?: SchemaLifecycleVariants;
}

export interface RecordTypeSummary {
  key: string;
  label: string;
  labelPlural: string;
  hasLifecycle: boolean;
  /** W3: the entitlement this type's WRITES require, or null when it is
   * ungated — which is nearly every type. Read null as "nothing to say",
   * never as locked. */
  writeFeature?: string | null;
  /** W3: this household's tier does not include `writeFeature`, so a create
   * would be refused.
   *
   * ⚑ ANSWERED BY THE SERVER, from the same resolution that refuses the
   * write. Never re-derived here from the plan's feature list: a second
   * derivation is exactly how a screen ends up offering a button that always
   * errors (the same reasoning `RetentionApi.canRecycle` carries).
   *
   * ⚠ REPORTED, NOT FILTERED. A surface that can explain WHY (the resume
   * screen's lapse banner) needs to know the type exists and is locked;
   * hiding it is the caller's choice, made where the caller can say
   * something. Older responses omit it — read as unlocked. */
  writeLocked?: boolean;
}

/** GET /records/types — the tenant pack's record types (summary list). */
export async function getRecordTypes(tenantId: string): Promise<RecordTypeSummary[]> {
  const result = await request<{ types: RecordTypeSummary[] }>('/records/types', { tenantId });
  return result.types;
}

/** GET /records/types/:key — one record type's full schema. */
export async function getRecordTypeSchema(tenantId: string, key: string): Promise<RecordTypeSchema> {
  const result = await request<{ schema: RecordTypeSchema }>(
    `/records/types/${encodeURIComponent(key)}`,
    { tenantId }
  );
  return result.schema;
}

/** One relationship link row (GET /records/:id?include=links). */
export interface LinkRow {
  id: string;
  src_type: string;
  src_id: string;
  dst_type: string;
  dst_id: string;
  link_type: string;
  label: string | null;
}

/** GET /records/:id — one record, no expansions. S9: how the relationship
 * picker resolves a SELECTED id that no list page returned (a link whose
 * target sits past the loaded pages), so the current selection always
 * renders with its real display name instead of reading as blank. */
export async function getRecord(tenantId: string, id: string): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>(`/records/${id}`, { tenantId });
  return result.record;
}

/** GET /records/:id?include=links — the record + its links (edit-mode
 * prefill: relationship pickers seed from these).
 *
 * S16 — `linkLimit`, AND TRUNCATION AS A FACT. S15 taught this route both
 * (`?link_limit=`, and a `links_truncated` boolean in every `include=links`
 * answer) and no client had asked for either, so the detail screens went on
 * MIRRORING the server's default page size (`LINK_PAGE = 50`) and guessing:
 * "the array came back exactly 50 long, so there is probably more". A guess
 * is wrong in both directions — a record with exactly fifty links was told
 * it had been cut short, and every screen rendered '—' where a real total
 * belonged. The server has always known; now it is asked, and answers.
 *
 * `linksTruncated` is additive: existing callers destructure `{record,
 * links}` and are byte-identical. */
export async function getRecordWithLinks(
  tenantId: string,
  id: string,
  opts: { linkLimit?: number } = {}
): Promise<{ record: RecordRow; links: LinkRow[]; linksTruncated: boolean }> {
  const qs = new URLSearchParams({ include: 'links' });
  if (opts.linkLimit !== undefined) qs.set('link_limit', String(opts.linkLimit));
  const result = await request<{
    record: RecordRow;
    links?: LinkRow[];
    links_truncated?: boolean;
  }>(`/records/${id}?${qs.toString()}`, { tenantId });
  return {
    record: result.record,
    links: result.links ?? [],
    linksTruncated: result.links_truncated ?? false,
  };
}

/** The biggest link page the engine will serve for one record
 * (`records.ts`'s LIST_MAX_LIMIT, which `resolvePageArgs` clamps to). A
 * detail screen asks for this rather than the 50-link default: four times
 * the reach for the same one request, and past it the server's own
 * `links_truncated` says so instead of the screen inferring it. */
export const MAX_LINK_PAGE = 200;

/** POST /records — create a record of `recordType` with `fields`. */
export async function createRecord(
  tenantId: string,
  recordType: string,
  fields: Record<string, unknown>,
  lifecycleState?: string | null
): Promise<RecordRow> {
  const body: Record<string, unknown> = { recordType, fields };
  if (lifecycleState !== undefined) body['lifecycleState'] = lifecycleState;
  const result = await request<{ record: RecordRow }>('/records', {
    method: 'POST',
    tenantId,
    body,
  });
  return result.record;
}

/** PATCH /records/:id — edit a record's fields. */
export async function updateRecord(
  tenantId: string,
  id: string,
  fields: Record<string, unknown>
): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>(`/records/${id}`, {
    method: 'PATCH',
    tenantId,
    body: { fields },
  });
  return result.record;
}

/** POST /records/:id/delete — soft-delete (spec §5 rule 3: nothing is lost).
 * S10: NO PRODUCT SURFACE CALLS THIS ANY MORE — RecordActions offers Retire
 * (archiveRecord below) instead, on every record type. The route and this
 * function stay because the route stays; a new caller needs a decision, not
 * a convenience. */
export async function deleteRecord(tenantId: string, id: string): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>(`/records/${id}/delete`, {
    method: 'POST',
    tenantId,
  });
  return result.record;
}

/** POST /records/:id/archive — RETIRE a record: status 'active' ->
 * 'archived', cascading to master_detail children, never removing a row.
 * The route has existed since P2.5; this function is what it never had, and
 * its absence is why "deactivate, never delete" had no affordance anywhere
 * in the product (S10).
 *
 * UNLIKE deleteRecord ABOVE, THIS ROUTE IS GATED. `archive` is one of the
 * manifest's five operation classes, so on a pack that declares an
 * authority manifest the server derives the action (`rexx.<type>.archive`)
 * and a caller who lacks it gets C5e's 409 offer instead of the change —
 * which is why callers read it with authorityOfferFrom and file it with
 * fileAskFromOffer rather than reporting a flat failure. On a pack with no
 * manifest (Actor, Personal) there is no gate and it simply archives,
 * exactly as delete simply deleted. */
export async function archiveRecord(tenantId: string, id: string): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>(`/records/${id}/archive`, {
    method: 'POST',
    tenantId,
  });
  return result.record;
}

/** Controlled numbering (2026-08-23): reserve the next number for a
 * (type, field); consume it when the record saves; void it on cancel. The
 * ledger is server-side — see core's records/numbering.ts. */
export interface NumberReservation {
  id: string;
  value: string;
  numericValue: number;
}
export async function reserveSequenceNumber(
  tenantId: string,
  typeKey: string,
  field: string
): Promise<NumberReservation> {
  const r = await request<{ reservation: NumberReservation }>(
    `/records/sequences/${encodeURIComponent(typeKey)}/${encodeURIComponent(field)}/reserve`,
    { tenantId, method: 'POST' }
  );
  return r.reservation;
}
export async function consumeSequenceReservation(
  tenantId: string,
  reservationId: string,
  recordId: string
): Promise<void> {
  await request(`/records/sequences/reservations/${encodeURIComponent(reservationId)}/consume`, {
    tenantId,
    method: 'POST',
    body: { recordId },
  });
}
export async function voidSequenceReservation(tenantId: string, reservationId: string): Promise<void> {
  await request(`/records/sequences/reservations/${encodeURIComponent(reservationId)}/void`, {
    tenantId,
    method: 'POST',
  });
}

/** POST /records/:id/links — link `id` to `dstId` via `relationshipKey`
 * (the relationship is declared on the SOURCE type, so srcId = id). */
export async function linkRecord(
  tenantId: string,
  id: string,
  relationshipKey: string,
  dstId: string
): Promise<void> {
  await request(`/records/${id}/links`, {
    method: 'POST',
    tenantId,
    body: { relationshipKey, dstId },
  });
}

/** DELETE /records/:id/links/:linkId — remove one link (lookup relationships;
 * master_detail unlinks are refused 409 by the engine). */
export async function unlinkRecord(tenantId: string, id: string, linkId: string): Promise<void> {
  await request(`/records/${id}/links/${linkId}`, { method: 'DELETE', tenantId });
}

/** B11 §1 — the presigned PUT, over XHR rather than `fetch`, because `fetch`
 * has no upload-progress event at all. A reel can take minutes on LTE, and
 * "Adding…" alone for four minutes reads as broken (B11's own brief) —
 * `onProgress` is how a caller shows a real percentage instead of a
 * spinner-shaped lie. Every OTHER behaviour is unchanged from the `fetch`
 * this replaces: same method, same one header, same body, same thrown
 * `ApiError` on a non-2xx status or a network failure — so a caller that
 * never passes `onProgress` (every one of them before B11) cannot tell the
 * difference. */
function putBlobWithProgress(
  url: string,
  mimeType: string,
  file: File,
  onProgress?: (fraction: number) => void
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('content-type', mimeType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(xhr.status, 'upload_failed', 'The upload did not reach storage. Try again.'));
    };
    xhr.onerror = () => reject(new ApiError(0, 'upload_failed', 'The upload did not reach storage. Try again.'));
    xhr.send(file);
  });
}

export interface UploadRecordBlobOpts {
  /** B11 §1 — a fraction 0..1, fired as the presigned PUT's own bytes move.
   * Never called at all under the `fs` backend's multipart fallback (no PUT
   * happens there) or on a server predating the presign route — a caller
   * showing a percentage falls back to its own "Adding…" wording when this
   * never fires, exactly as it already does before the first progress
   * event arrives here. */
  onProgress?: (fraction: number) => void;
}

/** POST /records/blobs — upload a file through the media pipeline, returning
 * the ref string a `file` record field stores. Bare fetch so the browser sets
 * the multipart boundary itself (same as uploadMedia/uploadCallSheet). */
export async function uploadRecordBlob(
  tenantId: string,
  file: File,
  opts: UploadRecordBlobOpts = {}
): Promise<{ ref: string; mimeType: string }> {
  // A12b (commitment 7e599dfe): ASK FIRST. Under r2 the answer is a
  // presigned PUT and the file goes STRAIGHT to the store — no byte through
  // the app server, which is what lets a phone send a 1 GiB tape without
  // taking the deployment down. Under fs (dev, walks) the answer is
  // 'multipart' and the old door does what it always did.
  //
  // A presign failure is NOT quietly downgraded to multipart: on the
  // backend where presign exists, multipart is the path with the known heap
  // hazard, and a silent fallback would hide an outage behind the exact
  // behaviour the flow exists to end. The one exception is a 404 — a server
  // predating the route — where multipart is simply the only door there is.
  const mimeType = file.type && /^[\w.-]+\/[\w.+-]+$/.test(file.type) ? file.type : 'application/octet-stream';
  const presignResp = await fetch('/records/blobs/presign', {
    method: 'POST',
    headers: withTenant(tenantId, { 'content-type': 'application/json' }),
    credentials: 'same-origin',
    body: JSON.stringify({ filename: file.name || 'upload', mimeType, sizeBytes: file.size }),
  });
  if (presignResp.status !== 404) {
    const presign = (await presignResp.json().catch(() => null)) as
      | { mode: 'multipart' }
      | { mode: 'direct'; url: string; stagingKey: string; mimeType: string }
      | null;
    if (!presignResp.ok) throw extractError(presignResp.status, presign);
    if (presign && presign.mode === 'direct') {
      // The PUT carries ONLY the signed content-type — anything else breaks
      // the signature, and nothing else is needed: the URL is the
      // credential, scoped to this one key for minutes.
      //
      // ⚠ XHR ONLY WHEN A CALLER ACTUALLY WANTS PROGRESS. `fetch` has no
      // upload-progress event at all, which is the entire reason this
      // second path exists — but every caller before B11 built its own
      // test against a stubbed `fetch` (`walkDay34Doors.test.ts` among
      // them), so switching the PUT unconditionally would have changed
      // what THEY exercise, not just added a percentage nobody asked them
      // for. `onProgress` absent -> the untouched `fetch` path below;
      // present -> XHR, so a caller that wants a percentage is the only
      // one that pays for it.
      if (opts.onProgress) {
        await putBlobWithProgress(presign.url, presign.mimeType, file, opts.onProgress);
      } else {
        const put = await fetch(presign.url, {
          method: 'PUT',
          headers: { 'content-type': presign.mimeType },
          body: file,
        });
        if (!put.ok) throw new ApiError(put.status, 'upload_failed', 'The upload did not reach storage. Try again.');
      }
      const commit = await fetch('/records/blobs/commit', {
        method: 'POST',
        headers: withTenant(tenantId, { 'content-type': 'application/json' }),
        credentials: 'same-origin',
        body: JSON.stringify({ stagingKey: presign.stagingKey, filename: file.name || 'upload', mimeType }),
      });
      const committed = (await commit.json().catch(() => null)) as { ref: string; mimeType: string } | null;
      if (!commit.ok) throw extractError(commit.status, committed);
      return committed!;
    }
  }

  const form = new FormData();
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/records/blobs', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as { ref: string; mimeType: string };
}

function withTenant(tenantId: string, base: Record<string, string>): Headers {
  const h = new Headers(base);
  if (tenantId) h.set('X-Tenant-Id', tenantId);
  return h;
}

/** A12 — the read side of uploadRecordBlob: ask the ONE serving door
 * (GET /records/:id/files/:field?url=1) for a URL a TAG can be pointed at.
 *
 * Under r2 that is a presigned R2 URL (short-lived — fetch it near mount,
 * not at module load); under fs it is this same route's own byte URL with
 * ?tenant= riding along. Video and audio MUST use this and a tag src, never
 * fetch→blob: a blob URL needs the whole file before it plays and defeats
 * Range/seeking, which is the entire point of the presigned redirect
 * (FIX 1, decision 2d6a5dc7). Images may fetch the answered URL bare (no
 * custom headers → no preflight) and paint a blob as before. */
export interface RecordFileUrl {
  url: string;
  mimeType: string;
  /** Null under fs (the session is the credential); seconds under r2. A
   * player that outlives this re-asks — the door is cheap. */
  expiresInSeconds: number | null;
}

export async function getRecordFileUrl(
  tenantId: string,
  recordId: string,
  fieldName: string,
  opts: { playable?: boolean } = {}
): Promise<RecordFileUrl> {
  const qs = opts.playable ? '?url=1&playable=1' : '?url=1';
  return request<RecordFileUrl>(
    `/records/${recordId}/files/${encodeURIComponent(fieldName)}${qs}`,
    { tenantId }
  );
}

// B2: the Actor vertical's two thin routes (packages/server/src/plugins/
// actorAuditions.ts) — see that file's own header for why the generic
// Records API above isn't enough for these two specifically.
// B3a: the compact source summary the composed audition detail now carries
// inline (kind + filename + id), so the detail screen can offer a "view the
// original breakdown" chip that deep-links to GET /api/actor/sources/:id.
export interface AuditionSourceRef {
  id: string;
  kind: string | null;
  filename: string | null;
}

export interface AuditionDetail {
  audition: RecordRow;
  sessions: RecordRow[];
  castingDirector: RecordRow | null;
  project: RecordRow | null;
  // B3a: present when this audition was derived from an actor_source (its
  // 'source' lookup). Older responses may omit it — treated as null.
  source?: AuditionSourceRef | null;
  // B6c-2: the audition's sides revisions (same shape the project detail
  // surfaces). Older responses may omit it — callers default to [].
  sides?: ProjectSides[];
}

export async function getAuditionDetail(tenantId: string, id: string): Promise<AuditionDetail> {
  return request<AuditionDetail>(`/api/actor/auditions/${id}`, { tenantId });
}

// B3: the Projects surface. The list reuses the generic listRecordsByType
// above (type_key=actor_project, lifecycle_state=<phase>); only these three
// composed/enrichment reads need the vertical routes (plugins/actorProjects.ts).

/** GET /api/actor/projects/:id — the composed phase-adaptive detail: the
 * project record itself plus its auditions, the people across those
 * auditions, and the source documents behind the project + its auditions. */
/** B6b: a shoot-day record plus the id of the ORIGINAL source (call sheet)
 * it was parsed from, so the day-of card's "view source" chip can open that
 * exact original. `sourceId` is null for a shoot day with no linked source. */
export interface ProjectShootDay extends RecordRow {
  sourceId: string | null;
}

/** B6c-2: one sides revision plus the id of its retained ORIGINAL file
 * (`sourceId` → SourceViewer) and the id of the prior revision it replaced
 * (`supersedesId`, null for a first revision). is_current (in `fields`) marks
 * the live one; a current revision WITH a supersedesId is what raises the calm
 * "new revision" change flag. Same attach-the-source shape as ProjectShootDay. */
export interface ProjectSides extends RecordRow {
  sourceId: string | null;
  supersedesId: string | null;
}

/** B6c-2: one contract plus the id of its retained ORIGINAL file. The guarded
 * lifecycle lives in `lifecycle_state` (received/under_review/signed/
 * countersigned/declined); `fields` carries kind/rate/respond_by/etc. */
export interface ProjectContract extends RecordRow {
  sourceId: string | null;
}

export interface ProjectDetail {
  project: RecordRow;
  auditions: RecordRow[];
  people: RecordRow[];
  sources: RecordRow[];
  // B6b additive fields — older responses may omit them; callers default to [].
  shootDays?: ProjectShootDay[];
  workedWith?: RecordRow[];
  // B6c-2 additive fields — the real sides revisions + contracts for this
  // project (older responses may omit them; callers default to []).
  sides?: ProjectSides[];
  contracts?: ProjectContract[];
  // WS5 additive field — the current uploaded poster (newest kind='poster'
  // media linked to this project), or null to fall back to the gradient art.
  poster?: { id: string; fileUrl: string } | null;
}

export async function getProjectDetail(tenantId: string, id: string): Promise<ProjectDetail> {
  return request<ProjectDetail>(`/api/actor/projects/${id}`, { tenantId });
}

// The medium hints the enrichment search actually honours (plugins/
// actorProjects.ts coerces anything else to "no hint"); the actor_project
// `medium` picklist is wider (film/tv/theatre/new_media/commercial/other),
// so callers map their own value down to one of these or omit it.
export type EnrichMedium = 'film' | 'tv' | 'voice';

/** One ranked Wikidata candidate for the confirm-a-match UI — the server
 * strips the internal `_e` entity plumbing before it reaches here (see
 * actorProjects.ts's enrich/search route). Ordered highest-confidence first
 * (the module's own score sort); `score` is the raw rank hint, never a
 * percentage. */
export interface EnrichCandidate {
  qid: string;
  label: string | undefined;
  description: string | undefined;
  type: string | undefined;
  year: string;
  score: number;
}

export interface EnrichSearchParams {
  title: string;
  medium?: EnrichMedium;
  year?: string;
}

/** GET /api/actor/projects/:id/enrich/search — ranked candidates. Reads
 * only; never writes. NEVER auto-applies (the actor confirms one, then
 * applyProjectEnrichment writes it). */
export async function searchProjectEnrichment(
  tenantId: string,
  id: string,
  params: EnrichSearchParams
): Promise<EnrichCandidate[]> {
  const qs = new URLSearchParams({ title: params.title });
  if (params.medium) qs.set('medium', params.medium);
  if (params.year) qs.set('year', params.year);
  const result = await request<{ candidates: EnrichCandidate[] }>(
    `/api/actor/projects/${id}/enrich/search?${qs.toString()}`,
    { tenantId }
  );
  return result.candidates;
}

/** The enrichment the actor confirmed, as written back onto the project. */
export interface AppliedEnrichment {
  qid: string;
  wikidataUrl: string;
  label: string | undefined;
  description: string | null;
  type: string | undefined;
  airDate: string | null;
  genres: string[];
  imdbId: string | null;
  tmdbId: string | null;
}

/** POST /api/actor/projects/:id/enrich/apply { qid } — writes the confirmed
 * enrichment fields onto the project and stamps enrichment_confirmed_at. The
 * 400-without-qid guard lives server-side; callers here always pass one. */
export async function applyProjectEnrichment(
  tenantId: string,
  id: string,
  qid: string
): Promise<{ project: RecordRow; enrichment: AppliedEnrichment }> {
  return request<{ project: RecordRow; enrichment: AppliedEnrichment }>(
    `/api/actor/projects/${id}/enrich/apply`,
    { method: 'POST', tenantId, body: { qid } }
  );
}

// B3a provenance: the source-document viewer read model. An actor_source is
// the ORIGINAL, untouched document (breakdown/call sheet/contract/sides/
// email) a derived record was built from.
/** One block of the note filed ON a retained original — the lines nothing
 * could place, the document's own terms, or the family's own typed note.
 * Parsed by the server module that writes it (actorCapture's
 * `parseSourceNote`), so a screen never re-derives the shape. */
export interface SourceNoteBlock {
  /** Null for the family's own prose; a heading ending in a colon otherwise. */
  heading: string | null;
  lines: string[];
}

export interface SourceDoc {
  id: string;
  kind: string | null;
  filename: string | null;
  raw_text: string | null;
  media_ref: string | null;
  received_at: string | null;
  /** ⚑ 2026-09-16, the Day 3–4 walk, item 1. Absent from older servers, hence
   * optional: the screen renders nothing rather than an empty heading. */
  note_blocks?: SourceNoteBlock[];
  /** CV7 — what this device should PLAY, which is not always what was stored.
   * 'rendered' means an AAC copy exists because the original's container has
   * no decoder on iOS; 'native' means the original plays everywhere already.
   * The original remains the truth and remains what Download hands over. */
  playback_status?: 'native' | 'rendered' | 'failed' | 'ffmpeg_unavailable' | 'too_large' | null;
  playback_mime?: string | null;
  playback_is_rendition?: boolean | null;
}

export async function getSource(tenantId: string, id: string): Promise<SourceDoc> {
  return request<SourceDoc>(`/api/actor/sources/${id}`, { tenantId });
}

// ---------------------------------------------------------------------------
// P2 — THE PROJECT BINDER (docs.binder, Pro). A project's filed papers
// (aa_source rows whose `source_project` lookup names this project), plus
// the three correction verbs. The LIST is ungated — filing put them here on
// every tier; only rename/re-type/move require the plan.
// ---------------------------------------------------------------------------

export interface ProjectDocument {
  id: string;
  kind: string | null;
  filename: string | null;
  receivedAt: string | null;
}

/** GET /api/actor/projects/:id/documents — newest received first. */
export async function getProjectDocuments(tenantId: string, projectId: string): Promise<ProjectDocument[]> {
  const result = await request<{ documents: ProjectDocument[] }>(
    `/api/actor/projects/${projectId}/documents`,
    { tenantId }
  );
  return result.documents;
}

/** PATCH /api/actor/documents/:id/rename — docs.binder. Refused (403,
 * `feature_not_in_plan`) below Pro; callers gate the affordance with
 * `planHas(plan, 'docs.binder')` first so this only ever fires the refusal
 * a household could not have avoided some other way. */
export async function renameProjectDocument(tenantId: string, id: string, filename: string): Promise<ProjectDocument> {
  const result = await request<{ document: ProjectDocument }>(`/api/actor/documents/${id}/rename`, {
    method: 'PATCH',
    tenantId,
    body: { filename },
  });
  return result.document;
}

/** PATCH /api/actor/documents/:id/retype — docs.binder. Corrects the
 * classifier's `kind`; NEVER re-runs extraction (see the server route's own
 * header). */
export async function retypeProjectDocument(tenantId: string, id: string, kind: string): Promise<ProjectDocument> {
  const result = await request<{ document: ProjectDocument }>(`/api/actor/documents/${id}/retype`, {
    method: 'PATCH',
    tenantId,
    body: { kind },
  });
  return result.document;
}

/** PATCH /api/actor/documents/:id/move { projectId } — docs.binder. Re-homes
 * the document; the source project's own list no longer carries it. */
export async function moveProjectDocument(tenantId: string, id: string, projectId: string): Promise<ProjectDocument> {
  const result = await request<{ document: ProjectDocument; projectId: string }>(
    `/api/actor/documents/${id}/move`,
    { method: 'PATCH', tenantId, body: { projectId } }
  );
  return result.document;
}

// ---------------------------------------------------------------------------
// P3 — THE POSITION ROSTER (people.position_roster, Pro).
//
// "1st AD" -> the name from the call sheet. Both routes are gated WHOLE on
// the server (actorPositionRoster.ts) — unlike the binder just above, there
// is no ungated half here, so a caller below Pro must gate the AFFORDANCE
// (planHas) rather than merely the edit control, or every project screen and
// every People-tab visit fires a doomed 403.
// ---------------------------------------------------------------------------

export interface PositionRosterPerson {
  personId: string | null;
  personName: string | null;
  /** When this credit was filed — a crew credit records WHAT somebody did,
   * not WHEN (recordTypes.ts's own rule), so this is "most recently learned"
   * and never a shoot date. */
  when: string;
}
export interface PositionRosterGroup {
  position: string;
  people: PositionRosterPerson[];
}
export interface ProjectPositionRoster {
  project: { id: string; displayName: string };
  positions: PositionRosterGroup[];
}

/** GET /api/actor/projects/:id/roster — one production's crew, grouped by
 * position. 403 `feature_not_in_plan` below Pro; callers gate the panel with
 * `planHas(plan, 'people.position_roster')` first (see the header above). */
export async function getProjectPositionRoster(tenantId: string, projectId: string): Promise<ProjectPositionRoster> {
  const result = await request<{ roster: ProjectPositionRoster }>(`/api/actor/projects/${projectId}/roster`, {
    tenantId,
  });
  return result.roster;
}

export interface PositionSearchResult {
  creditId: string;
  position: string;
  personId: string | null;
  personName: string | null;
  projectId: string | null;
  projectName: string | null;
  when: string;
}

/** GET /api/actor/people/position-search?q= — household-wide, "who has 1st
 * AD'd for us?" (case-insensitive substring on the verbatim position text).
 * Same gate as above. */
export async function searchPositionRoster(tenantId: string, query: string): Promise<PositionSearchResult[]> {
  const qs = new URLSearchParams({ q: query });
  const result = await request<{ query: string; results: PositionSearchResult[] }>(
    `/api/actor/people/position-search?${qs.toString()}`,
    { tenantId }
  );
  return result.results;
}

/** B6b (Huan 2026-07-13 correction): the URL of the ORIGINAL saved file —
 * GET /api/actor/sources/:id/original streams the retained bytes (a call-sheet
 * PDF/image) in their original form. `tenantId` is part of the signature
 * because the endpoint is tenant-scoped; scoping travels as a `?tenant=` query
 * param (see fetchSourceOriginal's own comment for why, as of 2026-09-20). */
export function sourceOriginalUrl(_tenantId: string, id: string): string {
  return `/api/actor/sources/${id}/original`;
}

/** CV7 — the bytes THIS DEVICE CAN DECODE. A separate route from `/original`
 * on purpose: `/original` must keep meaning "exactly as it was spoken", so a
 * flag on it would have quietly made that sentence false. When the recording
 * needs no rendition the server serves the original here anyway, so a caller
 * that wants to PLAY something never has to decide which one it wants. */
export function sourcePlayableUrl(_tenantId: string, id: string): string {
  return `/api/actor/sources/${id}/playable`;
}

/** ⚑ TENANT SCOPE RIDES `?tenant=`, NOT `X-Tenant-Id` — 2026-09-20, fixing a
 * defect this fetch shared with fetchSourceOriginal below and with
 * fetchMediaFile: under the r2 backend BOTH routes answer 302 to a presigned
 * R2 URL (mediaDelivery.ts's deliverBlob), and `fetch()` FOLLOWS a redirect —
 * turning what looks like a same-origin request into a real cross-origin one
 * partway through. A cross-origin fetch carrying a CUSTOM header (the old
 * `X-Tenant-Id`) forces the browser to preflight the REDIRECT TARGET, and R2's
 * CORS rule (proven by RecordImage's own 2026-09-20 fix) grants this app's
 * origin nothing beyond a plain GET — no `Access-Control-Allow-Headers` for
 * `x-tenant-id` — so the preflight fails and the whole fetch throws "Failed
 * to fetch", indistinguishable from the network actually being down.
 *
 * Proven with two bare Node http servers under a real Chromium (no such
 * harness existed here before): a same-origin fetch carrying a custom header,
 * redirected cross-origin, fails against a CORS rule that grants origin-only
 * access — and the IDENTICAL request with NO custom header (tenant scope
 * moved to the query string instead) succeeds against that same rule,
 * unchanged. `adoptTenantFromQuery` (actorSources.ts) already reads exactly
 * this query param — this fetch is the other half of a contract that already
 * existed. Nothing about tenant scoping weakens: a tenant id was always a
 * scope selector, never a credential (actorSources.ts's own comment); the
 * session cookie still authenticates and membership is still proven before a
 * byte is read. */
export async function fetchSourcePlayable(tenantId: string, id: string): Promise<SourceOriginal> {
  const url = tenantId ? `${sourcePlayableUrl(tenantId, id)}?tenant=${encodeURIComponent(tenantId)}` : sourcePlayableUrl(tenantId, id);
  const resp = await fetch(url, { credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const inline = (resp.headers.get('content-disposition') ?? '').toLowerCase().includes('inline');
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType, inline };
}

/** The retained original as an in-memory object URL + its mime type. Result
 * is embeddable directly. Callers MUST URL.revokeObjectURL(objectUrl) when
 * done. Throws ApiError on any non-2xx (e.g. 404 for a source with no stored
 * original). See fetchSourcePlayable's own comment (immediately above) for
 * why tenant scope rides `?tenant=` here and not a header. */
export interface SourceOriginal {
  objectUrl: string;
  mimeType: string;
  /** true when the server served the file inline (a render-safe type); false
   * when it forced a download (content-disposition: attachment). */
  inline: boolean;
}

export async function fetchSourceOriginal(tenantId: string, id: string): Promise<SourceOriginal> {
  const url = tenantId ? `${sourceOriginalUrl(tenantId, id)}?tenant=${encodeURIComponent(tenantId)}` : sourceOriginalUrl(tenantId, id);
  const resp = await fetch(url, { credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const inline = (resp.headers.get('content-disposition') ?? '').toLowerCase().includes('inline');
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType, inline };
}

/** B6b: the composed detail shape POST /api/actor/call-sheets returns. */
export interface CallSheetIngestResult {
  sourceId: string;
  shootDayId: string;
  peopleLinked: number;
}

/** POST /api/actor/call-sheets — uploads an ORIGINAL call-sheet file
 * (multipart) for a booked project; the server retains the original, parses a
 * shoot day, and links worked-with people. Uses a bare fetch (not `request`)
 * so the browser sets the multipart boundary itself. */
export async function uploadCallSheet(
  tenantId: string,
  projectId: string,
  file: File
): Promise<CallSheetIngestResult> {
  const form = new FormData();
  form.set('project_id', projectId);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/call-sheets', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as CallSheetIngestResult;
}

// B6c-2: sides + contracts uploads (multipart, mirroring uploadCallSheet
// above) and the guarded contract status transition.

/** POST /api/actor/sides response. `changeFlag` is true when this revision
 * SUPERSEDED a prior one (the calm "new revision" signal). */
export interface SidesIngestResult {
  sidesId: string;
  sourceId: string;
  supersededId: string | null;
  changeFlag: boolean;
}

/** Optional metadata to stamp on the new sides revision. */
export interface SidesUploadMeta {
  revisionColour?: string;
  versionLabel?: string;
  pagesOrScenesAffected?: string;
  changeSummary?: string;
}

/** POST /api/actor/sides — uploads an ORIGINAL sides file (multipart) anchored
 * to a project and/or audition. The server retains the original, creates the
 * new revision, and supersedes the prior current one. Bare fetch so the
 * browser sets the multipart boundary itself (same as uploadCallSheet). */
export async function uploadSides(
  tenantId: string,
  anchor: { projectId?: string; auditionId?: string },
  file: File,
  meta: SidesUploadMeta = {}
): Promise<SidesIngestResult> {
  const form = new FormData();
  if (anchor.projectId) form.set('project_id', anchor.projectId);
  if (anchor.auditionId) form.set('audition_id', anchor.auditionId);
  if (meta.revisionColour) form.set('revision_colour', meta.revisionColour);
  if (meta.versionLabel) form.set('version_label', meta.versionLabel);
  if (meta.pagesOrScenesAffected) form.set('pages_or_scenes_affected', meta.pagesOrScenesAffected);
  if (meta.changeSummary) form.set('change_summary', meta.changeSummary);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/sides', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as SidesIngestResult;
}

/** POST /api/actor/contracts response. `commitmentId` is set when a
 * `respond_by` created a response Commitment. */
export interface ContractIngestResult {
  contractId: string;
  sourceId: string;
  commitmentId: string | null;
}

export interface ContractUploadMeta {
  kind?: string;
  rate?: string;
  keyDates?: string;
  respondBy?: string;
  termsNote?: string;
}

/** POST /api/actor/contracts — uploads an ORIGINAL contract file (multipart)
 * for a project. The server retains the original + (respond_by) spins up a
 * real response Commitment. */
export async function uploadContract(
  tenantId: string,
  projectId: string,
  file: File,
  meta: ContractUploadMeta = {}
): Promise<ContractIngestResult> {
  const form = new FormData();
  form.set('project_id', projectId);
  if (meta.kind) form.set('kind', meta.kind);
  if (meta.rate) form.set('rate', meta.rate);
  if (meta.keyDates) form.set('key_dates', meta.keyDates);
  if (meta.respondBy) form.set('respond_by', meta.respondBy);
  if (meta.termsNote) form.set('terms_note', meta.termsNote);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/contracts', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as ContractIngestResult;
}

/** POST /api/actor/contracts/:id/status { to } — the GUARDED lifecycle
 * transition. An illegal transition is a 422 ApiError (surfaced calmly by the
 * caller). `commitmentClosed` is true when reaching signed/countersigned
 * closed the linked response Commitment. */
export async function setContractStatus(
  tenantId: string,
  id: string,
  to: string
): Promise<{ contract: RecordRow; commitmentClosed: boolean }> {
  return request<{ contract: RecordRow; commitmentClosed: boolean }>(`/api/actor/contracts/${id}/status`, {
    method: 'POST',
    tenantId,
    body: { to },
  });
}

export type SessionModality = 'in_person' | 'zoom' | 'self_tape';

export interface CreateSessionInput {
  sessionKind: string;
  round?: number;
  modality?: SessionModality;
  scheduledAt?: string;
  locationOrLink?: string;
  prepNotes?: string;
  travelNeeded?: boolean;
}

export interface CreateSessionResult {
  session: RecordRow;
  scheduling: { kind: 'commitment'; commitmentId: string } | null;
}

export async function createAuditionSession(
  tenantId: string,
  auditionId: string,
  input: CreateSessionInput
): Promise<CreateSessionResult> {
  return request<CreateSessionResult>(`/api/actor/auditions/${auditionId}/sessions`, {
    method: 'POST',
    tenantId,
    body: input,
  });
}

// ---------------------------------------------------------------------------
// B5b: MATERIALS — media (headshots/reels/clips), resume assembly, and the
// training/award/skill CRUD behind the resume. Backend: actorMedia.ts +
// actorResume.ts. Media BYTES are fetched with the tenant header and handed
// back as an object URL (fetchMediaFile) — a bare <img>/<video> src can't carry
// X-Tenant-Id, exactly the constraint fetchSourceOriginal solves above.
// ---------------------------------------------------------------------------

// WS5: 'poster' (project art) and 'avatar' (person portrait) added — both
// user-supplied because Wikidata gives metadata but never such imagery.
export type MediaKind = 'headshot' | 'reel' | 'clip' | 'other' | 'poster' | 'avatar';

/** One media row as GET /api/actor/media hands it back: the raw record fields
 * (kind/label/category/is_selected/on_resume/is_current/version_label/duration/
 * notes) plus its project + person links and the /file URL for its stored
 * bytes. */
export interface ActorMedia {
  id: string;
  fields: Record<string, unknown>;
  projectId: string | null;
  // WS5: the person this media is of (an avatar's subject); null otherwise.
  personId: string | null;
  fileUrl: string;
}

/** GET /api/actor/media?kind=&person=&project= — newest first. `person`/
 * `project` narrow to media linked to that record (e.g. kind='avatar' +
 * person yields that person's avatars, newest = index 0). */
export async function listMedia(
  tenantId: string,
  kind?: MediaKind,
  opts: { personId?: string; projectId?: string } = {}
): Promise<ActorMedia[]> {
  const qs = new URLSearchParams();
  if (kind) qs.set('kind', kind);
  if (opts.personId) qs.set('person', opts.personId);
  if (opts.projectId) qs.set('project', opts.projectId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const result = await request<{ media: ActorMedia[] }>(`/api/actor/media${suffix}`, { tenantId });
  return result.media;
}

/** The stored image/video as an in-memory object URL + mime type. Callers MUST
 * URL.revokeObjectURL(objectUrl) when done (see components/MediaEl.tsx, which
 * does this on unmount/refetch). Throws ApiError on any non-2xx (404 for media
 * with no stored file).
 *
 * ⚑ TENANT SCOPE RIDES `?tenant=`, NOT A HEADER — 2026-09-20, same defect and
 * same fix as fetchSourcePlayable/fetchSourceOriginal above (that comment
 * carries the full proof): under r2, `/file` 302s to a presigned R2 URL, and
 * a fetch() carrying a custom `X-Tenant-Id` header forces a CORS preflight
 * against the REDIRECT TARGET that this deployment's bucket rule refuses.
 * The server's `adoptTenantFromQuery` (actorMedia.ts) reads exactly this
 * query param. */
export interface MediaFile {
  objectUrl: string;
  mimeType: string;
}

export function mediaFileUrl(id: string): string {
  return `/api/actor/media/${id}/file`;
}

export async function fetchMediaFile(tenantId: string, id: string): Promise<MediaFile> {
  const url = tenantId ? `${mediaFileUrl(id)}?tenant=${encodeURIComponent(tenantId)}` : mediaFileUrl(id);
  const resp = await fetch(url, { credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType };
}

export interface MediaUploadMeta {
  label?: string;
  category?: string;
  versionLabel?: string;
  duration?: string;
  notes?: string;
  projectId?: string;
  // WS5: the person an avatar is of (kind='avatar').
  personId?: string;
}

/** POST /api/actor/media — uploads the ORIGINAL image/video (multipart) and
 * creates the actor_media row. Bare fetch so the browser sets the multipart
 * boundary itself (same as uploadCallSheet/uploadSides). */
export async function uploadMedia(
  tenantId: string,
  kind: MediaKind,
  file: File,
  meta: MediaUploadMeta = {}
): Promise<{ mediaId: string }> {
  const form = new FormData();
  form.set('kind', kind);
  if (meta.label) form.set('label', meta.label);
  if (meta.category) form.set('category', meta.category);
  if (meta.versionLabel) form.set('version_label', meta.versionLabel);
  if (meta.duration) form.set('duration', meta.duration);
  if (meta.notes) form.set('notes', meta.notes);
  if (meta.projectId) form.set('project_id', meta.projectId);
  if (meta.personId) form.set('person_id', meta.personId);
  form.set('file', file, file.name);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/actor/media', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as { mediaId: string };
}

/** POST /api/actor/media/:id/select — toggle curation flags. is_selected is
 * MULTI; on_resume + is_current are SINGULAR (setting one unsets the prior of
 * the same kind; is_current also links this reel's supersedes at the demoted
 * cut). `supersededId` is the demoted reel's id (null for the first current). */
export interface MediaSelectFlags {
  is_selected?: boolean;
  on_resume?: boolean;
  is_current?: boolean;
}

export interface MediaSelectResult {
  media: RecordRow;
  is_selected: boolean;
  on_resume: boolean;
  is_current: boolean;
  supersededId: string | null;
}

export async function selectMedia(
  tenantId: string,
  id: string,
  flags: MediaSelectFlags
): Promise<MediaSelectResult> {
  return request<MediaSelectResult>(`/api/actor/media/${id}/select`, {
    method: 'POST',
    tenantId,
    body: flags,
  });
}

// ---- Resume assembly + the training/award/skill inputs behind it. ----

export type ResumeVariant = 'full' | 'theatrical' | 'commercial' | 'voice';

export interface ResumeHeadshot {
  mediaId: string;
  fileUrl: string;
  label: string | null;
}

export interface ResumeCredit {
  projectId: string;
  production: string | null;
  role: string | null;
  medium: string | null;
  company: string | null;
}

export interface ResumeCreditGroup {
  group: string;
  credits: ResumeCredit[];
}

/** A training / award / skill row (the resume's editable sections) — the raw
 * record id + fields, exactly as the resume/CRUD routes return them. */
export interface ResumeEntry {
  id: string;
  fields: Record<string, unknown>;
}

export interface Resume {
  variant: string;
  headshot: ResumeHeadshot | null;
  creditGroups: ResumeCreditGroup[];
  training: ResumeEntry[];
  awards: ResumeEntry[];
  skills: ResumeEntry[];
}

export async function getResume(tenantId: string, variant?: ResumeVariant): Promise<Resume> {
  const qs = variant ? `?variant=${encodeURIComponent(variant)}` : '';
  return request<Resume>(`/api/actor/resume${qs}`, { tenantId });
}

/** The three resume sections that have their own CRUD endpoints. */
export type ResumeSectionKind = 'training' | 'awards' | 'skills';

export async function createResumeEntry(
  tenantId: string,
  kind: ResumeSectionKind,
  fields: Record<string, unknown>
): Promise<ResumeEntry> {
  const result = await request<{ record: RecordRow }>(`/api/actor/${kind}`, {
    method: 'POST',
    tenantId,
    body: { fields },
  });
  return { id: result.record.id, fields: result.record.fields };
}

export async function deleteResumeEntry(
  tenantId: string,
  kind: ResumeSectionKind,
  id: string
): Promise<void> {
  await request(`/api/actor/${kind}/${id}`, { method: 'DELETE', tenantId });
}

// ---------------------------------------------------------------------------
// B4b: CAREER — the Money / Documents / Stats read models + the expense ledger
// CRUD. Backends: actorMoney.ts, actorDocuments.ts, actorStats.ts. Every rate
// the Stats surface hands back (callbackRate/bookingRate/...) is a FRACTION in
// [0,1], never a percentage — the surface multiplies by 100 to render. Money
// numbers are raw currency values (no unit baked in); the surface owns the $.
// ---------------------------------------------------------------------------

/** One income-by-type tile: gross/net sums, the effective commission % across
 * the type, its count, and the overdue slice that colours the tile's severity. */
export interface MoneyIncomeType {
  type: string;
  gross: number;
  net: number;
  commissionPct: number;
  count: number;
  overdueGross: number;
}

export interface MoneyExpenseCategory {
  category: string;
  total: number;
  deductibleTotal: number;
}

/** GET /api/actor/money?year= — the money dashboard aggregation. `overdue` /
 * `overdueCount` are a NOW concern (never year-scoped); everything else is
 * scoped to `year`. */
export interface MoneyDashboard {
  year: number;
  thisYearGross: number;
  received: number;
  overdue: number;
  overdueCount: number;
  incomeByType: MoneyIncomeType[];
  expensesByCategory: MoneyExpenseCategory[];
  mileageKm: number;
}

export async function getMoney(tenantId: string, year?: number): Promise<MoneyDashboard> {
  const qs = year ? `?year=${year}` : '';
  return request<MoneyDashboard>(`/api/actor/money${qs}`, { tenantId });
}

/** GET /api/actor/money/export?year= — the structured year-end export the web
 * turns into a CSV download (buildMoneyCsv). Raw numbers only — no $/rate math
 * is baked in server-side. */
export interface MoneyExportIncomeRow {
  paymentId: string;
  date: string | null;
  type: string | null;
  project: string | null;
  gross: number;
  commissionPct: number | null;
  net: number;
  status: string;
  overdue: boolean;
}

export interface MoneyExportExpenseRow {
  expenseId: string;
  date: string | null;
  category: string | null;
  note: string | null;
  amount: number;
  deductible: boolean;
  mileageKm: number | null;
}

export interface MoneyExport {
  year: number;
  income: MoneyExportIncomeRow[];
  expenses: MoneyExportExpenseRow[];
  totals: {
    totalGross: number;
    totalNet: number;
    totalExpenses: number;
    totalDeductible: number;
    netIncome: number;
  };
}

export async function getMoneyExport(tenantId: string, year?: number): Promise<MoneyExport> {
  const qs = year ? `?year=${year}` : '';
  return request<MoneyExport>(`/api/actor/money/export${qs}`, { tenantId });
}

/** POST /api/actor/expenses — logs one expense (category/amount/date/note). */
export async function createExpense(
  tenantId: string,
  fields: Record<string, unknown>
): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>('/api/actor/expenses', {
    method: 'POST',
    tenantId,
    body: { fields },
  });
  return result.record;
}

export async function listExpenses(tenantId: string): Promise<ResumeEntry[]> {
  const result = await request<{ records: ResumeEntry[] }>('/api/actor/expenses', { tenantId });
  return result.records;
}

export async function deleteExpense(tenantId: string, id: string): Promise<void> {
  await request(`/api/actor/expenses/${id}`, { method: 'DELETE', tenantId });
}

/** One contract on the Documents surface. `sourceId` is present only when the
 * server later carries the linked ORIGINAL (the current documents aggregation
 * does not surface it, so the "view original" chip stays hidden until it does —
 * an honest absence, never a dead link). */
export interface DocumentContract {
  id: string;
  kind: string | null;
  status: string | null;
  rate: string | null;
  respondBy: string | null;
  keyDates: string | null;
  receivedAt: string | null;
  sourceId?: string | null;
}

export interface DocumentsTaxSummary {
  year: number;
  creditCount: number;
  deductibleTotal: number;
  expenseCount: number;
}

export interface DocumentsUnion {
  name: string | null;
  status: string | null;
  memberId: string | null;
}

/** GET /api/actor/documents?year= — Contracts + Tax-and-filings + Union. */
export interface ActorDocuments {
  contracts: DocumentContract[];
  taxSummary: DocumentsTaxSummary;
  union: DocumentsUnion;
}

export async function getDocuments(tenantId: string, year?: number): Promise<ActorDocuments> {
  const qs = year ? `?year=${year}` : '';
  return request<ActorDocuments>(`/api/actor/documents${qs}`, { tenantId });
}

export interface StatsGroupOffice {
  office: string;
  count: number;
  booked: number;
  bookingRate: number;
}

export interface StatsGroupType {
  medium: string;
  count: number;
  booked: number;
  bookingRate: number;
}

/** GET /api/actor/stats — computed analytics off real auditions. `callbackRate`
 * / `bookingRate` / `byX[].bookingRate` are FRACTIONS in [0,1]. `timeToCallback
 * Days` and `headshotAttribution` are DELIBERATELY DEFERRED (null) with a
 * `deferred` reason each — the surface renders them as honest "not enough data
 * yet", never fabricated. */
export interface ActorStats {
  total: number;
  reachedCallbackCount: number;
  bookedCount: number;
  callbackRate: number;
  bookingRate: number;
  timeToCallbackDays: number | null;
  byOffice: StatsGroupOffice[];
  byType: StatsGroupType[];
  headshotAttribution: { label?: string; note?: string } | null;
  deferred: string[];
}

export async function getStats(tenantId: string): Promise<ActorStats> {
  return request<ActorStats>('/api/actor/stats', { tenantId });
}

// ---------------------------------------------------------------------------
// B7 WEB — the tape → send-for-notes → external-reviewer-portal → received-notes
// loop, plus the Development-insights rollup. Backends: actorReviews.ts (owner
// tapes/reviews/development), actorReviewShare.ts (owner reviewer grants),
// actorReviewPortal.ts (the UNAUTHENTICATED reviewer portal — token-scoped, NO
// X-Tenant-Id). Tape + review-audio BYTES ride the object-URL pattern
// (fetchMediaFile / fetchReviewAudio / fetchPortalTape) exactly like media/
// sources above; the portal ones deliberately carry NO tenant header.
// ---------------------------------------------------------------------------

/** One tape: an `aa_media` record. The video is fetched from the record's
 * `original_file_ref` field by <RecordVideo>, so there is no URL here. */
export interface AuditionTape {
  id: string;
  fields: Record<string, unknown>;
}

/* ------------------------------------------------------------------ *
 * TAPES, ON THE PACK'S OWN VOCABULARY (2026-08-26).
 *
 * Both of these used to go through `/api/actor/*`, which is the JULY ACTOR
 * pack's namespace: those routes hardcode `actor_media` / `actor_audition`,
 * and `actor_assist` declares neither. On the live CallToWrap deployment the
 * upload failed at the audition lookup with 404 "audition not found" — a
 * button that looked functional and could never work. Confirmed against the
 * deployment 2026-08-25, not inferred from the code.
 *
 * Neither needs a server route. The generic record API already does all of
 * it, and does it BETTER: `uploadRecordBlob` presigns and the bytes go
 * STRAIGHT to R2, so a 1 GiB tape from a phone never transits the app heap —
 * exactly what the old multipart door did and what FIX 1 exists to end.
 * Proven end to end on the box: presign -> PUT -> commit -> aa_media ->
 * media_audition link.
 *
 * DELIBERATELY MINIMAL (Huan, 2026-08-26: "I don't think we need that much
 * detail, who the reader is, where it was recorded aren't really
 * important"). `aa_media` can carry reader, corner, takes_shot, take_sent
 * and an observation; a tape attached from the audition screen sets NONE of
 * them. They are all optional, and A6's TapeRecord renders whatever is
 * there — so the detail stays available to a parent who wants it on the tape
 * itself, while attaching a tape stays one tap. The upload captures what the
 * file already knows and asks for nothing.
 * ------------------------------------------------------------------ */

/** Every aa_media attached to this audition, in creation order. */
export async function listAuditionTapes(tenantId: string, auditionId: string): Promise<AuditionTape[]> {
  const { records, linksByRecordId } = await listAllRecordsWithLinks(tenantId, 'aa_media', {
    linkTypes: ['media_audition'],
  });
  return records
    .filter((r) => {
      const links = linksByRecordId[r.id] ?? [];
      return links.some(
        (l) =>
          l.link_type === 'media_audition' && (l.src_id === auditionId || l.dst_id === auditionId)
      );
    })
    .map((r) => ({ id: r.id, fields: r.fields }));
}

/* ------------------------------------------------------------------ *
 * ⚑ A HEADSHOT IS THE SAME ACT AS A TAPE (2026-09-16, the Day 3–4 walk,
 * item 5 — a BLOCKER).
 *
 * "Add a headshot" opened the GENERIC record editor for `aa_media` with the
 * kind and the performer pre-filled. `original_file_ref` is declared optional
 * on that type (a reel or a resume row may legitimately carry none), so the
 * form would Save with the file field untouched: POST /records, POST
 * /records/:id/links, and no presign/PUT/commit ever. The row existed, its
 * blob ref was null — and `looksFrom` (lib/resumeProfile.ts) keeps only media
 * with a non-empty ref, so the "successful" headshot vanished from the Looks
 * strip with nothing said. A record created and then invisible is the silent
 * failure this house does not ship: the Looks feature had nothing to select
 * and nothing to set as the resume photo, which is the whole feature.
 *
 * So this door becomes the door `uploadTape` already is: THE FILE IS THE ACT.
 * The picker IS the button, the bytes go up first, and the record is created
 * carrying its ref — so an `aa_media` with no photograph cannot be made here
 * at all, rather than being made and then filtered out of sight.
 * ------------------------------------------------------------------ */

/** Blob, then record, then link — `uploadTape`'s order, for `uploadTape`'s
 * reason: a failed upload leaves NO orphan record behind, only an
 * unreferenced staged blob. */
export async function uploadHeadshot(
  tenantId: string,
  subjectId: string,
  file: File,
  meta: { label?: string } = {}
): Promise<{ mediaId: string }> {
  const { ref } = await uploadRecordBlob(tenantId, file);
  const fields: Record<string, unknown> = { kind: 'headshot', original_file_ref: ref };
  // What the file itself says, never a guess — the same rule the tape door
  // follows. `aa_media`'s name template is `{kind} — {label}`, so a nameless
  // look renders as its own kind twice.
  const nameFromFile = file.name.replace(/\.[^.]+$/, '').trim();
  const label = meta.label ?? (nameFromFile.length > 0 ? nameFromFile : undefined);
  if (label) fields['label'] = label;
  if (Number.isFinite(file.lastModified) && file.lastModified > 0) {
    fields['recorded_at'] = new Date(file.lastModified).toISOString();
  }
  const media = await createRecord(tenantId, 'aa_media', fields);
  // ⚑ WHOSE FACE IT IS, IN THE SAME ACT. `media_subject` is the link the pack's
  // read scope walks for `aa_media` (`relations.aa_media`), and the Looks strip
  // reads through exactly that scope (`listAllScopedRecordsWithLinks`) — so a
  // headshot without it is a photograph belonging to nobody, and it would be
  // invisible on the very screen that filed it. Not swallowed the way the tape
  // door swallows its optional aboutness: there the read might genuinely name
  // nobody yet; here the performer is this door's own argument, so failing to
  // link is failing the act.
  await linkRecord(tenantId, media.id, 'media_subject', subjectId);
  return { mediaId: media.id };
}

/** `uploadHeadshot`'s own act, `kind: 'photo'` — a still that is neither a
 * headshot look, a self-tape, nor a reel (a portrait, an EPK montage, a
 * collage). Same order for the same reason (blob, then record, then link:
 * a failed upload leaves no orphan record) and the same `media_subject`
 * link (the pack's read scope for `aa_media`, `uploadHeadshot`'s own
 * comment explains it), so a photo filed here is exactly as visible as a
 * headshot is. */
export async function uploadPhoto(
  tenantId: string,
  subjectId: string,
  file: File,
  meta: { label?: string; kind?: 'photo' | 'headshot' | 'reel'; onProgress?: (fraction: number) => void } = {}
): Promise<{ mediaId: string }> {
  const { ref } = await uploadRecordBlob(tenantId, file, { onProgress: meta.onProgress });
  // `kind` (2026-09-24, widened B11): the same upload now serves Photos,
  // Headshots AND Reels — the public page takes `kind: 'headshot'` and
  // `kind: 'reel'` (pages.ts assertSelectable), and before B11 nothing on
  // this pack could create the latter.
  const fields: Record<string, unknown> = { kind: meta.kind ?? 'photo', original_file_ref: ref };
  const nameFromFile = file.name.replace(/\.[^.]+$/, '').trim();
  const label = meta.label ?? (nameFromFile.length > 0 ? nameFromFile : undefined);
  if (label) fields['label'] = label;
  if (Number.isFinite(file.lastModified) && file.lastModified > 0) {
    fields['recorded_at'] = new Date(file.lastModified).toISOString();
  }
  const media = await createRecord(tenantId, 'aa_media', fields);
  await linkRecord(tenantId, media.id, 'media_subject', subjectId);
  return { mediaId: media.id };
}

/** Blob, then record, then link — in that order on purpose: a failed upload
 * leaves NO orphan record behind, only an unreferenced staged blob. */
export async function uploadTape(
  tenantId: string,
  auditionId: string,
  file: File,
  meta: { label?: string } = {}
): Promise<{ mediaId: string }> {
  const { ref } = await uploadRecordBlob(tenantId, file);
  const fields: Record<string, unknown> = { kind: 'self_tape', original_file_ref: ref };
  // WHAT THE FILE ITSELF SAYS, so a tape added the easy way is not nameless
  // and undated. `aa_media`'s name template is `{kind} — {label}`, so without
  // a label the row renders as its own kind twice ("Self TapeSelf Tape"), and
  // without `recorded_at` every attached tape files under "no date recorded"
  // — which breaks the chronology the tape log exists to be.
  //
  // Both values are READ off the file, never guessed: the name the family
  // saved it under, and the file's own timestamp (for a tape just recorded on
  // a phone, that IS when it was recorded). A label the caller passes wins.
  const nameFromFile = file.name.replace(/\.[^.]+$/, '').trim();
  const label = meta.label ?? (nameFromFile.length > 0 ? nameFromFile : undefined);
  if (label) fields['label'] = label;
  if (Number.isFinite(file.lastModified) && file.lastModified > 0) {
    fields['recorded_at'] = new Date(file.lastModified).toISOString();
  }
  const media = await createRecord(tenantId, 'aa_media', fields);
  await linkRecord(tenantId, media.id, 'media_audition', auditionId);

  // ── ⚑ AND WHOSE TAPE IT IS (Day 2 walk, S3.31). ──
  //
  // `media_audition` says which read the tape belongs to. `media_subject` says
  // whose CAREER it is about, and that is the only one the pack's read scope
  // walks (`relations.aa_media`) — so a tape attached here carried no
  // aboutness at all and was, to the performer's own seat, a record that did
  // not exist. Everything hanging off it went with it: the reviews route
  // reaches a tape before it reaches the notes on it, so a child opening her
  // own read could not be answered for the feedback written about her.
  //
  // ⚠ IT IS TAKEN FROM THE AUDITION, NEVER FROM THE CALLER. Who a read is for
  // is the household's own record; a performer id off the wire would be a
  // caller deciding whose tape this is. And `media_subject` is NOT
  // `media_reader_subject` — a sibling who read off-camera does not become the
  // subject of the tape (recordTypes.ts draws that line in full).
  //
  // ⚠ NEVER FATAL. A read with no performer named yet is a real state, and a
  // tape that uploaded successfully must not be reported as failed over a link
  // that is about visibility rather than about the file.
  try {
    const { links } = await getRecordWithLinks(tenantId, auditionId);
    const performerId = links.find((l) => l.link_type === 'subject' && l.src_id === auditionId)?.dst_id;
    if (performerId) await linkRecord(tenantId, media.id, 'media_subject', performerId);
  } catch {
    /* the tape is uploaded and linked to its read; aboutness can be set by hand */
  }
  return { mediaId: media.id };
}

export type ReviewerRole = 'coach' | 'agent' | 'manager' | 'self' | 'other';

/** POST /api/actor/auditions/:id/reviewers — mints a scoped, one-time reviewer
 * link. `token`/`url` are returned ONCE here (never listed again). The caller
 * builds the shareable page URL from the token (shareablePortalUrl) — the raw
 * `url` is the bare API path the portal SPA then fetches. */
export interface CreatedReviewer {
  grantId: string;
  reviewId: string;
  token: string;
  url: string;
  expiresAt: string | null;
  tapePresent: boolean;
}

export async function createReviewer(
  tenantId: string,
  auditionId: string,
  input: { reviewerName?: string; reviewerRole?: ReviewerRole; expiresInDays?: number } = {}
): Promise<CreatedReviewer> {
  const body: Record<string, unknown> = {};
  if (input.reviewerName) body['reviewerName'] = input.reviewerName;
  if (input.reviewerRole) body['reviewerRole'] = input.reviewerRole;
  if (input.expiresInDays) body['expiresInDays'] = input.expiresInDays;
  return request<CreatedReviewer>(`/api/actor/auditions/${auditionId}/reviewers`, {
    method: 'POST',
    tenantId,
    body,
  });
}

export type ReviewerStatus = 'active' | 'submitted' | 'revoked' | 'expired';

/** One reviewer link as GET /api/actor/auditions/:id/reviewers lists it — status
 * + reviewer identity, NEVER the token (that's returned once, at creation). */
export interface ReviewerLink {
  grantId: string;
  reviewId: string;
  reviewerName: string | null;
  reviewerRole: string | null;
  status: ReviewerStatus;
  expiresAt: string | null;
  createdAt: string;
}

export async function listReviewers(tenantId: string, auditionId: string): Promise<ReviewerLink[]> {
  const result = await request<{ reviewers: ReviewerLink[] }>(`/api/actor/auditions/${auditionId}/reviewers`, {
    tenantId,
  });
  return result.reviewers;
}

export async function revokeReviewer(tenantId: string, grantId: string): Promise<void> {
  await request(`/api/actor/reviewers/${grantId}/revoke`, { method: 'POST', tenantId });
}

/** One time-marker on a review — a point in the tape (`t`, seconds) the reviewer
 * flagged, optionally with a short note. Clicking one seeks the tape. */
export interface ReviewMarker {
  t: number;
  label?: string;
}

/** One received review as GET /api/actor/auditions/:id/reviews shapes it. */
export interface AuditionReview {
  id: string;
  reviewerName: string | null;
  reviewerRole: string | null;
  body: string | null;
  transcript: string | null;
  timeMarkers: unknown;
  submittedAt: string | null;
  status: string | null;
  tapeId: string | null;
  tapeFileUrl: string | null;
  auditionId: string | null;
  audioFileUrl: string | null;
}

export async function getAuditionReviews(tenantId: string, auditionId: string): Promise<AuditionReview[]> {
  const result = await request<{ reviews: AuditionReview[] }>(`/api/actor/auditions/${auditionId}/reviews`, {
    tenantId,
  });
  return result.reviews;
}

/** Coerce the free-form `timeMarkers` a review carries into a clean marker list
 * — tolerant of the raw JSON the server stored (an array of {t,label} objects,
 * or bare numbers), never throwing on a malformed value. */
export function parseReviewMarkers(raw: unknown): ReviewMarker[] {
  if (!Array.isArray(raw)) return [];
  const out: ReviewMarker[] = [];
  for (const item of raw) {
    if (typeof item === 'number' && Number.isFinite(item)) {
      out.push({ t: item });
    } else if (item && typeof item === 'object') {
      const t = (item as { t?: unknown; time?: unknown }).t ?? (item as { time?: unknown }).time;
      if (typeof t === 'number' && Number.isFinite(t)) {
        const label = (item as { label?: unknown; note?: unknown }).label ?? (item as { note?: unknown }).note;
        out.push({ t, label: typeof label === 'string' && label ? label : undefined });
      }
    }
  }
  return out;
}

/** The review's recorded audio as an in-memory object URL + mime. Callers
 * MUST URL.revokeObjectURL when done. Throws ApiError on any non-2xx.
 *
 * ⚑ TENANT SCOPE RIDES `?tenant=`, NOT A HEADER — same 2026-09-20 fix as
 * fetchMediaFile/fetchSourcePlayable above (their own comments carry the
 * proof): a custom `X-Tenant-Id` header survives this route's 302-under-r2
 * redirect and forces a CORS preflight against R2 that this deployment's
 * bucket rule refuses. actorReviews.ts's `adoptTenantFromQuery` reads this
 * query param exactly as actorSources.ts's does. */
export async function fetchReviewAudio(tenantId: string, reviewId: string): Promise<MediaFile> {
  const base = `/api/actor/reviews/${reviewId}/audio`;
  const url = tenantId ? `${base}?tenant=${encodeURIComponent(tenantId)}` : base;
  const resp = await fetch(url, { credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType };
}

// ---------------------------------------------------------------------------
// THE CRAFT LAYER (W6, `2f377423`) — GATED ON `craft.layer`.
//
// ⚑ NOTHING HERE IS PROSE ABOUT A CHILD. Every string these types carry is a
// QUOTE the server has already re-checked against the note it came from
// (packages/core/src/craft/craftLayer.ts) — the client renders quotes and
// their doors, and never composes a sentence about a performer from them.
//
// ⚠ A 403 IS AN EXPECTED ANSWER, not an error to surface. A Free household
// gets `feature_not_in_plan` on all three routes; the surface reads `planHas`
// FIRST and simply does not offer the layer, so the 403 is the belt behind
// the braces rather than something a parent should ever see.
// ---------------------------------------------------------------------------

/** A quote, the note it came from, and the door onto that note. A receipt
 * with no door would be a claim, which is the thing this layer refuses. */
export interface CraftReceiptApi {
  noteId: string;
  quote: string;
  reviewerName: string | null;
  reviewerRole: string | null;
  /** True for the household's own note (`reviewer_role: 'self'`). */
  isSelf: boolean;
  receivedAt: string | null;
  tapeId: string | null;
  auditionId: string | null;
}

export interface CraftSummaryApi {
  lines: CraftReceiptApi[];
  generatedAt: string;
  /** ⚠ A NOTE HAS LANDED SINCE THIS WAS BUILT. The surface must SAY so —
   * `fromNoteCount` of `currentNoteCount` — rather than showing an
   * out-of-date summary as if it were current. */
  stale: boolean;
  fromNoteCount: number;
  currentNoteCount: number;
}

export interface AuditionSummaryApi {
  summary: CraftSummaryApi | null;
  /** How many coach notes it takes before there is anything to summarise. */
  minCoachNotes: number;
  coachNoteCount: number;
}

/** GET /api/actor/auditions/:id/summary — tenant-scoped, gated. */
export async function getAuditionSummary(tenantId: string, auditionId: string): Promise<AuditionSummaryApi> {
  return request<AuditionSummaryApi>(`/api/actor/auditions/${auditionId}/summary`, { tenantId });
}

/** POST /api/actor/auditions/:id/self-note — the family's own note.
 *
 * ⚠ `no_tape_to_note` IS A REAL, ACTIONABLE ANSWER, not a fault: a note lives
 * on the take it is about (`review_media` is master_detail), so an audition
 * with no tape yet has nowhere to put one. Show the server's own words. */
export async function addSelfNote(tenantId: string, auditionId: string, body: string): Promise<string> {
  const result = await request<{ reviewId: string }>(`/api/actor/auditions/${auditionId}/self-note`, {
    method: 'POST',
    tenantId,
    body: { body },
  });
  return result.reviewId;
}

export interface CraftTrendApi {
  /** A phrase lifted out of the notes, never a heading a model wrote. */
  label: string;
  receipts: CraftReceiptApi[];
}

export interface PerformerCraftApi {
  /** Null when this performer has no trend cache yet — read that as "not
   * enough repetition yet", never as "no patterns exist". */
  trends: { items: CraftTrendApi[]; generatedAt: string; stale: boolean } | null;
  summaries: Array<{ auditionId: string } & CraftSummaryApi>;
  /** The lever (`craft.trend_min_receipts`) as this household has it. */
  minReceipts: number;
  noteCount: number;
}

/** GET /api/actor/performers/:id/craft — tenant-scoped, gated. */
export async function getPerformerCraft(tenantId: string, performerId: string): Promise<PerformerCraftApi> {
  return request<PerformerCraftApi>(`/api/actor/performers/${performerId}/craft`, { tenantId });
}

// ---- The Development-insights rollup (GET /api/actor/development). ----------

export interface DevelopmentFeedback {
  reviewId: string;
  reviewer: string | null;
  reviewerRole: string | null;
  snippet: string;
  tapeId: string | null;
  tapeFileUrl: string | null;
  auditionId: string | null;
  submittedAt: string | null;
}

export interface DevelopmentTheme {
  phrase: string;
  count: number;
}

export interface DevelopmentProgress {
  total: number;
  reachedCallbackCount: number;
  bookedCount: number;
  callbackRate: number;
  bookingRate: number;
}

export interface DevelopmentClass {
  id: string;
  fields: Record<string, unknown>;
}

export interface Development {
  recentFeedback: DevelopmentFeedback[];
  recurringThemes: DevelopmentTheme[];
  callbackProgress: DevelopmentProgress;
  classes: DevelopmentClass[];
  notes?: string[];
}

export async function getDevelopment(tenantId: string): Promise<Development> {
  return request<Development>('/api/actor/development', { tenantId });
}

// ---------------------------------------------------------------------------
// THE UNAUTHENTICATED REVIEWER PORTAL (actorReviewPortal.ts). Every call here is
// authorized SOLELY by the grant token in the path — there is NO session, NO
// X-Tenant-Id, NO tenant scope for an external party (D9: "never accounts"). A
// bad/expired/revoked/used token is an EXPECTED outcome (404 / 410), surfaced
// calmly, never a crash. These deliberately do NOT go through `request` (which
// is the tenant-scoped client) — they're plain, header-free fetches.
// ---------------------------------------------------------------------------

/** The reviewer-facing shareable page URL. The bare `/portal/review/:token`
 * path is a registered SERVER route (returns JSON to fetch), so the human-
 * facing link is the HASH route that loads THIS SPA, which then fetches the
 * JSON itself. Absolute (origin-qualified) so it survives a copy-paste into an
 * email. */
export function shareablePortalUrl(token: string): string {
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return `${origin}/#/portal/review/${token}`;
}

/** The reviewer's minimal scoped context. A null return means the token is
 * invalid/expired/revoked/target-gone (a uniform 404 server-side) — the caller
 * renders the graceful "this link is no longer active" state. */
export interface ReviewPortalContext {
  /** ⚑ THE HOUSEHOLD'S OWN NAME, when their tier includes Pro's de-branding —
   * "Your name, not ours". `null` on every other tier, and on a tier the
   * server could not resolve: the fallback is the PRODUCT's name, because
   * guessing the other way hands out a paid feature. The client must not
   * decide this — a reviewer holds a token and no session. */
  brand: string | null;
  tapePresent: boolean;
  audition: { role: string | null; project: string | null } | null;
  reviewerName: string | null;
  reviewerRole: string | null;
  alreadySubmitted: boolean;
  tapeUrl: string | null;
}

export async function getReviewPortalContext(token: string): Promise<ReviewPortalContext | null> {
  const resp = await fetch(`/portal/review/${token}`, { credentials: 'same-origin' });
  if (resp.status === 404) return null;
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as ReviewPortalContext;
}

/** The granted tape's bytes as an object URL + mime — TOKEN-scoped, header-free
 * (must NOT send X-Tenant-Id: the reviewer has no tenant). Callers MUST
 * URL.revokeObjectURL when done. Throws ApiError on any non-2xx.
 *
 * ⚑ NOT AFFECTED by the redirect-CORS defect fetchMediaFile/fetchReviewAudio/
 * fetchSourcePlayable carried (their comments have the proof): this fetch
 * sends NO custom header, so even when this route 302s cross-origin under
 * r2, the browser treats it as a plain "simple" GET — no preflight, and the
 * final response only needs `Access-Control-Allow-Origin`, which R2's rule
 * already grants this app's origin. Leave it header-free; adding one back
 * would reopen exactly this defect. */
export async function fetchPortalTape(token: string): Promise<MediaFile> {
  const resp = await fetch(`/portal/review/${token}/tape`, { credentials: 'same-origin' });
  if (!resp.ok) {
    const contentType = resp.headers.get('content-type') ?? '';
    const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
    throw extractError(resp.status, parsed);
  }
  const mimeType = (resp.headers.get('content-type') ?? 'application/octet-stream').split(';')[0]!.trim();
  const blob = await resp.blob();
  return { objectUrl: URL.createObjectURL(blob), mimeType };
}

/** The outcome of a portal submit. `submitted` — done (act-once). `consumed` —
 * this review was already submitted (410). `invalid` — the link is no longer
 * active (404). Never throws for these EXPECTED outcomes; a caller renders each
 * calmly. */
export type PortalSubmitOutcome =
  | { outcome: 'submitted'; transcriptFilled: boolean }
  | { outcome: 'consumed' }
  | { outcome: 'invalid' };

/** POST /portal/review/:token — submit the review (act-once). Sends multipart
 * when an audio recording is present (so the browser sets the boundary), else a
 * plain JSON body. `timeMarkers` is serialised to a JSON string either way. */
export async function submitReviewPortal(
  token: string,
  input: { body?: string; audio?: Blob; audioFilename?: string; timeMarkers?: ReviewMarker[] }
): Promise<PortalSubmitOutcome> {
  const markersJson =
    input.timeMarkers && input.timeMarkers.length > 0 ? JSON.stringify(input.timeMarkers) : undefined;

  let resp: Response;
  if (input.audio) {
    const form = new FormData();
    if (input.body) form.set('body', input.body);
    if (markersJson) form.set('time_markers', markersJson);
    form.set('file', input.audio, input.audioFilename ?? 'review-audio.webm');
    resp = await fetch(`/portal/review/${token}`, { method: 'POST', credentials: 'same-origin', body: form });
  } else {
    const body: Record<string, unknown> = {};
    if (input.body) body['body'] = input.body;
    if (markersJson) body['time_markers'] = markersJson;
    resp = await fetch(`/portal/review/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  }

  if (resp.status === 410) return { outcome: 'consumed' };
  if (resp.status === 404) return { outcome: 'invalid' };
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return { outcome: 'submitted', transcriptFilled: Boolean((parsed as { transcriptFilled?: boolean })?.transcriptFilled) };
}

// ---- Knot push-to-talk (Core hardening 2026-07-29, Item 6 — ported from
// the Rexx fork's 2026-07-21 build, generalized: routes moved from
// /api/hvac/* to the generic /api/knot/* namespace, and KnotContextType/
// KnotTargetType are plain `string` here rather than an HVAC-literal union
// — Core has no way to know a vertical's own context/target keys ahead of
// time (see KnotIntentPackConfig, @vowric/shared). Only registered when the
// active pack declares a knotIntent config (packages/server/src/plugins/
// knotIntent.ts) — these client functions simply 404 against a pack that
// doesn't opt in, same as any other absent route. ----

export interface TranscribeResult {
  text: string;
  ref: string;
  mimeType: string;
}

/** Uploads a recorded voice note, transcribes it, and retains the audio as
 * a stored blob in one call — the ref rides through capture exactly like a
 * photo attachment, same source-retention provenance a typed+photo capture
 * already gets. */
export async function transcribeVoiceNote(tenantId: string, audio: Blob, filename: string): Promise<TranscribeResult> {
  const form = new FormData();
  form.set('file', audio, filename);
  const headers = new Headers();
  if (tenantId) headers.set('X-Tenant-Id', tenantId);
  const resp = await fetch('/api/knot/transcribe', {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as TranscribeResult;
}

export type KnotContextType = string;
export type KnotTargetType = string;

export interface KnotCandidate {
  id: string;
  name: string;
}

export type KnotIntentResult =
  | { status: 'unclear' }
  // R-10 — the update lane (sent only when the request declared
  // supportsUpdate; a stale bundle never sees these statuses).
  | {
      status: 'update-ready';
      recordId: string;
      recordType: string;
      recordName: string;
      targetType: KnotTargetType;
      targetLabel: string;
      diffs: { field: string; label: string; from: unknown; to: unknown }[];
      fields: Record<string, unknown>;
    }
  | {
      status: 'update-confirm';
      targetType: KnotTargetType;
      /** The record TYPE key ('hvac_client') — what the confirm dialog's
       * search needs; targetType above is the intent target key. */
      typeKey: string;
      targetLabel: string;
      itemName: string;
      candidates: KnotCandidate[];
      suggestion: KnotCandidate | null;
    }
  | {
      status: 'ready';
      targetType: KnotTargetType;
      targetLabel: string;
      /** Computed server-side (the target's declared nameField, or its
       * label) — the client never re-derives this per target type. */
      itemName: string;
      fields: Record<string, unknown>;
      parent: { type: KnotContextType; id: string; name: string } | null;
    }
  | {
      status: 'confirm';
      targetType: KnotTargetType;
      targetLabel: string;
      itemName: string;
      fields: Record<string, unknown>;
      parentType: KnotContextType;
      parentLabel: string;
      candidates: KnotCandidate[];
      suggestion: KnotCandidate | null;
    };

export async function knotIntent(
  tenantId: string,
  text: string,
  context?: { type: KnotContextType; id: string },
  /** R-10 — the confirm round trip: the person picked WHICH record; the
   * server skips resolution and diffs against exactly this one. */
  subject?: { type: string; id: string }
): Promise<KnotIntentResult> {
  return request('/api/knot/intent', {
    method: 'POST',
    tenantId,
    // supportsUpdate is this bundle's capability declaration, not an
    // option: every build shipping this code can render update responses.
    body: { text, contextType: context?.type, contextId: context?.id, supportsUpdate: true, subject },
  });
}

/** R-10 — the spoken/typed update door. Same gated write PATCH /records/:id
 * performs, origin 'knot', with CV5 §4 recording retention on the changed
 * record. */
export async function knotIntentUpdate(
  tenantId: string,
  recordId: string,
  fields: Record<string, unknown>,
  voice?: KnotVoiceRef
): Promise<KnotCreateResult> {
  return request<KnotCreateResult>('/api/knot/intent/update', {
    method: 'POST',
    tenantId,
    body: { recordId, fields, voice },
  });
}

/** CV5 §4 — THE RECORDING THIS UTTERANCE CAME FROM, carried by reference.
 *
 * `transcribeVoiceNote` above already stored the audio and handed back its
 * `ref`; this is that ref travelling the one step further it always should
 * have. Never bytes: the blob is on the server, and re-uploading it would be
 * the second copy of a thing we already own. */
export interface KnotVoiceRef {
  ref: string;
  mimeType?: string;
  filename?: string;
  /** What we heard. Sent so the retained recording carries our reading of it
   * WITHOUT a second transcription — the same words the note was written
   * from, never re-derived. */
  transcript?: string;
}

/** What became of a recording handed to `knotIntentCreate`. `null` = it was
 * NOT retained (this build declares nowhere to keep one, or the utterance had
 * no subject to attach it to). A caller that sent `voice` MUST read this and
 * say so — the whole defect CV5 §4 fixes was a recording going nowhere in
 * silence. Absent entirely when no recording was sent. */
export interface KnotCreateResult {
  record: RecordRow;
  recording?: { sourceId: string | null } | null;
}

export async function knotIntentCreate(
  tenantId: string,
  targetType: KnotTargetType,
  fields: Record<string, unknown>,
  parent?: { type: KnotContextType; id: string },
  voice?: KnotVoiceRef
): Promise<KnotCreateResult> {
  return request<KnotCreateResult>('/api/knot/intent/create', {
    method: 'POST',
    tenantId,
    body: { targetType, fields, parentType: parent?.type, parentId: parent?.id, voice },
  });
}


// ---- Read-only agent access (2026-09-01) ----

export interface AgentAccessInfo {
  url: string;
  hasKey: boolean;
  keyPrefix: string | null;
  createdAt: string | null;
}

/** Owner-only; a non-owner gets a 403 ApiError, which callers render as
 * "this section is not yours" by rendering nothing. */
export async function getAgentAccess(tenantId: string): Promise<AgentAccessInfo> {
  return request('/api/agent/access', { tenantId });
}

/** Mints a NEW key and revokes the previous one. The returned `key` is shown
 * exactly once — it is never retrievable again. */
export async function rotateAgentKey(tenantId: string): Promise<{ key: string; keyPrefix: string; url: string }> {
  return request('/api/agent/access/rotate', { method: 'POST', tenantId, body: {} });
}

// ---- Team (Core hardening 2026-07-29, Item 8 — domain/nav roles) ----

/** One tab a pack-declared nav role shows — mirrors KnotIntentPackConfig's
 * own opaque-string convention (@vowric/shared): `tab` must match one of
 * this fork's own routable TopTab values, but Core has no way to see (or
 * validate against) a fork-specific route union, so it's a plain string
 * here too. */
export interface NavTabDef {
  tab: string;
  label: string;
  short?: string;
}

export interface TeamMember {
  id: string;
  userId: string;
  email: string | null;
  name: string | null;
  role: string;
  status: string;
  jobRoles: string[];
}

/** S1 (wf02 §01b): one nav-role GROUP a membership holds — the server
 * resolves the union (authority is a union; the rail never hides a held
 * capability) and the rail renders the groups in order, landing group
 * first. */
export interface NavGroup {
  key: string;
  label: string;
  tabs: NavTabDef[];
  /** The few destinations this role's PHONE bottom bar carries, when the
   * pack names them. Absent = the bar shows the same union the rail does. */
  phoneTabs?: NavTabDef[];
}

/** S12b: one job role the ACTIVE PACK declares, and the tier it is seated
 * at. The invite door offers exactly this list — a hardcoded
 * owner/admin/dispatcher/tech in the client would be a second declaration
 * of the pack's own vocabulary, and the first pack edit would make it a
 * lie. Absent for a pack that declares no seating (Actor, Personal). */
export interface PackJobRole {
  key: string;
  seat: string;
}

/** GET /api/team/me — any active member. `navTabs` is present only when
 * the active pack declared a NavRolesPackConfig; absent (not an empty
 * array) otherwise — callers distinguish "no pack nav config" from "pack
 * says this role gets zero tabs" by presence. `navGroups` (S1) travels
 * with it: EVERY group the membership holds, landing group first —
 * a groups-aware client prefers it over the single-landing `navTabs`.
 * `packJobRoles` (S12b) is that same declare-to-exist convention for the
 * pack's job-role vocabulary. */
export async function getTeamMe(tenantId: string): Promise<{
  member: TeamMember;
  navTabs?: NavTabDef[];
  navGroups?: NavGroup[];
  packJobRoles?: PackJobRole[];
  /** G-17 — THIS DEPLOYMENT'S PACK REQUIRES EVERY MEMBER'S BIRTH DATE
   * BEFORE THEIR SEAT CAN GO LIVE (`requiresMemberAge`).
   *
   * Served beside `packJobRoles` because it is the same kind of fact and the
   * same declare-to-exist convention: absent (never `false`) for every pack
   * that has never heard of the rule, so their invite doors render exactly
   * as they did. It rides THIS answer rather than `/auth/entry` because it
   * is not front-door copy — it is a condition on inviting, and this is the
   * call the invite door already makes to learn what it may offer.
   *
   * Without it the web could not know that `POST /auth/invites/accept` was
   * going to refuse: an invited co-parent got a 409 `member_age_required`
   * with nothing on any screen able to fix it. */
  requiresMemberAge?: boolean;
  /** ⚑ MAY THIS SEAT INVITE ANYBODY — the pack's AUTHORITY MANIFEST
   * answering, not the engine rank (2026-09-15, the Day 1 walk).
   *
   * `actor_assist` seats its second adult at `admin` and reserves
   * `actor_assist.member.invite` to `parent`, so the client's rank check drew
   * "Your people" and the invite door for a co-parent — an act the manifest
   * says is not hers. The server resolves it against her minted capabilities
   * and answers one boolean; the client holds no copy of the rule.
   *
   * Absent (not `false`) for a pack that declares no invite action, so every
   * other build keeps the rank check it always had. */
  mayInvite?: boolean;
}> {
  return request('/api/team/me', { tenantId });
}

/** GET /api/team/members — the roster. ADMIN-ONLY on the server, which is
 * a real constraint on callers: a surface that only wants to put a NAME on
 * somebody's id (S11's asks screen) must treat the 403 as "no names
 * available", never as a failed screen. */
export async function listTeamMembers(tenantId: string): Promise<TeamMember[]> {
  const result = await request<{ members: TeamMember[] }>('/api/team/members', { tenantId });
  return result.members;
}

/**
 * S12b — POST /auth/invites: THE SEND HALF, which this app has never had.
 * `acceptInvite` below was the only half wired, which is why an owner could
 * not invite anybody from any screen despite holding the authority to.
 *
 * `jobRoles` carries the pack's own vocabulary and the SERVER derives the
 * engine tier from it (auth.ts's seating block) — so this deliberately does
 * NOT send `role`: naming a tier here would either be redundant or, if it
 * disagreed with the pack's seating, a 400 seating_conflict. Authority is
 * the server's on both axes: a caller who may not invite gets the route's
 * ordinary 403, never a button this client decided to grey out.
 *
 * Returns the membership id the invite created — the handle
 * `bindMembershipParty` needs to bind that person to their own record.
 */
export async function inviteMember(
  tenantId: string,
  email: string,
  jobRoles: string[]
): Promise<{ membershipId: string }> {
  // THE SECOND TAP (2026-09-16, the Day 3–4 walk, item 6). The route now asks
  // the pack's authority manifest, and an invite pings a person — so the
  // action is declared external and the gate PROPOSES before it acts. A 409
  // `authority: 'propose'` is not a refusal, it is "say so and I will":
  // re-sent once with `proposalApproved`, byte for byte the two-step
  // `enrolMemberTelegram` above already takes. A 409 `convert` is left to
  // throw — this actor may not invite at all, approval is not authority, and
  // the panel renders the server's own refusal rather than inventing one.
  try {
    return await request('/auth/invites', {
      method: 'POST',
      tenantId,
      body: { email, jobRoles },
    });
  } catch (err) {
    const offer = authorityOfferFrom(err);
    if (!offer || offer.authority !== 'propose') throw err;
    return await request('/auth/invites', {
      method: 'POST',
      tenantId,
      body: { email, jobRoles, proposalApproved: true },
    });
  }
}

/** A7 — POST /api/team/members/:id/date-of-birth.
 *
 * The door for `recordDateOfBirth`, which Core has always had and nothing
 * could reach. The actor_assist pack declares `requiresMemberAge: true`, so
 * WITHOUT THIS CALL A SEAT CAN NEVER GO ACTIVE — the invite is accepted, the
 * age guard finds no date, and activation rolls back. It is not an optional
 * detail of enrolment; it is the half without which enrolment does not
 * happen.
 *
 * `dateOfBirth` is a calendar day, `YYYY-MM-DD`. Never an instant: a birth
 * date parsed as a timestamp is the day before for half the world. */
export async function recordMemberDateOfBirth(
  tenantId: string,
  membershipId: string,
  dateOfBirth: string
): Promise<void> {
  await request(`/api/team/members/${encodeURIComponent(membershipId)}/date-of-birth`, {
    method: 'POST',
    tenantId,
    body: { dateOfBirth },
  });
}

/** S12b — POST /api/team/members/:id/parties. C5f's binding: what makes a
 * membership and the record that IS that person one thing, and therefore
 * what makes every "@assigned" capability in the Rexx manifest reachable
 * for a newly invited tech. Admin+ server-side, like the rest of the
 * roster. */
export async function bindMembershipParty(
  tenantId: string,
  membershipId: string,
  partyRecordId: string
): Promise<void> {
  await request(`/api/team/members/${encodeURIComponent(membershipId)}/parties`, {
    method: 'POST',
    tenantId,
    body: { partyRecordId },
  });
}

// ---- Generic action-link portal (Core hardening 2026-07-29, Item 9) ----
// Extends the existing external, unauthenticated /p/:token grant portal
// (whose only prior client was ReviewerPortal.tsx's own bespoke
// /portal/review/:token namespace) with a GENERIC screen for any 'record'
// target grant — approve/sign/rate/upload on a quote, invoice, whatever a
// vertical declares eligible. Header-free, `credentials: 'same-origin'`,
// same pattern getReviewPortalContext/submitReviewPortal already use — the
// token IS the only credential, there is no session and no tenant.

export type GrantAllowedAction = 'view' | 'approve' | 'sign' | 'rate' | 'upload';

export interface PortalRecordChild {
  linkType: string;
  id: string;
  recordType: string;
  displayName: string;
  fields: Record<string, unknown>;
}

export interface PortalProposalView {
  kind: 'proposal_approval';
  proposal: {
    id: string;
    proposed: unknown;
    rationale: string | null;
    sourceRef: unknown;
    confidence: number | null;
    status: string;
  };
  alreadyActioned: boolean;
  decidedAt: string | null;
  commitment: { id: string } | null;
}

export interface PortalRecordView {
  kind: 'record_view';
  record: { id: string; recordType: string; displayName: string; fields: Record<string, unknown> };
  children: PortalRecordChild[];
  schema: RecordTypeSchema;
  allowedAction: GrantAllowedAction;
  alreadyActioned: boolean;
}

export type PortalTargetView = PortalProposalView | PortalRecordView;

/** GET /p/:token — null on 404 (invalid/expired/revoked token), mirroring
 * getReviewPortalContext's own null-on-404 convention. Throws ApiError on
 * any other non-2xx. */
export async function getRecordGrantPortal(token: string): Promise<PortalTargetView | null> {
  const resp = await fetch(`/p/${token}`, { credentials: 'same-origin' });
  if (resp.status === 404) return null;
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  return parsed as PortalTargetView;
}

export type RecordGrantActionOutcome =
  | { outcome: 'actioned'; recordId: string; action: GrantAllowedAction }
  | { outcome: 'consumed' }
  | { outcome: 'invalid' }
  | { outcome: 'conflict' };

/** POST /p/:token/action — multipart when `file` is present (the 'upload'
 * action), a bodyless POST otherwise (approve/sign/rate need nothing but
 * the token itself) — mirrors submitReviewPortal's own multipart-vs-plain
 * branch. 410/404/409 map to typed outcomes, never a thrown ApiError, so
 * the screen can render the calm state for each without a try/catch. */
export async function submitRecordGrantAction(
  token: string,
  file?: Blob,
  fileName?: string
): Promise<RecordGrantActionOutcome> {
  const resp = file
    ? await (() => {
        const form = new FormData();
        form.set('file', file, fileName ?? 'upload');
        return fetch(`/p/${token}/action`, { method: 'POST', credentials: 'same-origin', body: form });
      })()
    : await fetch(`/p/${token}/action`, { method: 'POST', credentials: 'same-origin' });

  if (resp.status === 410) return { outcome: 'consumed' };
  if (resp.status === 404) return { outcome: 'invalid' };
  if (resp.status === 409) return { outcome: 'conflict' };
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (!resp.ok) throw extractError(resp.status, parsed);
  const body = parsed as { recordId: string; action: GrantAllowedAction };
  return { outcome: 'actioned', recordId: body.recordId, action: body.action };
}

// ---------------------------------------------------------------------------
// V19b-2 — Eden's client onboarding surface (docs/console/v19b-the-client-
// surface.md). Same family as the review/record-grant portals just above:
// ANONYMOUS, token-scoped — no cookie, no X-Tenant-Id, the token in the
// path/body IS the credential. `GET .../links/:token`, `POST
// .../activate|resume|complete`, and `GET .../capture` are V18a's own
// already-live routes; `.../consent-texts`, `.../consent`,
// `.../profile-fact` and `.../observation` are V19b-1's — this file only
// CODES AGAINST that contract (docs/console/v19b-the-client-surface.md's
// route table), it does not implement it. A 404/410 here means "the link
// is no longer good" (INV-EDEN-10's uniform-invalid posture) and is always
// surfaced as a typed outcome, never a thrown ApiError — the screen must
// render each calmly, the same discipline getReviewPortalContext already
// takes.
const EDEN_BASE = '/api/eden/onboarding/links';

async function edenFetch(
  path: string,
  init: { method?: 'GET' | 'POST'; body?: unknown } = {}
): Promise<{ status: number; parsed: unknown }> {
  const resp = await fetch(`${EDEN_BASE}${path}`, {
    method: init.method ?? 'GET',
    headers: init.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    credentials: 'same-origin',
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  return { status: resp.status, parsed };
}

/** Every scoped call below resolves to one of these three shapes — 'invalid'
 * and 'expired' are EXPECTED outcomes (INV-EDEN-10 / the distinct 410), never
 * thrown, so a caller renders each without a try/catch. `message` carries
 * the server's own honest sentence (sendUniformInvalid / sendExpired) —
 * read, never re-worded, so the two stay in one place. */
export type EdenOutcome<T> =
  | { status: 'invalid'; message?: string }
  | { status: 'expired'; message?: string }
  | ({ status: 'open' } & T);

function classifyEdenResponse<T>(status: number, parsed: unknown): EdenOutcome<T> | null {
  const message = (parsed as { error?: { message?: string } } | null)?.error?.message;
  if (status === 404) return { status: 'invalid', message };
  if (status === 410) return { status: 'expired', message };
  // Spread order matters: some real success payloads carry their OWN
  // 'status' field ('.../complete' answers {status:'completed', ...} —
  // V18a's own pre-existing convention, unrelated to this envelope) which
  // would silently clobber the 'open' discriminant every caller below
  // checks against if it were spread LAST. `parsed` goes first so this
  // envelope's own 'open' always wins — found by the real-Chromium walk
  // (scripts/walkEdenOnboardingV19b2.ts), where completeEdenOnboarding
  // read back status:'completed' as a FALSE invalid/expired and showed
  // "That link stopped working" over an onboarding that had, in fact,
  // just completed — a mocked unit test could not have caught this, since
  // every mock supplied 'open' directly rather than a real route's shape.
  if (status >= 200 && status < 300) return { ...(parsed as object), status: 'open' } as EdenOutcome<T>;
  return null;
}

export interface EdenLinkStatus {
  accountName: string;
  partyName: string;
}

/** GET .../links/:token — status only, GET-safe (V18a, INV-EDEN-1). Never
 * mutates, so it is safe to call from a plain page load / refresh. */
export async function getEdenOnboardingLink(token: string): Promise<EdenOutcome<EdenLinkStatus>> {
  const { status, parsed } = await edenFetch(`/${token}`);
  const outcome = classifyEdenResponse<EdenLinkStatus>(status, parsed);
  if (!outcome) throw extractError(status, parsed);
  return outcome;
}

export interface EdenSessionContext {
  sessionId: string;
  accountId: string;
  accountName: string;
  partyId: string;
  partyName: string;
  resumed: boolean;
  receipt: string;
}

/** POST .../activate — idempotent (core's own activateOrResumeCore):
 * a first visit opens a session, a repeat visit on the same still-open
 * token resumes it. The screen never has to know which happened ahead of
 * time — it always calls activate, and reads `resumed` back to greet
 * accordingly. */
export async function activateEdenOnboarding(token: string): Promise<EdenOutcome<EdenSessionContext>> {
  const { status, parsed } = await edenFetch(`/${token}/activate`, { method: 'POST' });
  const outcome = classifyEdenResponse<EdenSessionContext>(status, parsed);
  if (!outcome) throw extractError(status, parsed);
  return outcome;
}

/** The live wording for one consent kind — INV-19B-3: rendered VERBATIM,
 * never paraphrased or hard-coded, and the (kind, version) pair travels
 * back with the grant so the A2P record points at the exact bytes shown.
 * `key` mirrors `kind` server-side (consent_texts.key === the kind string)
 * — carried through unchanged rather than re-derived on this side. */
export interface EdenConsentText {
  kind: EdenContactConsentKind;
  key: string;
  version: number;
  body: string;
}

/** GET .../consent-texts — the live wording the consent checkbox renders.
 * V19b-1's real route (packages/server/src/plugins/edenOnboarding.ts)
 * returns `texts` as an ARRAY, one entry per kind that currently resolves
 * — a kind with nothing live is simply absent, never synthesized, and the
 * screen must not offer that checkbox at all. */
export async function getEdenConsentTexts(
  token: string
): Promise<EdenOutcome<{ texts: EdenConsentText[] }>> {
  const { status, parsed } = await edenFetch(`/${token}/consent-texts`);
  const outcome = classifyEdenResponse<{ texts: EdenConsentText[] }>(status, parsed);
  if (!outcome) throw extractError(status, parsed);
  return outcome;
}

export type EdenContactConsentKind = 'contact.email' | 'contact.sms';

/** POST .../consent — grants one or more contact-consent kinds in ONE
 * call (V19b-1's real `consentGrantSchema`: `{ grants: [{kind,
 * textVersion}] }`, no `textKey` — the server derives the key from
 * `kind` itself). Each grant names the EXACT text version the client was
 * shown (INV-19B-3); the server 409s the whole call if any version has
 * gone stale since. `grantedVia: 'form'` server-side (D1's own
 * vocabulary) — this is always the web door, never the spoken one. */
export async function grantEdenConsent(
  token: string,
  grants: Array<{ kind: EdenContactConsentKind; textVersion: number }>
): Promise<EdenOutcome<{ grants: Array<{ kind: EdenContactConsentKind; version: number; id: string }>; receipt: string }>> {
  const { status, parsed } = await edenFetch(`/${token}/consent`, {
    method: 'POST',
    body: { grants },
  });
  const outcome = classifyEdenResponse<{
    grants: Array<{ kind: EdenContactConsentKind; version: number; id: string }>;
    receipt: string;
  }>(status, parsed);
  if (!outcome) throw extractError(status, parsed);
  return outcome;
}

export type EdenProfileFactKey = 'full_name' | 'preferred_name' | 'company_name' | 'company_size' | 'company_does' | 'role' | 'email' | 'phone' | 'notes';
export type EdenCapturedVia = 'voice' | 'typed';

/** POST .../profile-fact — one (key, value) fact. No update path server-side
 * (eden.session.record_profile_fact): a correction is a NEW row, so this
 * function is called again with the same key to correct a value, never a
 * PATCH — see EdenOnboarding.tsx's `correctFact`. */
export async function submitEdenProfileFact(
  token: string,
  args: { key: EdenProfileFactKey; value: string; capturedVia: EdenCapturedVia }
): Promise<EdenOutcome<{ receipt: string }>> {
  const { status, parsed } = await edenFetch(`/${token}/profile-fact`, {
    method: 'POST',
    body: args,
  });
  const outcome = classifyEdenResponse<{ receipt: string }>(status, parsed);
  if (!outcome) throw extractError(status, parsed);
  return outcome;
}

export type EdenObservationKind = 'direction' | 'fault';

/** POST .../observation — captures verbatim, never acted on here (V18b's
 * own "capture, don't configure"). The receipt names the ticket ref and
 * nothing more (INV-19B-7). */
export async function submitEdenObservation(
  token: string,
  args: { kind: EdenObservationKind; body: string; capturedVia: EdenCapturedVia }
): Promise<EdenOutcome<{ ticketRef: string; receipt: string }>> {
  const { status, parsed } = await edenFetch(`/${token}/observation`, {
    method: 'POST',
    body: args,
  });
  const outcome = classifyEdenResponse<{ ticketRef: string; receipt: string }>(status, parsed);
  if (!outcome) throw extractError(status, parsed);
  return outcome;
}

/** POST .../complete — the durable credential (INV-EDEN-3): mints a real
 * membership + sends a sign-in magic link to `email`. This is the ONLY
 * step that requires an email — nothing upstream of it does. */
export async function completeEdenOnboarding(
  token: string,
  args: { email: string }
): Promise<EdenOutcome<{ accountName: string; partyName: string; email: string; receipt: string }>> {
  const { status, parsed } = await edenFetch(`/${token}/complete`, { method: 'POST', body: args });
  const outcome = classifyEdenResponse<{ accountName: string; partyName: string; email: string; receipt: string }>(
    status,
    parsed
  );
  if (!outcome) throw extractError(status, parsed);
  return outcome;
}

/** POST /api/eden/realtime/session — mints the ephemeral OpenAI secret
 * (V18c, already live). INV-19B-8: the secret this returns must NEVER be
 * rendered on screen — EdenOnboarding.tsx holds it only in a ref passed to
 * the (V19b-3) realtime connection, never in visible state. Budget-exceeded
 * (409) is a named, calm outcome, not a crash — the typed door is still
 * how the client finishes (INV-19B-4). */
export type EdenRealtimeMintOutcome =
  | { status: 'invalid' }
  | { status: 'expired' }
  | { status: 'budget_exceeded'; receipt: string }
  | { status: 'open'; sessionId: string; ephemeralSecret: string; expiresAt: string; model: string };

export async function mintEdenRealtimeSession(token: string): Promise<EdenRealtimeMintOutcome> {
  const resp = await fetch('/api/eden/realtime/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ token }),
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (resp.status === 404) return { status: 'invalid' };
  if (resp.status === 410) return { status: 'expired' };
  if (resp.status === 409) {
    return { status: 'budget_exceeded', receipt: (parsed as { error?: { message?: string } })?.error?.message ?? 'This session has reached its budget.' };
  }
  if (!resp.ok) throw extractError(resp.status, parsed);
  const body = parsed as { sessionId: string; ephemeralSecret: string; expiresAt: string; model: string };
  return { status: 'open', ...body };
}

/** POST /api/eden/realtime/tool-calls — V19b-3's own relay target
 * (V18c's dispatcher, already live). The BROWSER never maps a tool name
 * to an action (INV-EDEN-5) — this function's whole job is to forward
 * exactly what the model asked for (`name`/`arguments` are UNTRUSTED,
 * caller-supplied, and never interpreted here) and hand back the
 * server's own honest verdict. Idempotent server-side on
 * (session, clientToolCallId) — a redelivery of the same id is safe. */
export type EdenRealtimeToolCallOutcome =
  | { status: 'invalid' }
  | { status: 'expired' }
  | { status: 'budget_exceeded'; receipt: string }
  | { status: 'executed'; receipt: string; replay: boolean }
  | { status: 'refused'; reason: string; replay: boolean };

export async function dispatchEdenRealtimeToolCall(
  token: string,
  call: { clientToolCallId: string; name: string; arguments: unknown }
): Promise<EdenRealtimeToolCallOutcome> {
  const resp = await fetch('/api/eden/realtime/tool-calls', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ token, ...call }),
  });
  const contentType = resp.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json') ? await resp.json().catch(() => null) : null;
  if (resp.status === 404) return { status: 'invalid' };
  if (resp.status === 410) return { status: 'expired' };
  if (resp.status === 409) {
    return {
      status: 'budget_exceeded',
      receipt: (parsed as { error?: { message?: string } })?.error?.message ?? 'This session has reached its budget.',
    };
  }
  if (resp.status === 422) {
    const body = parsed as { reason?: string; replay?: boolean };
    return { status: 'refused', reason: body?.reason ?? 'Refused.', replay: body?.replay ?? false };
  }
  if (!resp.ok) throw extractError(resp.status, parsed);
  const body = parsed as { receipt: string; replay: boolean };
  return { status: 'executed', receipt: body.receipt, replay: body.replay };
}

// ---------------------------------------------------------------------------
// C6b — the first ninety seconds, and the file the company keeps (wf05
// §02/§03): invite acceptance, the first-run walk, the self-service
// tickets door, member preferences, device sessions.
// Backend: packages/server/src/plugins/{auth,firstRun,session}.ts.
// ---------------------------------------------------------------------------

/** POST /auth/invites/accept — the landing App.tsx runs when a real invite
 * email's GET arrives (INVITE_ACCEPT_PATH, the magic-link landing's twin).
 * Returns the signed-in user AND the tenant the invite seats them in. */
export async function acceptInvite(token: string): Promise<{ user: ApiUser; tenantId: string }> {
  return request('/auth/invites/accept', { method: 'POST', body: { token } });
}

/** One lane row of the first-run chat_lanes step. `connected` tells the ONE
 * truth the record layer holds: the C5f binding exists (it is lane-agnostic
 * by design, so both rows flip together on the one bind). */
export interface FirstRunLane {
  key: string;
  label: string;
  deepLink: string | null;
  connected: boolean;
}

export interface FirstRunTicket {
  id: string;
  name: string;
  expires_on: string | null;
  file_ref: string | null;
  added_at: string;
  verified_by: string | null;
  verified_at: string | null;
}

export interface FirstRunState {
  steps: string[];
  stepsDone: string[];
  completedAt: string | null;
  /** The first scripted step not yet passed — where re-entry resumes. */
  resume: string | null;
  identity: {
    name: string | null;
    email: string | null;
    phoneMasked: string | null;
    /** The admin who enrolled the phone — "and Wendy set this up for you". */
    setUpByName: string | null;
  };
  lanes: FirstRunLane[];
  /** C7: the enrolment door, for the person no admin preceded. `available`
   * is the MANIFEST's answer to "may you enrol a phone" (never a role
   * name); `membershipId` is the caller's own — the only one this screen
   * ever posts at; `phoneMasked` is the enrolment already on file, so a
   * screen offering the field can tell "nobody has entered a number" from
   * "one is entered and waiting for the contact share". */
  selfEnrol: { available: boolean; membershipId: string; phoneMasked: string | null } | null;
  /** `canCreateProfile`: no binding exists AND the pack says this record
   * type is a person's own file — i.e. POST /api/me/record is open. */
  tickets: { available: boolean; canCreateProfile: boolean; items: FirstRunTicket[] } | null;
  firstCapture: { targetKey: string; label: string; textField: string | null } | null;
}

/** GET /api/first-run — 404s on a build whose pack declares no script (the
 * route does not exist there at all); callers treat that as "no first run". */
export async function getFirstRun(tenantId: string): Promise<FirstRunState> {
  return request('/api/first-run', { tenantId });
}

export async function completeFirstRunStep(
  tenantId: string,
  stepId: string,
  body?: { name?: string }
): Promise<{ stepsDone: string[]; completedAt: string | null; resume: string | null }> {
  return request(`/api/first-run/steps/${encodeURIComponent(stepId)}`, {
    method: 'POST',
    tenantId,
    body: body ?? {},
  });
}

/** POST /api/first-run/steps/:id/reopen — the door back into a step whose
 * answer belongs to a DEVICE or a MOMENT rather than to a person: add-to-home-
 * screen is answered on whatever phone is in your hand, and a licence expires.
 * Clears the walk's completion stamp so `resume` lands on that step again.
 * The caller's own membership only. */
export async function reopenFirstRunStep(
  tenantId: string,
  stepId: string
): Promise<{ stepsDone: string[]; completedAt: string | null; resume: string | null }> {
  return request(`/api/first-run/steps/${encodeURIComponent(stepId)}/reopen`, {
    method: 'POST',
    tenantId,
    body: {},
  });
}

/** GET /api/me/record — the caller's own bound party record (the company's
 * file on THEM); null until the office binds one. */
export async function getMyRecord(tenantId: string): Promise<RecordRow | null> {
  const result = await request<{ record: RecordRow | null }>('/api/me/record', { tenantId });
  return result.record;
}

/** POST /api/me/record — C7: the caller creates their OWN file, once (the
 * pack has to declare `tickets.selfProfile` for the route to exist at
 * all). The server writes the name; there is nothing to send. */
export async function createMyRecord(tenantId: string): Promise<RecordRow> {
  const result = await request<{ record: RecordRow }>('/api/me/record', { method: 'POST', tenantId });
  return result.record;
}

/** The enrolment two-step, for ANY membership in the tenant — the route
 * itself always accepted one (C6b's "Wendy typed his phone"); C7 wrote this
 * client for the self-enrol case, and S5d generalizes the NAME because the
 * roster door posts at other members' ids. Enrolment is externally visible,
 * so the gate PROPOSES: a 409 `authority: 'propose'` is not a refusal, it is
 * "say so and I will" — re-sent once with proposalApproved, the same
 * two-step the seeder and the workbook importer take. Any other 409 (a
 * convert — this actor may not enrol at all) is left to throw: approval is
 * not authority. */
export async function enrolMemberTelegram(
  tenantId: string,
  membershipId: string,
  phone: string
): Promise<ChatEnrolmentStatus> {
  const path = `/api/team/members/${encodeURIComponent(membershipId)}/telegram-enrolment`;
  try {
    const first = await request<{ enrolment: ChatEnrolmentStatus }>(path, {
      method: 'POST',
      tenantId,
      body: { phone },
    });
    return first.enrolment;
  } catch (err) {
    const offer = authorityOfferFrom(err);
    if (!offer || offer.authority !== 'propose') throw err;
    const approved = await request<{ enrolment: ChatEnrolmentStatus }>(path, {
      method: 'POST',
      tenantId,
      body: { phone, proposalApproved: true },
    });
    return approved.enrolment;
  }
}

/** POST /api/me/tickets — append ONE ticket to the caller's own bound
 * record. `fileRef` is a /records/blobs ref (uploadRecordBlob above). */
export async function addMyTicket(
  tenantId: string,
  input: { name: string; expiresOn?: string; fileRef?: string }
): Promise<{ ticket: FirstRunTicket; tickets: FirstRunTicket[] }> {
  return request('/api/me/tickets', { method: 'POST', tenantId, body: input });
}

/** POST /api/profiles/:recordId/tickets/:ticketId/verify — P-B's office
 * verify, the other half of "you add · office verifies". The server
 * derives and spends the pack's own profile-update action; a non-holder
 * gets the C5e 409 with its signed offer (authorityOfferFrom below), so
 * the caller files an ask instead of dying — never a dead button (S8). */
export async function verifyProfileTicket(
  tenantId: string,
  recordId: string,
  ticketId: string
): Promise<{ ticket: FirstRunTicket; tickets: FirstRunTicket[]; cascadeId: string }> {
  return request(
    `/api/profiles/${encodeURIComponent(recordId)}/tickets/${encodeURIComponent(ticketId)}/verify`,
    { method: 'POST', tenantId }
  );
}

// (The production Knot create door already has a client — knotIntentCreate
// further up; the first-run capture step drives that same function at the
// pack-declared target. A 409 from it is the authority gate's offer — read
// it off ApiError.body via authorityOfferFrom below.)

/** The C5e-shaped authority-conversion body a gated door's 409 carries. */
export interface AuthorityConvertOffer {
  authority: 'convert' | 'propose';
  routeTo?: string;
  missingCapability?: string;
  fileAsk?: { available: boolean; actionId: string | null; offerToken?: string };
}

export function authorityOfferFrom(err: unknown): AuthorityConvertOffer | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body;
  if (!body || typeof body !== 'object' || !('authority' in body)) return null;
  return body as AuthorityConvertOffer;
}

/** POST /api/asks {offerToken} — files the ask a conversion offered. */
export async function fileAskFromOffer(tenantId: string, offerToken: string): Promise<{ askId: string }> {
  return request('/api/asks', { method: 'POST', tenantId, body: { offerToken } });
}

// ---- the asks inbox (S11) -------------------------------------------------
//
// Until this block the app called exactly ONE of the five ask routes — the
// POST above. So every "your change converts into a request" affordance
// (S4's Request a change, S8's pay ask, S10's pay editor) filed into a room
// with no door: the request converted, and then disappeared for everyone,
// including the person who made it. These four are the read and the answer.
//
//   GET  /api/asks?view=&status=   -> {asks, view}
//   GET  /api/asks/:id             -> the ask
//   POST /api/asks/:id/approve     -> {kind:'approved', …} | {kind:'declined', …}
//   POST /api/asks/:id/decline     -> the closed ask, reason on the row
//
// Shapes mirrored from @vowric/core's AskView rather than imported, for the
// same reason CascadeWindow below is: importing core into the browser bundle
// would drag `pg` with it. Mirrored as far as a screen reads — the remainder
// (carry / intent / stoppedAt / priorCascadeId / origin) is the executor's
// business, not a person's.

/** The four statuses the server admits (asks.ts's ASK_STATUSES). An ask is
 * `open` until it is answered: `done` = approved and executed, `cancelled` =
 * declined (by a person, by an expiry sweep, or by a stale manifest — the
 * reason is on the row and says which). */
export type AskStatus = 'open' | 'done' | 'broken' | 'cancelled';

/** The two lenses. There is deliberately no "all asks in the tenant" view:
 * an ask is somebody's conversation, not a report (core's ListAsksArgs). */
export type AskListView = 'routed_to_me' | 'filed_by_me';

export interface Ask {
  id: string;
  status: AskStatus;
  title: string;
  packKey: string;
  manifestVersion: number;
  actionId: string;
  requesterMembershipId: string;
  /** The person who asked. A user id — resolve it to a name against
   * listTeamMembers when the caller may read the roster. */
  requesterUserId: string | null;
  /** The record the ask is ABOUT, when its first step names one. */
  targetId: string | null;
  routeTo: string;
  missingCapability: string;
  /** The cascade an approved ask executed into (null while open). */
  cascadeId: string | null;
  /** Requester-visible and first-class: a human's own words, or
   * 'expired' / 'stale_manifest'. This is what makes a decline reach the
   * person who asked instead of just closing the row. */
  decisionReason: string | null;
  /** A USER id (0036's own semantics — the person who answered). */
  decidedBy: string | null;
  dueAt: string | null;
  createdAt: string;
  /** G45: 'cascade' (a records-engine remainder — everything before this
   * chunk) or 'door' (a single call through the door registry — deliver a
   * document, send an agreement, un-happen a cascade). Missing on an ask
   * filed before G45 reads as 'cascade', which is exactly what it always
   * was — the server's own toAskView default. */
  kind?: 'cascade' | 'door';
  /** G45, kind 'door' only: the sentence the ask card shows — the door's
   * own describe(args), computed once at filing time (pure, no side
   * effects). Null for kind 'cascade'. */
  doorDescribe?: string | null;
}

/** GET /api/asks — the inbox. `routed_to_me` defaults to open on the SERVER
 * (a queue is unanswered work by definition); `filed_by_me` defaults to
 * every status, which is what lets a requester see an answer at all. */
export async function listAsks(
  tenantId: string,
  view: AskListView,
  status?: AskStatus
): Promise<Ask[]> {
  const qs = new URLSearchParams({ view });
  if (status) qs.set('status', status);
  const result = await request<{ asks: Ask[]; view: AskListView }>(`/api/asks?${qs.toString()}`, {
    tenantId,
  });
  return result.asks;
}

/** GET /api/asks/:id. A 404 here means "no such ask you may read" — the
 * server answers absence and refusal identically, on purpose (asks.ts's
 * header note 2), so a caller must not translate it into "it exists but
 * isn't yours". */
export async function getAsk(tenantId: string, id: string): Promise<Ask> {
  return request<Ask>(`/api/asks/${encodeURIComponent(id)}`, { tenantId });
}

/** What an approval answers with. `declined` is NOT a refusal of the
 * approver — it is the server auto-declining an ask whose manifest moved
 * under it, and the detail is in words the requester can read. */
export type ApproveAskResult =
  | { kind: 'approved'; askId: string; cascadeId: string | null; execution?: unknown }
  | { kind: 'declined'; askId: string; reason: string; detail: string };

/** POST /api/asks/:id/approve — approving spends the APPROVER's authority
 * and re-executes the carried remainder as them, so the change actually
 * lands. `proposalApproved` is the same second-tap contract every other
 * door has (it lets the approver past the externality gate's 'propose'; it
 * never manufactures authority — a 409 convert still throws). */
export async function approveAsk(
  tenantId: string,
  id: string,
  proposalApproved?: boolean
): Promise<ApproveAskResult> {
  return request<ApproveAskResult>(`/api/asks/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    tenantId,
    ...(proposalApproved === undefined ? {} : { body: { proposalApproved } }),
  });
}

/** POST /api/asks/:id/decline {reason} — the reason is required by the
 * server and required here: "no" with nothing after it is the CRM answer,
 * and the reason is the only thing the asker will ever see. */
export async function declineAsk(tenantId: string, id: string, reason: string): Promise<Ask> {
  return request<Ask>(`/api/asks/${encodeURIComponent(id)}/decline`, {
    method: 'POST',
    tenantId,
    body: { reason },
  });
}

// ---- the cascade's window, return and exchange (P-A) ---------------------
//
// The three doors packages/server/src/plugins/cascades.ts opened. The
// window is what a receipt reads to know whether — and how urgently — an
// undo is still on offer (map it with lib/cascadeWindow.ts's
// expiryPhaseFor); return and exchange are what the undo DOES.
//
// Their 409s are not ordinary errors and are not normalized into one:
// `{window:'dependent'|'correction_only'|'settled', …}` is the state of the
// window, carrying the path forward, and the authority gate's
// `{authority:'convert', …}` is a routing instruction. Both arrive as
// ApiError with the body attached — read them with cascadeConflictFrom and
// authorityOfferFrom respectively. (A return's convert offer always carries
// `fileAsk.available:false` — the reverse doors mint no offer, deliberately;
// see the plugin's header.)

/** A structural mirror of @vowric/core's CascadeWindow (packages/core/src/
 * cascade/window.ts) — the four arms and their fields copied verbatim; the
 * subtree typed as far as a receipt needs to read it. Mirrored rather than
 * imported for the same reason every other server shape in this file is:
 * pulling @vowric/core into the browser bundle would drag `pg` with it.
 *
 * It lives HERE, and not beside the mapper in lib/cascadeWindow.ts, because
 * this file is inside the portal module graph and that one names
 * '@vowric/ui' — see that module's header and ChassisBoundary.test.tsx. */
export type CascadeWindow =
  | { state: 'settled'; status: string; closedAt: string | null }
  | {
      state: 'correction_only';
      reason: 'escaped_effect' | 'ceiling_passed' | 'no_window';
      windowCeilingAt: string | null;
    }
  | { state: 'dependent'; subtree: CascadeSubtree; windowCeilingAt: string }
  | { state: 'open'; windowCeilingAt: string };

export interface CascadeSubtree {
  records: Array<{ id: string; recordType: string }>;
  cascades: string[];
  entanglements: unknown[];
}

/** The 409 body a blocked/closed window hands back. */
export type CascadeWindowConflict =
  | { window: 'dependent'; subtree: CascadeSubtree; message?: string }
  | { window: 'correction_only'; reason: string; message?: string }
  | { window: 'settled'; status: string; message?: string };

export function cascadeConflictFrom(err: unknown): CascadeWindowConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body;
  if (!body || typeof body !== 'object' || !('window' in body)) return null;
  return body as CascadeWindowConflict;
}

/** What a record's LAST move left reversible — the record-side entrance to
 * the same window the cascade routes compute. `undo: null` means no open
 * cascade moved this record at all; a non-null `undo` whose `window.state`
 * is anything but 'open' means one did and the door has since closed, which
 * is a different fact and renders differently (nothing, per the mapper). */
export interface RecordUndo {
  cascadeId: string;
  actionId: string;
  movedAt: string;
  priorState: string | null;
  window: CascadeWindow;
}

/** GET /records/:id/undo */
export async function getRecordUndo(tenantId: string, id: string): Promise<RecordUndo | null> {
  const result = await request<{ undo: RecordUndo | null }>(
    `/records/${encodeURIComponent(id)}/undo`,
    { tenantId }
  );
  return result.undo;
}

/** GET /api/cascades/:id/window — computed on read, never stored. */
export async function getCascadeWindow(tenantId: string, cascadeId: string): Promise<CascadeWindow> {
  const result = await request<{ window: CascadeWindow }>(
    `/api/cascades/${encodeURIComponent(cascadeId)}/window`,
    { tenantId }
  );
  return result.window;
}

export interface CascadeInverseApplied {
  ordinal: number;
  op: string;
  objectId: string;
}

export interface CascadeInverseSkipped {
  ordinal: number;
  reason: string;
}

export interface CascadeReturned {
  cascadeId: string;
  status: 'returned';
  closedAt: string;
  /** Which inverses ran and which did not — a reversal reports itself
   * step by step; an "undone" with nothing behind it is the
   * acknowledgement this model refuses. */
  applied: CascadeInverseApplied[];
  skipped: CascadeInverseSkipped[];
}

/** POST /api/cascades/:id/return — un-happen the whole cascade. */
export async function returnCascade(tenantId: string, cascadeId: string): Promise<CascadeReturned> {
  return request(`/api/cascades/${encodeURIComponent(cascadeId)}/return`, {
    method: 'POST',
    tenantId,
  });
}

export interface CascadeExchanged {
  kind: 'exchanged';
  oldCascadeId: string;
  reversal: { applied: CascadeInverseApplied[]; skipped: CascadeInverseSkipped[] };
  /** The replacement's own execution result — `kind:'executed'`, or
   * `'converted'` when a mid-replay step exceeded this actor's authority
   * (the executed prefix stands and the remainder is carried). Left as the
   * server's shape on purpose: flattening it would hide the half-landed
   * case, which is the one a person most needs to see. */
  execution: { kind: string; cascadeId?: string; createdIds?: string[] } & Record<string, unknown>;
}

/** POST /api/cascades/:id/exchange — return, then re-execute an amendment
 * as a NEW cascade citing the old. The client never names an action: the
 * replacement rides the original row's own action id, server-side. */
export async function exchangeCascade(
  tenantId: string,
  cascadeId: string,
  amendedIntent: unknown[],
  proposalApproved?: boolean
): Promise<CascadeExchanged> {
  return request(`/api/cascades/${encodeURIComponent(cascadeId)}/exchange`, {
    method: 'POST',
    tenantId,
    body: {
      amendedIntent,
      ...(proposalApproved === undefined ? {} : { proposalApproved }),
    },
  });
}

/** GET /api/team/members/:id/telegram-enrolment — the member's live
 * enrolment (member+; the settings screen reads the caller's OWN). */
export interface ChatEnrolmentStatus {
  id: string;
  membershipId: string;
  status: 'pending' | 'bound' | 'revoked';
  phoneMasked: string;
}

export async function getMemberEnrolment(
  tenantId: string,
  membershipId: string
): Promise<ChatEnrolmentStatus | null> {
  const result = await request<{ enrolment: ChatEnrolmentStatus | null }>(
    `/api/team/members/${encodeURIComponent(membershipId)}/telegram-enrolment`,
    { tenantId }
  );
  return result.enrolment;
}

/** GET /api/team/members/:id/parties — admin+, like the rest of the roster.
 * S5d: the join between a membership and the record that IS that person
 * (membership_parties), which is how a roster ROW (a tech profile record)
 * learns which membership its Telegram door posts at. */
export interface MembershipParty {
  id: string;
  partyRecordId: string;
  createdAt: string;
}

export async function listMemberParties(
  tenantId: string,
  membershipId: string
): Promise<MembershipParty[]> {
  const result = await request<{ parties: MembershipParty[] }>(
    `/api/team/members/${encodeURIComponent(membershipId)}/parties`,
    { tenantId }
  );
  return result.parties;
}

// ---- member preferences (wf05 §03 "Preferences — yours") ----------------

export interface MemberPreference {
  key: string;
  value: unknown;
  updatedAt: string;
}

export async function listMemberPreferences(tenantId: string): Promise<MemberPreference[]> {
  const result = await request<{ preferences: MemberPreference[] }>('/api/preferences', { tenantId });
  return result.preferences;
}

export async function setMemberPreference(
  tenantId: string,
  key: string,
  value: unknown
): Promise<MemberPreference> {
  const result = await request<{ preference: MemberPreference }>('/api/preferences', {
    method: 'PUT',
    tenantId,
    body: { key, value },
  });
  return result.preference;
}

// ---- device sessions (wf05 §03 "Connections — yours") --------------------

export interface DeviceSession {
  id: string;
  createdAt: string;
  lastSeenAt: string;
  userAgent: string | null;
  current: boolean;
}

export async function listDeviceSessions(): Promise<DeviceSession[]> {
  const result = await request<{ sessions: DeviceSession[] }>('/auth/sessions');
  return result.sessions;
}

export async function revokeDeviceSession(id: string): Promise<void> {
  await request(`/auth/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---- S5b: the comms lanes (the office's conversations) -------------------
//
// `packages/server/src/plugins/telegramComms.ts` (C5g) shipped these three
// routes complete, and until this chunk NOTHING IN THIS FILE CALLED ANY OF
// THEM — the fifth instance of "built and never wired" on the Core register
// and, per decision 817a0489, the one feature Rexx v1 adds over AppSheet
// parity. The routes exist only on a build whose pack declares a
// CommsPackConfig (Rexx does; Actor does not), so on any other vertical
// these calls 404 and the screens that make them are never mounted.
//
// THE VISIBILITY DECISION IS THE SERVER'S, ENTIRELY. `/api/comms/
// conversations` filters through core's ONE `laneVisibleTo` predicate and
// paginates AFTER — so a lane the caller may not read is not in the answer
// at all, and a client cannot know it exists. Nothing here re-derives that,
// and nothing here may: a locked "not your lane" row drawn client-side
// would be manufacturing the existence of a conversation out of nothing.
//
// PAGED, DELIBERATELY USED AS SUCH (Core register gap 11 — "a request
// budget is not a data budget"): both list calls take limit/offset and the
// screens ask for one page at a time rather than sweeping.

export type CommsLane = 'job' | 'office' | 'dispatch';

/** What core's `ChatSenderKind` puts on a row. 'system' is Rexx itself
 * posting what it did into the thread — it must never render as though a
 * person typed it. */
export type CommsSenderKind = 'tech' | 'office' | 'system';

export interface CommsLastMessage {
  body: string;
  senderKind: string;
  createdAt: string;
}

/**
 * CV6a §5 — WHAT IS NEW IN A THREAD, FOR THIS CALLER. Every conversation
 * entry carries it, on both shapes below.
 *
 * `unreadCountCapped` is the pairing that keeps the number honest: the
 * server stops counting at 99 (nobody acts differently on 400), but a
 * truncated number that LOOKS exact is a lie, so the cap is reported and
 * the UI renders "99+". `unreadCount` never includes the caller's own
 * messages — a thread you just spoke in is not a thread with something new.
 */
export interface CommsThreadUnread {
  unreadCount: number;
  unreadCountCapped: boolean;
  lastReadAt: string | null;
}

/** One job thread — one per claimed work order, keyed by the RECORD. */
export interface CommsJobConversation extends CommsThreadUnread {
  kind: 'job';
  recordId: string;
  recordName: string;
  topicStatus: 'pending' | 'created' | 'failed';
  lastMessage: CommsLastMessage | null;
  lastActivityAt: string;
}

/** One DM lane. The identity is (membership, LANE): the same person's
 * office and dispatch lanes are two conversations and never collapse.
 * `enrolmentStatus` is NULL on the MEMBER shape (CV1: a member's own two
 * lanes exist whether or not a Telegram enrolment does — the app door needs
 * no phone); the office response's field is non-null because its list IS
 * the enrolment roster. */
export interface CommsDmConversation extends CommsThreadUnread {
  kind: 'dm';
  lane: 'office' | 'dispatch';
  membershipId: string;
  userId: string;
  enrolmentStatus: 'pending' | 'bound' | null;
  lastMessage: CommsLastMessage | null;
  lastActivityAt: string;
}

export type CommsConversation = CommsJobConversation | CommsDmConversation;

export interface CommsMessage {
  id: string;
  lane: CommsLane;
  recordId: string | null;
  counterpartMembershipId: string | null;
  senderKind: CommsSenderKind;
  senderUserId: string | null;
  externalMessageId: string | null;
  body: string;
  /** A media REFERENCE ({mediaKind, fileId}); C5g stores the handle, the
   * bytes are a later chunk. Null for an ordinary text message. */
  metadata: { mediaKind?: string; fileId?: string; event?: string } | null;
  createdAt: string;
}

/** GET /api/comms/conversations — job threads and DM lanes MERGED and
 * already sorted by `lastActivityAt` (newest first) by the route. Render
 * the order it returns; do not re-sort. One page per call. */
export async function listCommsConversations(
  tenantId: string,
  page: { limit: number; offset: number }
): Promise<CommsConversation[]> {
  const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
  const result = await request<{ conversations: CommsConversation[] }>(
    `/api/comms/conversations?${q.toString()}`,
    { tenantId }
  );
  return result.conversations;
}

/** GET /api/comms/messages — paged, two modes (CV1's own contract):
 * `order:'asc'` (the default) is offset paging, oldest first;
 * `order:'desc'` is the phone thread's newest-first KEYSET mode — the first
 * page is the latest N, scrolling back passes `before` (the oldest loaded
 * message id — or an ISO timestamp — EXCLUSIVE) and an empty page means
 * history is exhausted. `offset` is ignored in desc mode. A 403 carries the
 * ONE refusal string every /api/comms route uses; callers put it through
 * `describeReadFailure`, never render it as an empty thread; a bad cursor
 * is a 422. */
export async function listCommsMessages(
  tenantId: string,
  args: {
    lane: CommsLane;
    recordId?: string;
    membershipId?: string;
    limit: number;
    offset?: number;
    order?: 'asc' | 'desc';
    before?: string;
  }
): Promise<CommsMessage[]> {
  const q = new URLSearchParams({ lane: args.lane, limit: String(args.limit) });
  if (args.offset !== undefined) q.set('offset', String(args.offset));
  if (args.order !== undefined) q.set('order', args.order);
  if (args.before !== undefined) q.set('before', args.before);
  if (args.recordId !== undefined) q.set('recordId', args.recordId);
  if (args.membershipId !== undefined) q.set('membershipId', args.membershipId);
  const result = await request<{ messages: CommsMessage[] }>(`/api/comms/messages?${q.toString()}`, {
    tenantId,
  });
  return result.messages;
}

/**
 * POST /api/comms/messages — SEND-THEN-STORE: Telegram delivers first and
 * only then is a row written, so a 502 means nothing was stored and nothing
 * arrived (no false receipt), and a 201 means it really went.
 *
 * The 422 is the most useful error on the route and callers must SHOW it:
 * it names the missing STEP ("member … has no bound telegram enrolment",
 * "work order … has no job thread yet"), which is actionable in a way
 * "could not send" never is.
 */
export async function sendCommsMessage(
  tenantId: string,
  args: { lane: CommsLane; recordId?: string; membershipId?: string; body: string }
): Promise<CommsMessage> {
  const body: Record<string, unknown> = { lane: args.lane, body: args.body };
  if (args.recordId !== undefined) body['recordId'] = args.recordId;
  if (args.membershipId !== undefined) body['membershipId'] = args.membershipId;
  const result = await request<{ message: CommsMessage }>('/api/comms/messages', {
    method: 'POST',
    tenantId,
    body,
  });
  return result.message;
}

// ---- CV2: chat grants — the office's "add them in" door, made visible ----
//
// CV1 shipped POST/DELETE /api/comms/grants (manager+, the laneVisibleTo
// job gate) with no reader: the office could open a lane and never see who
// it had opened one for. CV2 adds the ONE read route (GET, same gate) and
// these three clients for the job thread's People panel. A grant only ever
// ADDS to the derived set; revoking removes the grant, never an assignment.

export interface ChatGrant {
  id: string;
  recordId: string;
  membershipId: string;
  grantedBy: string | null;
  createdAt: string;
}

/** GET /api/comms/grants?recordId= — every standing grant on one work
 * order. Manager+ (the same people who may mint one); a 403 means the
 * People door is not this caller's, and the panel must not render as empty. */
export async function listChatGrants(tenantId: string, recordId: string): Promise<ChatGrant[]> {
  const q = new URLSearchParams({ recordId });
  const result = await request<{ grants: ChatGrant[] }>(`/api/comms/grants?${q.toString()}`, {
    tenantId,
  });
  return result.grants;
}

/** POST /api/comms/grants — idempotent (re-granting answers the standing
 * row). 201 either way. */
export async function addChatGrant(
  tenantId: string,
  args: { recordId: string; membershipId: string }
): Promise<ChatGrant> {
  const result = await request<{ grant: ChatGrant }>('/api/comms/grants', {
    method: 'POST',
    tenantId,
    body: args,
  });
  return result.grant;
}

/** DELETE /api/comms/grants — answers whether a row actually fell. */
export async function removeChatGrant(
  tenantId: string,
  args: { recordId: string; membershipId: string }
): Promise<boolean> {
  const result = await request<{ revoked: boolean }>('/api/comms/grants', {
    method: 'DELETE',
    tenantId,
    body: args,
  });
  return result.revoked;
}

// ---- CV6b: the app remembers — reads, and the phone's own channel --------
//
// CV6a built the whole server half of "the phone buzzes and the app
// remembers" and nothing in this file called any of it: no caller for
// `/api/comms/reads`, none for the push subscription routes, none for the
// notification preferences that have existed since P6.1. That is the same
// built-and-never-wired shape S5b closed for the comms lanes, and this
// closes it for the notification half.

export interface MarkThreadReadResult {
  lane: CommsLane;
  threadId: string;
  lastReadAt: string;
}

/**
 * POST /api/comms/reads — the caller's OWN read marker on one thread, at
 * now(). It runs through the SAME predicate as reading the thread (you
 * cannot mark read what you cannot read), so a 403 here means exactly what
 * a 403 on the messages read means.
 *
 * Called when a HUMAN OPENS A THREAD, never when a push arrives: a
 * notification is something that happened to someone, not something they
 * have seen.
 */
export async function markThreadRead(
  tenantId: string,
  args: { lane: CommsLane; recordId?: string; membershipId?: string }
): Promise<MarkThreadReadResult> {
  const body: Record<string, unknown> = { lane: args.lane };
  if (args.recordId !== undefined) body['recordId'] = args.recordId;
  if (args.membershipId !== undefined) body['membershipId'] = args.membershipId;
  const result = await request<{ read: MarkThreadReadResult }>('/api/comms/reads', {
    method: 'POST',
    tenantId,
    body,
  });
  return result.read;
}

/**
 * GET /api/push/vapid-key — the deployment's public VAPID key.
 *
 * NOT BAKED INTO THIS BUNDLE, deliberately (CV6a's own reasoning): staging
 * and production hold different keypairs, so a build-time key means the
 * bundle promoted between them mints subscriptions the private key can never
 * sign for — and the phone simply never buzzes, silently.
 *
 * Answers NULL when the deployment has no keys (the route's honest 503,
 * `push_not_configured`). Null is a STATE the UI renders, never an error it
 * swallows: an empty string here would become a corrupt subscription that
 * fails days later instead of a refusal now.
 */
export async function getVapidPublicKey(tenantId: string): Promise<string | null> {
  try {
    const result = await request<{ publicKey: string }>('/api/push/vapid-key', { tenantId });
    return result.publicKey;
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) return null;
    throw err;
  }
}

export interface PushSubscriptionRow {
  id: string;
  endpoint: string;
  createdAt?: string;
}

/** POST /api/push/subscriptions — the server upserts on endpoint, which is
 * what makes the boot-time re-registration in lib/pushEnrolment.ts safe to
 * run on every load. */
export async function registerPushSubscription(
  tenantId: string,
  args: { endpoint: string; keys: { p256dh: string; auth: string } }
): Promise<PushSubscriptionRow> {
  const result = await request<{ subscription: PushSubscriptionRow }>('/api/push/subscriptions', {
    method: 'POST',
    tenantId,
    body: args,
  });
  return result.subscription;
}

/** POST /api/push/subscriptions/revoke — this device stops being a
 * destination. The browser subscription is unsubscribed alongside it (see
 * lib/pushEnrolment.ts) so the two never disagree. */
export async function revokePushSubscription(tenantId: string, endpoint: string): Promise<void> {
  await request('/api/push/subscriptions/revoke', { method: 'POST', tenantId, body: { endpoint } });
}

/** The three modes the server actually stores. A two-state toggle cannot
 * represent them, which is why Settings renders a three-way chip rather
 * than silently rewriting somebody's 'digest' as 'immediate'. */
export type NotificationMode = 'immediate' | 'digest' | 'off';

export interface NotificationPreference {
  category: string;
  mode: NotificationMode;
  updatedAt: string | null;
}

/** GET /api/notification-preferences — ONLY the categories the user has
 * actually set. An absent row is not "off": the server's default is
 * 'immediate', and a UI that painted absence as off would tell someone they
 * had turned something off that is in fact on. */
export async function listNotificationPreferences(tenantId: string): Promise<NotificationPreference[]> {
  const result = await request<{ preferences: NotificationPreference[] }>('/api/notification-preferences', {
    tenantId,
  });
  return result.preferences;
}

/** PUT /api/notification-preferences/:category */
export async function setNotificationPreference(
  tenantId: string,
  category: string,
  mode: NotificationMode
): Promise<NotificationPreference> {
  const result = await request<{ preference: NotificationPreference }>(
    `/api/notification-preferences/${encodeURIComponent(category)}`,
    { method: 'PUT', tenantId, body: { mode } }
  );
  return result.preference;
}

/* ------------------------------------------------------------------ *
 * A3 — THE HOUSEHOLD ROUTES.
 *
 * Three calls, all served by packages/server/src/plugins/household.ts, whose
 * header carries the reasoning these types only summarise. The one thing
 * worth repeating on this side, because it is what a UI author would
 * otherwise get wrong:
 *
 *   A 403 FROM THE PERFORMERS READ IS AN ANSWER, NOT A FAILURE. The
 *   household section is adults-only — a child's own Today is the same
 *   screen with that section absent. Rendering an error there would be the
 *   app telling a nine-year-old she is not allowed to exist.
 * ------------------------------------------------------------------ */

export interface HouseholdPerformer {
  id: string;
  name: string;
  /** Completed years, derived by the server at read time. ABSENT when no
   * birth date is on file — never null, never 0, never NaN. The card's age
   * slot renders as honestly empty. */
  age?: number;
  isSelf: boolean;
  liveAuditions: number;
  currentBookings: number;
  /** Present only when something is genuinely live. */
  state?: 'in_production';
}

/** GET /api/household/performers — the household home's card row.
 *
 * Throws ApiError 403 for a child or a helper, which the caller renders as
 * an absent section rather than an error. See this block's header. */
export async function listHouseholdPerformers(tenantId: string): Promise<HouseholdPerformer[]> {
  const result = await request<{ performers: HouseholdPerformer[] }>('/api/household/performers', {
    tenantId,
  });
  return result.performers;
}

/** An attention item that knows whose it is. `subject` is absent when the
 * item is not about a performer (a household-level expense, a tenant-wide
 * notice) or when the caller may not read performers at all — in both cases
 * the item still renders, unnamed. */
export interface HouseholdAttentionItem extends AttentionItem {
  subject?: { id: string; name: string };
}

/** GET /api/household/attention — "Needs you", spanning every performer. */
export async function listHouseholdAttention(tenantId: string): Promise<HouseholdAttentionItem[]> {
  const result = await request<{ attentionItems: HouseholdAttentionItem[] }>(
    '/api/household/attention',
    { tenantId }
  );
  return result.attentionItems;
}

// ---------------------------------------------------------------------------
// B8 — the performer's public page: preview, address, publish, the link.
// Backend: packages/server/src/plugins/actorSubjectPage.ts (all built by F5/
// B6/P1/P3; this is the first client of it). Every route is session +
// membership + tier gated and takes the performer's `aa_subject` id.
// ---------------------------------------------------------------------------

/** B9 §3 — one item on the page: a headshot, a reel or a credit the artist
 * chose. Mirrors `@vowric/core`'s `SubjectPageItem` (actorSubjectPage.ts's
 * GET .../page and POST .../page/items both answer this shape). */
export interface SubjectPageItemApi {
  itemId: string;
  itemKind: 'headshot' | 'reel' | 'credit';
  recordId: string;
  position: number;
  includeInProgress: boolean;
}

/** P1 — mirrors `@vowric/core`'s own `SubjectPageHeroKind`. */
export type SubjectPageHeroKind = 'headshot' | 'reel';

/** B11 §3 — the theme and hero this page ACTUALLY renders with right now
 * (the artist's own choice, or the computed default when they have not
 * chosen — `themeChosen`/`heroKindChosen` say which), plus which hero kinds
 * there is anything to offer at all. Mirrors `@vowric/core`'s
 * `SubjectPageAppearance & { availableHeroKinds }` exactly
 * (`getSubjectPageAppearance`, `actorSubjectPage.ts`'s GET .../page and PUT
 * .../page/appearance both answer this shape). */
export interface SubjectPageAppearanceApi {
  theme: 'title_page' | 'contact_sheet' | 'index';
  heroKind: SubjectPageHeroKind | null;
  themeChosen: boolean;
  heroKindChosen: boolean;
  availableThemes: readonly string[];
  availableHeroKinds: SubjectPageHeroKind[];
}

export interface SubjectPageStateApi {
  page: {
    slug: string | null;
    url: string | null;
    live: boolean;
    publishedAt: string | null;
    unpublishedAt: string | null;
  } | null;
  /** B9 §3 — what is already ON the page, so the "On the page" strip can
   * show each of the account's photos ticked or not without a second read. */
  items: SubjectPageItemApi[];
  /** B11 §3 — the hero choice reads this rather than a second GET; see
   * `SubjectPageAppearanceApi`'s own header. */
  appearance: SubjectPageAppearanceApi;
}

/** GET /api/actor/subjects/:id/page — the page's own state. `indexing`/
 * `views` are in the response too (a later chunk's editor reads them);
 * `page`, `items` and `appearance` are typed here — B9's own additions to
 * what B8 typed, plus B11's own (`appearance`, for the hero choice). */
export async function getSubjectPageState(tenantId: string, subjectId: string): Promise<SubjectPageStateApi> {
  return request(`/api/actor/subjects/${subjectId}/page`, { tenantId });
}

/** POST /api/actor/subjects/:id/page/items — put one headshot/reel/credit on
 * the page (B9 §3, P3 §3's `includeInProgress` for a credit only). Idempotent
 * on (performer, record): ticking the same photo twice re-orders rather than
 * duplicating (actorSubjectPage.ts's own comment). */
export async function addSubjectPageItem(
  tenantId: string,
  subjectId: string,
  args: { itemKind: 'headshot' | 'reel' | 'credit'; recordId: string; position?: number; includeInProgress?: boolean }
): Promise<{ item: SubjectPageItemApi; receipt: string }> {
  return request(`/api/actor/subjects/${subjectId}/page/items`, {
    method: 'POST',
    tenantId,
    body: args,
  });
}

/** DELETE /api/actor/subjects/:id/page/items/:recordId — take one off the
 * page. The file itself is untouched; only the choice to show it. */
export async function removeSubjectPageItem(
  tenantId: string,
  subjectId: string,
  recordId: string
): Promise<{ removed: boolean; receipt: string }> {
  return request(`/api/actor/subjects/${subjectId}/page/items/${recordId}`, {
    method: 'DELETE',
    tenantId,
  });
}

/** B11 §3 — PUT /api/actor/subjects/:id/page/appearance: choose (or change)
 * the theme and/or the hero. SELECTION AND ORDERING ONLY (P1 §5) — this
 * never authors a word of copy. Each argument is independently optional and
 * omitting one leaves it untouched server-side (`setSubjectPageAppearance`,
 * `@vowric/core`'s own header) — so a caller that only wants to change the
 * hero passes `{ heroKind }` alone and the stored theme survives. Throws
 * `ApiError` with code `index_theme_cannot_take_reel_hero` if the combined
 * result would be the index theme with a reel hero (0109's CHECK, the
 * server's own sentence names the fix) — YourPage.tsx's own hero picker
 * sends BOTH fields together instead, so this refusal is not the normal
 * path for it. */
export async function setSubjectPageAppearance(
  tenantId: string,
  subjectId: string,
  args: { theme?: 'title_page' | 'contact_sheet' | 'index'; heroKind?: SubjectPageHeroKind }
): Promise<{ appearance: SubjectPageAppearanceApi; receipt: string }> {
  return request(`/api/actor/subjects/${subjectId}/page/appearance`, {
    method: 'PUT',
    tenantId,
    body: args,
  });
}

/** GET /api/actor/subjects/:id/page/slug-suggestion — B6 §2: the address,
 * offered before it is asked for. Read-only; reserves nothing. */
export async function getSubjectPageSlugSuggestion(tenantId: string, subjectId: string): Promise<string | null> {
  const result = await request<{ suggestion: string | null }>(
    `/api/actor/subjects/${subjectId}/page/slug-suggestion`,
    { tenantId }
  );
  return result.suggestion;
}

/** B10 §1 — one WRAPPED engagement offered for the tick, the credits group's
 * own candidate shape. Mirrors `@vowric/core`'s `SubjectPageCreditCandidate`
 * exactly: only these four keys travel. */
export interface SubjectPageCreditCandidateApi {
  recordId: string;
  production: string;
  role: string;
  year: number | null;
  /** Round 3a §6.6(b): false for a booked credit that has not wrapped yet (`reason: 'not_wrapped'`). */
  publishable?: boolean;
  reason?: string;
}

/** GET /api/actor/subjects/:id/page/credit-candidates — this performer's
 * wrapped credits, year descending, for the "Credits" group under the
 * headshot strip. In-progress credits and the adult opt-in are out of scope
 * (B10 §1) — every candidate here is already wrapped. */
export async function getSubjectPageCreditCandidates(
  tenantId: string,
  subjectId: string
): Promise<SubjectPageCreditCandidateApi[]> {
  const result = await request<{ candidates: SubjectPageCreditCandidateApi[] }>(
    `/api/actor/subjects/${subjectId}/page/credit-candidates`,
    { tenantId }
  );
  // ⚑ Round 3a §6.6(b): the route now also lists booked-but-unwrapped credits
  // (`publishable: false`) for the native app's "Shows once it wraps". This
  // screen has no such line, and a tick on one would show ✓ while the public
  // page keeps it off — so it keeps showing wrapped credits only, as before.
  return result.candidates.filter((c) => c.publishable !== false);
}

export interface SubjectPageConsentTextApi {
  /** `age.isMinor !== false` — true whenever this account is not a
   *  CONFIRMED adult (a real minor, or an age never established at all).
   *  See YourPage.tsx's own header for why that single boolean is what this
   *  screen gates the whole flow on. */
  isMinor: boolean;
  /** S1 (4691c16d) — the PERFORMER's age band, from their month and year of
   *  birth (or, on a self account, the holder's own evidence). `isMinor` is
   *  `band !== 'adult'`. */
  band: 'adult' | '16_17' | 'under_16' | 'unknown';
  /** S2 (a9ab3122) — `warning !== null`. */
  firstPublishWarning: boolean;
  /** S2 — the under-16 warning sheet: non-null exactly when the band is
   *  under_16 and the next publish is a first publish. */
  warning?: { title: string; paragraphs: string[] } | null;
  /** S2 — true exactly when the next publish is a first publish (a
   *  re-publish on a live consent needs no typed name). */
  requiresTypedName?: boolean;
  /** 0974 (353be3cc / f1ff9f55) — the operator marked this account private:
   *  publish and listing-on refuse 409 `publish_disabled`. Hide Publish. */
  publishDisabled?: boolean;
  /** 0974 — the server's own `publish_disabled` sentence. */
  publishDisabledMessage?: string;
  /** B12 — true only when NOBODY has ever answered the age question for
   *  this performer: no attestation of either value, and no real birth
   *  date. False once it has been answered even once (including "No,
   *  under 18") or a real DOB is on file. `isMinor` alone cannot make this
   *  distinction (`@vowric/core`'s `previewPublicPage` own header carries
   *  the full reasoning) — YourPage.tsx reads this instead so it offers
   *  the age question exactly once. */
  ageUnanswered: boolean;
  /** B12 — the server's own `age_unknown` sentence (`@vowric/core`'s
   *  `AGE_UNKNOWN_MESSAGE`, the same string `publishSubjectPage` throws),
   *  served rather than duplicated so YourPage.tsx's age-unresolved screen
   *  cannot drift from it. */
  ageUnknownMessage: string;
  /** B12 review — the settled-but-not-adult sentence (core's MINOR_PAGE_NOT_YET_MESSAGE). */
  minorMessage: string;
  version: string;
  paragraphs: string[];
  typePrompt: string;
  turnOff: { method: string; path: string };
}

/** GET /api/actor/subjects/:id/page/consent-text — the exact words the
 * confirm step must show, SERVED rather than held by the client, so the
 * words a person reads and the version recorded beside their typed
 * confirmation cannot drift apart (actorSubjectPage.ts's own header). */
export async function getSubjectPageConsentText(
  tenantId: string,
  subjectId: string
): Promise<SubjectPageConsentTextApi> {
  return request(`/api/actor/subjects/${subjectId}/page/consent-text`, { tenantId });
}

export interface SubjectPagePreviewApi {
  url: string;
  isMinor: boolean;
  /** The real, server-rendered page — the SAME renderer the live `/a/:slug`
   * route calls.
   *
   * ⚠ B9: THE SCREEN NO LONGER RENDERS THIS STRING. `YourPage.tsx` used to put
   * it in `<iframe srcdoc>`, which INHERITS the app shell's CSP and blocks the
   * page's own inline `<style>` (invisible to jsdom; a real browser showed
   * black serif on the dark card — B9's own brief). The screen now points an
   * iframe's `src` at `subjectPagePreviewUrl` below — a real navigation under
   * the page's OWN policy — and this route/type are left as-is for any other
   * caller; nothing in this codebase still reads `.html` off this response. */
  html: string;
}

/** GET /api/actor/subjects/:id/page/preview — the page a stranger would
 * see, before anyone has agreed to anything. Refuses `slug_required` until
 * an address is chosen (the preview's photos resolve through the real
 * `/a/<slug>/…` address). Superseded as `YourPage.tsx`'s OWN preview source
 * by `subjectPagePreviewUrl` (B9 §1/§2 — that route needs no address at
 * all); kept for any other caller. */
export async function getSubjectPagePreview(tenantId: string, subjectId: string): Promise<SubjectPagePreviewApi> {
  return request(`/api/actor/subjects/${subjectId}/page/preview`, { tenantId });
}

/** GET /api/actor/subjects/:id/page/preview.html — B9 §1: the REAL page,
 * same-origin, under the page's own CSP (`frame-ancestors 'self'`, the only
 * directive that differs from the live `/a/:slug` route). Point an iframe's
 * `src` at this directly — never `srcdoc` (a `srcdoc` document inherits the
 * PARENT page's CSP; see `SubjectPagePreviewApi.html`'s own comment for why
 * that broke the preview and jsdom could not see it).
 *
 * ⚑ TENANT SCOPE RIDES `?tenant=`, NOT A HEADER — a plain `<iframe src>`
 * navigation cannot send `X-Tenant-Id` (same reasoning as `mediaFileUrl`/
 * `fetchMediaFile` above; the server's own `adoptTenantFromQuery`,
 * actorSubjectPage.ts, reads exactly this query param). */
export function subjectPagePreviewUrl(tenantId: string, subjectId: string): string {
  return `/api/actor/subjects/${subjectId}/page/preview.html?tenant=${encodeURIComponent(tenantId)}`;
}

export interface SubjectPageSlugResultApi {
  slug: string;
  url: string;
  receipt: string;
}

/** PUT /api/actor/subjects/:id/page/slug — choose the address. Throws
 * `ApiError` with code `slug_taken` (candidates in `subjectPageCandidates`
 * below) or `slug_reserved`/`invalid_slug` for a shape error — every one of
 * them the server's own sentence, shown verbatim. */
export async function setSubjectPageSlug(
  tenantId: string,
  subjectId: string,
  slug: string
): Promise<SubjectPageSlugResultApi> {
  return request(`/api/actor/subjects/${subjectId}/page/slug`, {
    method: 'PUT',
    tenantId,
    body: { slug },
  });
}

/** P3 §1 — the collision candidates riding beside a `slug_taken` refusal
 * (`SubjectPageRefused.candidates`, actorSubjectPage.ts's `sendRefusal`).
 * A sibling of the `{error}` envelope, not inside it, so it is read off the
 * raw parsed body rather than off `ApiError.message`. */
export function subjectPageCandidates(err: unknown): string[] {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return [];
  const candidates = (err.body as { candidates?: unknown }).candidates;
  return Array.isArray(candidates) ? candidates.filter((c): c is string => typeof c === 'string') : [];
}

export interface SubjectPagePublishResultApi {
  url: string;
  live: boolean;
  isMinor: boolean;
  /** S1 — the performer's band when the page went live. */
  band: 'adult' | '16_17' | 'under_16';
  /** S1 — always 0: the holder decides, and there is no second guardian to tell. */
  notifiedGuardianCount: number;
  turnOff: { method: string; path: string };
  receipt: string;
}

/** POST /api/actor/subjects/:id/page — PUBLISH. The typed confirmation and
 * the consent text VERSION the confirm step actually showed; both required,
 * both the server's own record of what was agreed to. */
export async function publishSubjectPage(
  tenantId: string,
  subjectId: string,
  args: { typedConfirmation: string; consentTextVersion: string }
): Promise<SubjectPagePublishResultApi> {
  return request(`/api/actor/subjects/${subjectId}/page`, {
    method: 'POST',
    tenantId,
    body: args,
  });
}

export interface SubjectPageUnpublishResultApi {
  live: boolean;
  receipt: string;
}

/** DELETE /api/actor/subjects/:id/page — unpublish. Immediate, idempotent,
 * destroys nothing: everything chosen for the page stays chosen. */
export async function unpublishSubjectPage(
  tenantId: string,
  subjectId: string
): Promise<SubjectPageUnpublishResultApi> {
  return request(`/api/actor/subjects/${subjectId}/page`, { method: 'DELETE', tenantId });
}

// ⚑ 2026-09-19 — `listSubjectRecords` (GET /api/household/subjects/:id/records,
// "the drill-in read") IS DELETED FROM THE CLIENT. It existed to let
// lib/subjectScope.ts narrow a list to one performer inside a household of
// several; an account is one career now (Huan's ruling), so there is no
// second performer to narrow into and no caller left. The server route
// itself is untouched — removing it, if it should be, is not this chunk's
// call (packages/server is not packages/web).

/* ------------------------------------------------------------------
 * B16a — the cockpit's first wires (the personal-pack surface).
 *
 * These are the FIRST web consumers of Lane B's doors: GET /api/today
 * (B4c/B7) and GET /api/threads (B1). Shapes mirror the serializers in
 * packages/server/src/plugins/today.ts and threads.ts — the server is the
 * authority; nothing here re-derives a state or a window.
 * ------------------------------------------------------------------ */

export interface TodayThreadRow {
  threadId: string;
  subject: string | null;
  state: string;
  channel: string | null;
  lastMessageAt: string | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  /** Both numbers ship together so a surfaced wait can explain itself —
   * "quiet 30h, limit 20h" is actionable where a bare flag is not. */
  hoursSinceLastMessage: number | null;
  quietAfterHours: number | null;
  party: { id: string; displayName: string | null } | null;
  productId: string | null;
}

export interface TodayCommitmentRow {
  id: string;
  title: string;
  direction: string;
  counterpartyName: string | null;
  dueAt: string | null;
  overdue: boolean;
}

// Chunk TODAY-S/TODAY-W — the four groups `/api/today` gains on top of the
// four thread groups above. `TodayJudgmentProposal` is deliberately the
// same shape `listProposals`'s `Proposal` (above) already carries — Review
// and Today read the SAME row, so `proposalKindLabel`/`proposalSummaryLine`
// (lib/proposals.ts) work on either without a second vocabulary.
export interface TodayJudgmentProposal {
  id: string;
  kind: string;
  targetType: string;
  targetId: string | null;
  proposed: Record<string, unknown>;
  rationale: string | null;
  createdAt: string;
}

export interface TodayJudgmentRun {
  id: string;
  programmeId: string;
  programmeTitle: string;
  stepKey: string;
  title: string;
  state: string;
  dueAt: string;
  owner: string;
  gate: string;
  why: string;
  link: string;
}

export interface TodayProceedingProgramme {
  id: string;
  title: string;
  dayN: number | null;
  dayTotal: number | null;
  nextRun: { title: string; dueAt: string } | null;
  link: string;
}

/** Mirrors core's `SystemFact` (packages/core/src/systems/types.ts) — the
 * Systems tab's own vocabulary, not yet built on the web (SYS-W), so this
 * is Today's own copy rather than an import from a screen that doesn't
 * exist yet. */
export interface TodaySystemFact {
  section: string;
  key: string;
  label: string;
  state: string;
  detail: string | null;
  valueNum: number | null;
  capNum: number | null;
  unit: string | null;
  dueOn: string | null;
  link: string | null;
  asOf: string;
  reportedBy: string;
  stale: boolean;
  staleSince: string | null;
  meter: 'ok' | 'near' | 'at_cap' | null;
}

export interface TodayReceipt {
  at: string;
  text: string;
  link: string | null;
}

export interface TodayBriefing {
  arrived: TodayThreadRow[];
  waitingOnMe: { threads: TodayThreadRow[]; commitments: TodayCommitmentRow[] };
  waitingOnThem: { threads: TodayThreadRow[]; commitments: TodayCommitmentRow[] };
  goneQuiet: TodayThreadRow[];
  kept: {
    repliedThreads: TodayThreadRow[];
    commitments: Array<{ id: string; title: string; direction: string; closedAt: string | null }>;
  };
  collisions: Array<{
    kind: string;
    value: string;
    previousHolderName: string | null;
    currentHolderName: string | null;
    releasedAt: string | null;
  }>;
  window: { hours: number; since: string };
  judgment: {
    proposals: TodayJudgmentProposal[];
    proposalsTotal: number;
    runs: TodayJudgmentRun[];
  };
  proceeding: {
    programmes: TodayProceedingProgramme[];
    agents: TodaySystemFact[];
  };
  receipts: TodayReceipt[];
  meters: {
    facts: TodaySystemFact[];
    hoursThisWeek: number | null;
    weekHours: number;
  };
  /** G1 — the viewer's own Google accounts that need reconnecting. Optional:
   * a server before G1 omits it, and absent reads as none. */
  reconnect?: Array<{ id: string; email: string; reason: string | null }>;
}

/** GET /api/today — one route, one call, one clock (the four receipt groups
 * are computed at a single instant; fetching them separately is exactly what
 * the server refuses to let a client do). */
export async function getToday(tenantId: string): Promise<TodayBriefing> {
  return request<TodayBriefing>('/api/today', { tenantId });
}

/* ------------------------------------------------------------------
 * H1a (COCKPIT-HOME-COLUMNS-SPEC.md §H1) — the home board's column
 * registry. Mirrors core's own CockpitColumn (packages/core/src/cockpit/
 * columns.ts) — the server is the authority; this file never re-derives
 * the default set.
 * ------------------------------------------------------------------ */

export type CockpitColumnKind = 'personal' | 'project';

export type CockpitTopCardSource =
  | { source: 'today'; group: 'arrived' | 'waitingOnMe' | 'judgment' | 'proceeding' | 'meters' | 'reconnect' }
  | { source: 'systems'; section: string; key?: string }
  | { source: 'audience' }
  | { source: 'calendar' }
  | { source: 'radar' }
  | { source: 'record' };

export type CockpitProjectOutput =
  | { kind: 'frame'; url: string }
  | { kind: 'record'; query: string }
  | { kind: 'shots'; pythiaPrefix: string }
  | { kind: 'link'; url: string; label: string };

export interface CockpitColumn {
  id: string;
  kind: CockpitColumnKind;
  title: string;
  screen: string;
  topCard: CockpitTopCardSource;
  // H3a — a LIST of parts (core's own `CockpitColumn.output`'s header has
  // the full reasoning): a project screen can carry a scoped Record
  // window, a frame, and one or more link buttons at once.
  output?: CockpitProjectOutput[];
  builder?: string | null;
  hidden: boolean;
  pinnedOrder: number | null;
}

// H3a (COCKPIT-HOME-COLUMNS-SPEC.md §H3) — the only five project slugs
// that exist this chunk (H4a adds the fifth, 'studio' — Studio Assist,
// coordinator scope addition 2026-10-16, Huan's yes), mirroring core's own
// `PROJECT_SLUGS` (packages/core/src/cockpit/columns.ts) exactly.
export const PROJECT_SLUGS = ['rexx', 'stays', 'voicestrip', 'origincare', 'studio'] as const;
export type ProjectSlug = (typeof PROJECT_SLUGS)[number];
export function isProjectSlug(v: string): v is ProjectSlug {
  return (PROJECT_SLUGS as readonly string[]).includes(v);
}

/** GET /api/cockpit/columns — no rows for the tenant reads the default
 * personal set; rows, when present, override. */
export async function getCockpitColumns(tenantId: string): Promise<{ columns: CockpitColumn[] }> {
  return request('/api/cockpit/columns', { tenantId });
}

/* ------------------------------------------------------------------
 * H4a (COCKPIT-HOME-COLUMNS-SPEC.md §H4) — the builder pane's
 * OUTBOUND-ONLY journal, relayed into a project screen's right-hand pane.
 * Mirrors core's own BuilderFeedItemView (packages/core/src/cockpit/
 * builderFeed.ts).
 * ------------------------------------------------------------------ */

export type BuilderFeedKind = 'status' | 'action' | 'task_opened' | 'task_closed';

export interface BuilderFeedItemApi {
  id: string;
  project: string;
  kind: BuilderFeedKind;
  text: string;
  at: string;
  createdAt: string;
}

/** GET /api/cockpit/builder/:project/items — newest first; `before` (an
 * item id) pages further back. */
export async function getBuilderFeedItems(
  tenantId: string,
  project: string,
  opts: { before?: string | null; limit?: number } = {}
): Promise<{ items: BuilderFeedItemApi[] }> {
  const params = new URLSearchParams();
  if (opts.before) params.set('before', opts.before);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return request(`/api/cockpit/builder/${encodeURIComponent(project)}/items${qs ? `?${qs}` : ''}`, { tenantId });
}

/* ------------------------------------------------------------------
 * H1b (COCKPIT-HOME-COLUMNS-SPEC.md §H1, "Receipts and Undo") — the one
 * lever the board reads client-side: `cockpit.board.done_ttl_minutes`.
 * Mirrors core's LeverRow (packages/core/src/commitments/levers.ts); this
 * file never re-derives a default — a read that fails or omits the key
 * falls back to the SAME default the catalogue declares (5), named at the
 * one call site rather than re-declared here.
 * ------------------------------------------------------------------ */
export interface LeverRow {
  key: string;
  category: string;
  description: string;
  valueType: 'integer' | 'number' | 'boolean' | 'string' | 'string-list';
  currentValue: unknown;
  defaultValue: unknown;
  scope: 'platform' | 'tenant';
}

/** GET /api/levers — every known lever, tenant-merged with its code
 * default (viewer+). */
export async function getLevers(tenantId: string): Promise<{ levers: LeverRow[] }> {
  return request('/api/levers', { tenantId });
}

/** B17 §1 — PATCH /api/commitments/:id/status. `transitionCommitmentStatus`'s
 * own table (core/commitments/commitments.ts) makes 'done'/'broken'/
 * 'cancelled' TERMINAL — there is no transition back to 'open', including a
 * same-status no-op — so this is the one call Today's "Done" act ever makes;
 * there is no companion "Undo" door to call. */
export async function transitionCommitmentStatus(
  tenantId: string,
  id: string,
  status: 'done' | 'broken' | 'cancelled'
): Promise<{ commitment: { id: string; status: string } }> {
  return request<{ commitment: { id: string; status: string } }>(`/api/commitments/${id}/status`, {
    method: 'PATCH',
    tenantId,
    body: { status },
  });
}

export interface ThreadRowApi {
  id: string;
  subject: string | null;
  state: string;
  lastMessageAt: string | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /api/threads — the mail spine's list. `state` is the server's own
 * vocabulary (open/waiting_me/waiting_them/quiet/closed); this client holds
 * no copy of it. */
export async function listThreads(
  tenantId: string,
  opts: { state?: string; limit?: number; offset?: number } = {}
): Promise<ThreadRowApi[]> {
  const params = new URLSearchParams();
  if (opts.state) params.set('state', opts.state);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const result = await request<{ threads: ThreadRowApi[] }>(`/api/threads${qs ? `?${qs}` : ''}`, { tenantId });
  return result.threads;
}

/** G4: the connected Google account a message came through — the Mail
 * screen's own account chip/dot, and what locks the reply sheet's From. */
export interface GmailAccountRefApi {
  connectionId: string;
  email: string;
  status: 'active' | 'needs_reconnect' | 'revoked';
}

export interface ThreadMessageApi {
  id: string;
  threadId: string;
  doorId: string;
  direction: 'inbound' | 'outbound';
  senderIdentity: string | null;
  senderMembershipId: string | null;
  body: string;
  bodyFormat: string;
  externalId: string | null;
  externalThreadRef: string | null;
  occurredAt: string;
  createdAt: string;
  /** G4 (S1's mail surface) — null on a thread whose door is not a
   * connected Google account (e.g. Telegram). */
  account: GmailAccountRefApi | null;
  labels: string[] | null;
  /** Named reasons, never a bare flag — same "say the thing" rule the
   * voice canon holds everywhere else. Empty when not flagged. */
  phishing: string[];
  /** MAIL-POLISH BRIEF round 2: the message's stored attachments (metadata only) — a Forward shows
   * them as chips and carries the ones left on. Absent on a non-Gmail thread. */
  attachments?: Array<{ fileId: string; name: string; mimeType: string; size: number }>;
}

/** G4 — the open recorded draft for a thread, if any. Metadata only — the
 * body text is a separate read, GET /api/mail/drafts/:id (getMailDraft,
 * below): this row only carries enough to know a draft exists and to ask
 * for it by id. */
export interface GmailThreadDraftApi {
  id: string;
  connectionId: string;
  bodyHash: string;
  origin: 'atlas' | 'person';
}

export interface GmailThreadContextApi {
  /** The account a reply goes out from — the reply sheet's locked From.
   * null on a thread with no Gmail door (or no inbound message yet). */
  replyFrom: GmailAccountRefApi | null;
  draft: GmailThreadDraftApi | null;
}

/** GET /api/threads/:id — one thread with its messages in occurrence order.
 * The unit is the thread, not the message (there is deliberately no
 * GET /api/messages — see the server plugin's own header). `gmail` rides
 * along for every thread (G4) — null fields on a non-Gmail thread. */
export async function getThread(
  tenantId: string,
  id: string
): Promise<{ thread: ThreadRowApi; messages: ThreadMessageApi[]; gmail: GmailThreadContextApi; whoIsThis?: WhoIsThisApi | null }> {
  return request<{ thread: ThreadRowApi; messages: ThreadMessageApi[]; gmail: GmailThreadContextApi; whoIsThis?: WhoIsThisApi | null }>(
    `/api/threads/${id}`,
    { tenantId }
  );
}

/** G6 C1 (0975): a door's unproven claim about who sent a thread — a caller's number — and the
 * customer who holds it now. `suggestion` is only ever shown; nothing is filed until a person
 * answers (POST /api/threads/:id/who). */
export interface WhoIsThisApi {
  kind: string;
  value: string;
  suggestion: { partyId: string; name: string } | null;
  confirmed: { partyId: string; name: string; confirmedAt: string } | null;
}

export interface ConfirmWhoIsThisApi {
  confirmationId: string;
  party: { partyId: string; name: string };
  movedFrom: { partyId: string; name: string } | null;
  whoIsThis: WhoIsThisApi | null;
}

/** Refusals: 422 invalid, 409 nothing_to_confirm, 404 no_such_thread / no_such_party — carried as
 * ApiError with the server's own sentence. */
export async function confirmWhoIsThis(
  tenantId: string,
  threadId: string,
  answer: { partyId: string } | { newParty: { name: string } }
): Promise<ConfirmWhoIsThisApi> {
  return request<ConfirmWhoIsThisApi>(`/api/threads/${threadId}/who`, { method: 'POST', tenantId, body: answer });
}

/** GET /api/mail/drafts/:id — core/mail-draft-read (atlas-core, 2026-10-15):
 * the recorded draft's LIVE content, read from Gmail. `:id` is
 * `gmail.draft.id` off GET /api/threads/:id. `changedInGmail` is true when
 * the draft was edited on the Gmail side since it was recorded — shown as
 * a line beside the composer, never blocking it: Send always sends what
 * the sheet shows. Refusals: 409 draft_changed (the draft is gone from
 * Gmail — "This draft was changed in Gmail — reload it"), 409
 * reconnect_required, 403 mail_turned_off, 503 google_not_configured, 404
 * not_found — all carried as ApiError by the shared `request` helper. */
export interface MailDraftApi {
  id: string;
  threadId: string | null;
  connectionId: string;
  origin: 'atlas' | 'person';
  from: string | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  attachments: Array<{ name: string; size: number; mimeType: string }>;
  changedInGmail: boolean;
}

export async function getMailDraft(tenantId: string, draftId: string): Promise<{ draft: MailDraftApi }> {
  return request<{ draft: MailDraftApi }>(`/api/mail/drafts/${draftId}`, { tenantId });
}

/* ------------------------------------------------------------------ *
 * G4 / S1 — the Mail screen's own wires (core google/gmailMailbox.ts;
 * packages/server/src/plugins/gmailMailbox.ts). Shapes pinned to that
 * plugin's actual responses, not guessed.
 * ------------------------------------------------------------------ */

export interface MailLabelApi {
  id: string;
  name: string;
  type: string;
  total: number | null;
  unread: number | null;
}

export interface MailAccountApi {
  connectionId: string;
  email: string;
  status: 'active' | 'needs_reconnect' | 'revoked';
  labels: MailLabelApi[];
}

/** GET /api/mail/accounts — every mail-enabled Google account, with its
 * synced label list and counts (the accounts strip's own data). */
export async function getMailAccounts(tenantId: string): Promise<MailAccountApi[]> {
  const result = await request<{ accounts: MailAccountApi[] }>('/api/mail/accounts', { tenantId });
  return result.accounts;
}

export interface MailThreadRowApi {
  threadId: string;
  subject: string | null;
  lastMessageAt: string;
  account: { connectionId: string; email: string };
  from: string | null;
  snippet: string;
  labels: string[];
  messageCount: number;
  unread: boolean;
  hasDraft: boolean;
  phishing: boolean;
}

/** GET /api/mail/threads — conversations through Gmail doors, newest
 * first. No `account` = All inboxes; `label` defaults to INBOX. */
export async function getMailThreads(
  tenantId: string,
  opts: { account?: string; label?: string; before?: string; limit?: number } = {}
): Promise<{ threads: MailThreadRowApi[]; nextBefore: string | null }> {
  const params = new URLSearchParams();
  if (opts.account) params.set('account', opts.account);
  if (opts.label) params.set('label', opts.label);
  if (opts.before) params.set('before', opts.before);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return request<{ threads: MailThreadRowApi[]; nextBefore: string | null }>(
    `/api/mail/threads${qs ? `?${qs}` : ''}`,
    { tenantId }
  );
}

/** POST /api/mail/threads/:id/labels — Archive = {remove:['INBOX']}, mark
 * read = {remove:['UNREAD']}. The transport refuses TRASH/SPAM before the
 * wire (server-side guard); this client never offers them. */
export async function setMailThreadLabels(
  tenantId: string,
  threadId: string,
  change: { add?: string[]; remove?: string[] }
): Promise<{ labels: string[] }> {
  return request<{ labels: string[] }>(`/api/mail/threads/${threadId}/labels`, {
    method: 'POST',
    tenantId,
    body: change,
  });
}

/** MAIL-POLISH BRIEF (2026-10-16, Huan's own words: "add delete") — Delete.
 * POST /api/mail/threads/:id/trash moves the conversation to Gmail's Trash;
 * untrashThread (Undo) restores it. Both HIS tap only — never offered to
 * the assistant, which has no door to either. */
export async function trashThread(tenantId: string, threadId: string): Promise<{ labels: string[] }> {
  return request<{ labels: string[] }>(`/api/mail/threads/${threadId}/trash`, { method: 'POST', tenantId, body: {} });
}

export async function untrashThread(tenantId: string, threadId: string): Promise<{ labels: string[] }> {
  return request<{ labels: string[] }>(`/api/mail/threads/${threadId}/untrash`, { method: 'POST', tenantId, body: {} });
}

/** MAIL-POLISH BRIEF addition (Huan, final, 2026-10-16) — Compose: the
 * cockpit "+"'s own door on Mail. `id` is OUR gmail_drafts row id (never
 * Gmail's own draft id), handed back by the first save and passed on every
 * one after so editing never orphans a second draft in Gmail. */
export interface MailComposeDraftApi {
  draftId: string;
  shownHash: string;
  from: string;
  to: string[];
  cc: string[];
  subject: string;
  body: string;
}

export async function saveMailCompose(
  tenantId: string,
  fields: { connectionId: string; to: string[]; cc?: string[]; subject: string; body: string; draftId?: string; attachmentFileIds?: string[]; forwardOfThreadId?: string }
): Promise<{ draft: MailComposeDraftApi }> {
  return request<{ draft: MailComposeDraftApi }>('/api/mail/compose', { method: 'POST', tenantId, body: fields });
}

/** POST /api/mail/compose/:id/send — his tap, same cap/refusals as a
 * reply's own Send. `shownHash` is the hash the composer had on screen
 * (the LAST saveMailCompose response) — the same stale-draft guard a
 * reply's recorded draft gets. */
export async function sendMailCompose(tenantId: string, draftId: string, shownHash: string): Promise<{ sent: { gmailMessageId: string } }> {
  return request(`/api/mail/compose/${draftId}/send`, { method: 'POST', tenantId, body: { shownHash } });
}

/* ------------------------------------------------------------------ *
 * B16c/d — the spread's wires and the pen's decisions. Shapes pinned to
 * the server serializers (threads.ts / attribution.ts / emailDrafts.ts /
 * proposals.ts), verified against the source, never guessed.
 * ------------------------------------------------------------------ */

export interface ThreadSummaryApi {
  text: string;
  messageIds: string[];
  truncated: boolean;
  model: string;
  promptVersion: number;
}

export type ThreadSummaryStatus = 'composing' | 'ready' | 'failed' | 'not_needed';

/** GET /api/threads/:id/summary — B15's async door. Every status is 200;
 * 'composing' means poll again (B13b's contract). */
export async function getThreadSummary(
  tenantId: string,
  id: string
): Promise<{ status: ThreadSummaryStatus; summary: ThreadSummaryApi | null }> {
  return request(`/api/threads/${id}/summary`, { tenantId });
}

export interface ThreadAttributionApi {
  id: string;
  threadId: string;
  productId: string | null;
  engagementId: string | null;
  source: string;
  confidence: number | null;
  rationale: string | null;
  createdAt?: string;
  supersededAt?: string | null;
}

/** GET /api/threads/:id/attribution — the current filing + its history
 * (the history rides along: "why is this filed here?" is asked at the same
 * moment as "where"). */
export async function getThreadAttribution(
  tenantId: string,
  id: string
): Promise<{ attribution: ThreadAttributionApi | null; history: ThreadAttributionApi[] }> {
  return request(`/api/threads/${id}/attribution`, { tenantId });
}

/** PUT /api/threads/:id/attribution — the HUMAN door (the server hardcodes
 * source='human'; provenance a caller could claim would be worthless). */
export async function putThreadAttribution(
  tenantId: string,
  id: string,
  productId: string | null
): Promise<{ applied: boolean; reason: string | null; attribution: ThreadAttributionApi }> {
  return request(`/api/threads/${id}/attribution`, {
    method: 'PUT', tenantId, body: { productId },
  });
}

export interface ProductApi {
  id: string;
  key: string;
  name: string;
  status: string;
}

export async function listProducts(tenantId: string): Promise<ProductApi[]> {
  const result = await request<{ products: ProductApi[] }>('/api/products', { tenantId });
  return result.products;
}

export interface EmailDraftApi {
  threadId: string;
  to: Array<{ email: string; name?: string | null }>;
  subject: string;
  body: string;
  rationale: string;
}

/** POST /api/threads/:id/draft-reply — B14: the model writes a BODY, staged
 * as a pending proposal. NOTHING sends until the approve door. */
export async function draftThreadReply(
  tenantId: string,
  threadId: string,
  instructions?: string
): Promise<{ proposalId: string; draft: EmailDraftApi; supersededProposalId: string | null }> {
  return request(`/api/threads/${threadId}/draft-reply`, {
    method: 'POST', tenantId, body: instructions ? { instructions } : {},
  });
}

export interface SentReplyApi {
  threadId: string;
  messageId: string;
  appendedMessageId: string;
}

/** POST /api/threads/:id/reply — B16c: the CODED reply. A typed reply is
 * the approval; no model, no proposal row. */
export async function sendThreadReply(
  tenantId: string,
  threadId: string,
  body: string,
  subject?: string
): Promise<{ sent: SentReplyApi & { to: string; subject: string; deduped: boolean } }> {
  return request(`/api/threads/${threadId}/reply`, {
    method: 'POST', tenantId, body: subject ? { body, subject } : { body },
  });
}

/** POST /api/proposals/:id/approve for an email_draft — B14: approving IS
 * sending; the optional overrides are the human's edits, and the model's
 * original stays on the proposal row. */
export async function approveEmailDraft(
  tenantId: string,
  proposalId: string,
  overrides?: { subject?: string; body?: string }
): Promise<{ proposalId: string; sent: SentReplyApi & { edited: boolean } }> {
  return request(`/api/proposals/${proposalId}/approve`, {
    method: 'POST', tenantId, body: overrides ?? {},
  });
}

/* ------------------------------------------------------------------ *
 * B18a — the funnel's triage card (serializeProposal carries `proposed`;
 * packages/core/src/intake/triage.ts is that payload's authority).
 * ------------------------------------------------------------------ */

export interface IntakeTriageProposedApi {
  submissionId: string;
  partyId: string | null;
  email: string | null;
  businessName: string | null;
  fork: 'invite' | 'needs_more';
  forkReason: string;
  confidence: 'thin' | 'workable' | 'rich';
  summaryMd: string;
  draftReplyMd: string;
}

export interface TriageDecisionApi {
  proposalId: string;
  submissionId: string;
  fork: 'invite' | 'needs_more';
  reclassified: boolean;
  edited: boolean;
  detailsLinkIssued: boolean;
  to: string;
}

/** POST /api/proposals/:id/approve on a triage card — SENDS the reply. The
 * fork travels with the decision: passing the other one is the reclassify,
 * and the server records which was chosen. */
export async function approveIntakeTriage(
  tenantId: string,
  proposalId: string,
  decision: { fork: 'invite' | 'needs_more'; body?: string }
): Promise<{ proposalId: string; triage: TriageDecisionApi }> {
  return request(`/api/proposals/${proposalId}/approve`, {
    method: 'POST', tenantId, body: decision,
  });
}

/** GET /api/proposals/count — the review queue's badge number. */
export async function countPendingProposals(tenantId: string): Promise<number> {
  const result = await request<{ count: number }>('/api/proposals/count?status=pending', { tenantId });
  return result.count;
}

/** PATCH /api/threads/:id/state — the explicit state act. Closing is a
 * human decision and so is REOPENING (the coded rule: a later message on a
 * closed thread never silently reopens it — appendMessage leaves 'closed'
 * alone, so this door is the only way back). */
export async function setThreadState(
  tenantId: string,
  id: string,
  state: string
): Promise<{ thread: ThreadRowApi }> {
  return request(`/api/threads/${id}/state`, { method: 'PATCH', tenantId, body: { state } });
}

/* ------------------------------------------------------------------ *
 * B17a — the calendar's wires. Shapes pinned to the serializers in
 * packages/server/src/plugins/preparation.ts.
 * ------------------------------------------------------------------ */

export interface CalendarEventApi {
  id: string;
  title: string | null;
  description: string | null;
  location: string | null;
  organizer: string | null;
  startsAt: string;
  endsAt: string | null;
  /** The event's OWN local time, rendered by the server (the B6a lesson:
   * the client never re-derives it from the absolute instant). */
  timezone: string | null;
  startLocal: string | null;
  status: string;
  method: string | null;
  messageId: string | null;
  // 0912 — an authored event rides the same list (spec-coconup-calendar-
  // reminders-lists-on-core-2026-10-07); these say which kind it is and what
  // the Personal tab's edit form opens with. Optional: an older server, or a
  // test fixture, may not send them, and an invite is read-only either way.
  // S1b: 'google' joins the union — every Google event (owner/writer/reader
  // calendar alike) rides this same list now, carrying googleConnectionId +
  // externalCalendarId so the cockpit's Calendar screen can match it to its
  // account and calendar entry from GET /api/google/connections.
  source?: 'invite' | 'authored' | 'google';
  visibility?: 'tenant' | 'private';
  allDay?: boolean;
  recurrenceRule?: string | null;
  notes?: string | null;
  createdByUserId?: string | null;
  remindMinutesBefore?: number[];
  googleConnectionId?: string | null;
  externalCalendarId?: string | null;
}

export async function listEvents(
  tenantId: string,
  opts: { from?: string; to?: string; includeCancelled?: boolean } = {}
): Promise<CalendarEventApi[]> {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  if (opts.includeCancelled) params.set('include_cancelled', 'true');
  const qs = params.toString();
  const result = await request<{ events: CalendarEventApi[] }>(`/api/events${qs ? `?${qs}` : ''}`, { tenantId });
  return result.events;
}

/* ------------------------------------------------------------------ *
 * B18g / Core gap 18 — soft time blocks. A hold is time set aside for a
 * promise: it overlaps freely, refuses nothing, and becomes a real
 * commitment when the thing is booked.
 * ------------------------------------------------------------------ */

export interface HoldApi {
  id: string;
  subjectType: string;
  subjectId: string;
  /** Inclusive at both ends — the day named is held. */
  startsOn: string;
  endsOn: string;
  label: string;
  status: 'held' | 'promoted' | 'released';
  createdAt: string;
}

/** GET /api/events — the agenda, in ONE round trip.
 *
 *  ⚑ HOLDS COME BACK IN THEIR OWN ARRAY and must stay there. An event is a
 *  fact that happened; a hold is time set aside for a promise. A screen that
 *  merged them would show a meeting that does not exist. */
export async function listAgenda(
  tenantId: string,
  opts: { from?: string; to?: string; includeCancelled?: boolean } = {}
): Promise<{ events: CalendarEventApi[]; holds: HoldApi[] }> {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  if (opts.includeCancelled) params.set('include_cancelled', 'true');
  const qs = params.toString();
  return request(`/api/events${qs ? `?${qs}` : ''}`, { tenantId });
}

/* ------------------------------------------------------------------ *
 * The cockpit's Personal tab (spec-the-cockpit-personal-tab-and-the-01b-
 * nav-build-1-2026-10-08 §2f). Session routes over the SAME Core functions
 * the service API calls (server plugins/personalCockpit.ts). `tenantId` is
 * whichever tenant the row lives in — the cockpit's own, or the family
 * tenant read beside it (N3) — so the membership gate and RLS that guard any
 * tenant guard both.
 * ------------------------------------------------------------------ */

/** The family's word for visibility on the way OUT of these routes. */
export type FamilyVisibility = 'family' | 'private';

export interface AuthoredEventInput {
  title: string;
  /** Offset-free wall clock (`2026-10-13T15:30`) or a date for all-day. */
  start: string;
  end?: string | null;
  timezone: string;
  allDay?: boolean;
  rrule?: string | null;
  visibility?: FamilyVisibility;
  remindMinutesBefore?: number[];
}

export interface AuthoredEventApi {
  id: string;
  source: 'invite' | 'authored';
  title: string | null;
  start: string;
  startLocal: string | null;
  timezone: string | null;
  allDay: boolean;
  rrule: string | null;
  visibility: FamilyVisibility;
  remindMinutesBefore: number[];
  status: 'confirmed' | 'cancelled';
}

export async function createAuthoredEvent(tenantId: string, input: AuthoredEventInput): Promise<AuthoredEventApi> {
  const r = await request<{ event: AuthoredEventApi }>('/api/calendar/authored', { method: 'POST', tenantId, body: input });
  return r.event;
}

export async function updateAuthoredEvent(
  tenantId: string,
  id: string,
  patch: Partial<AuthoredEventInput>
): Promise<AuthoredEventApi> {
  const r = await request<{ event: AuthoredEventApi }>(`/api/calendar/authored/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    tenantId,
    body: patch,
  });
  return r.event;
}

export async function cancelAuthoredEvent(tenantId: string, id: string): Promise<AuthoredEventApi> {
  const r = await request<{ event: AuthoredEventApi }>(`/api/calendar/authored/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    tenantId,
  });
  return r.event;
}

export interface ReminderApi {
  id: string;
  recipientUserId: string;
  text: string;
  fireAt: string;
  fireAtLocal: string | null;
  timezone: string | null;
  eventId: string | null;
  visibility: FamilyVisibility;
  status: 'pending' | 'claimed' | 'sent' | 'failed' | 'cancelled';
  sentAt: string | null;
  lastError: string | null;
  createdByUserId: string | null;
}

export async function listReminders(tenantId: string): Promise<ReminderApi[]> {
  const r = await request<{ reminders: ReminderApi[] }>('/api/reminders', { tenantId });
  return r.reminders;
}

export async function createReminder(
  tenantId: string,
  input: { text: string; fireAt: string; timezone: string; visibility?: FamilyVisibility }
): Promise<ReminderApi> {
  const r = await request<{ reminder: ReminderApi }>('/api/reminders', { method: 'POST', tenantId, body: input });
  return r.reminder;
}

export async function cancelReminder(tenantId: string, id: string): Promise<ReminderApi> {
  const r = await request<{ reminder: ReminderApi }>(`/api/reminders/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    tenantId,
  });
  return r.reminder;
}

export interface ListApi {
  id: string;
  title: string;
  visibility: FamilyVisibility;
  archivedAt: string | null;
  createdByUserId: string | null;
  createdVia: string | null;
  openCount?: number;
}

export interface ListItemApi {
  id: string;
  listId: string;
  text: string;
  position: number;
  done: boolean;
  doneAt: string | null;
  doneByUserId: string | null;
  createdByUserId: string | null;
  createdVia: string | null;
  createdAt: string;
}

export interface ListWithItemsApi extends ListApi {
  /** Open first (in order), then done in the last seven days. */
  items: ListItemApi[];
}

export async function listLists(tenantId: string): Promise<ListApi[]> {
  const r = await request<{ lists: ListApi[] }>('/api/lists', { tenantId });
  return r.lists;
}

export async function createList(tenantId: string, input: { title: string; visibility: FamilyVisibility }): Promise<ListApi> {
  const r = await request<{ list: ListApi }>('/api/lists', { method: 'POST', tenantId, body: input });
  return r.list;
}

export async function getList(tenantId: string, id: string): Promise<ListWithItemsApi> {
  const r = await request<{ list: ListWithItemsApi }>(`/api/lists/${encodeURIComponent(id)}`, { tenantId });
  return r.list;
}

export interface AddListItemsApi {
  results: Array<{ text: string; outcome: 'added' | 'undone' | 'already_open'; item: ListItemApi }>;
}

export async function addListItems(tenantId: string, listId: string, texts: string[]): Promise<AddListItemsApi> {
  return request<AddListItemsApi>(`/api/lists/${encodeURIComponent(listId)}/items`, {
    method: 'POST',
    tenantId,
    body: { texts },
  });
}

export async function setListItemDone(tenantId: string, listId: string, itemId: string, done: boolean): Promise<ListItemApi> {
  const r = await request<{ item: ListItemApi }>(
    `/api/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`,
    { method: 'PATCH', tenantId, body: { done } }
  );
  return r.item;
}

export async function removeListItem(tenantId: string, listId: string, itemId: string): Promise<ListItemApi> {
  const r = await request<{ item: ListItemApi }>(
    `/api/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}/remove`,
    { method: 'POST', tenantId }
  );
  return r.item;
}

export async function archiveList(tenantId: string, listId: string): Promise<ListApi> {
  const r = await request<{ list: ListApi }>(`/api/lists/${encodeURIComponent(listId)}/archive`, { method: 'POST', tenantId });
  return r.list;
}

/** The family tenant read beside this one (N3), or null — null whenever the
 * lever names nothing or a tenant the viewer is not an active member of. */
export interface FamilyTenantApi {
  tenantId: string;
  name: string;
}

export async function getPersonalFamily(tenantId: string): Promise<FamilyTenantApi | null> {
  const r = await request<{ family: FamilyTenantApi | null }>('/api/personal/family', { tenantId });
  return r.family;
}

export interface PersonalMemberApi {
  userId: string;
  name: string | null;
  role: string;
}

export async function listPersonalMembers(tenantId: string): Promise<PersonalMemberApi[]> {
  const r = await request<{ members: PersonalMemberApi[] }>('/api/personal/members', { tenantId });
  return r.members;
}

export interface SlippingApi {
  hold: HoldApi;
  /** Whole days past the window's end. Always >= 1. */
  daysOver: number;
}

/** GET /api/holds/slipping — what we said we would do by now, and have not.
 *  Derived at read time; `today` is the caller's clock. */
export async function listSlipping(tenantId: string, today?: string): Promise<SlippingApi[]> {
  const qs = today ? `?today=${encodeURIComponent(today)}` : '';
  const result = await request<{ slipping: SlippingApi[] }>(`/api/holds/slipping${qs}`, { tenantId });
  return result.slipping;
}

/** A prepared item: the thing itself plus HOW it got here ('explicit' —
 * a human attached it — outranks 'derived'; `via` names the derivation). */
export type PreparedItemApi = Record<string, unknown> & { source: string; via?: string };

export interface EventBriefingApi {
  preparation: {
    event: Record<string, unknown>;
    party: PreparedItemApi | null;
    engagement: PreparedItemApi | null;
    threads: PreparedItemApi[];
    commitments: { owedByMe: PreparedItemApi[]; owedToMe: PreparedItemApi[] };
    documents: PreparedItemApi[];
    gaps: string[];
  };
  note: { text: string; sources: Array<{ key: string; type: string; id: string }>; model: string; promptVersion: number } | null;
  aiOrdered: boolean;
  noteStatus: 'composing' | 'ready' | 'failed';
}

/** GET /api/events/:id/briefing — B13/B13b's async door. 'composing' means
 * poll again; the assembly is complete either way. */
export async function getEventBriefing(tenantId: string, id: string): Promise<EventBriefingApi> {
  const result = await request<{ briefing: EventBriefingApi }>(`/api/events/${id}/briefing`, { tenantId });
  return result.briefing;
}

/* ------------------------------------------------------------------ *
 * B17b — the to-dos' wires (serializeTask in
 * packages/server/src/plugins/operations.ts is the shape's authority).
 * ------------------------------------------------------------------ */

export interface TaskApi {
  id: string;
  title: string;
  description: string | null;
  dueAt: string | null;
  status: string;
  assigneeType: string | null;
  assigneeId: string | null;
  recordId: string | null;
  createdBy: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  deferUntil: string | null;
  sourceThreadId: string | null;
  sourceEventId: string | null;
  /** The task's OWN attribution only — the derived answer stays behind
   * GET /api/tasks/:id/attribution, deliberately. */
  productId: string | null;
  startedAt: string | null;
  droppedAt: string | null;
  /** B18e forward-compat: the class chip renders whatever the row carries;
   * the server does not ship this yet. */
  classification?: string | null;
}

/** GET /api/tasks — the whole tenant's tasks (on a single-user cockpit,
 * all of them are Huan's; /mine filters by assignee and a quick-added task
 * has none). */
export async function listTasks(
  tenantId: string,
  opts: { status?: string } = {}
): Promise<TaskApi[]> {
  const qs = opts.status ? `?status=${encodeURIComponent(opts.status)}` : '';
  const result = await request<{ tasks: TaskApi[] }>(`/api/tasks${qs}`, { tenantId });
  return result.tasks;
}

/** POST /api/tasks — files-are-approval: a typed to-do is real immediately. */
export async function createTask(
  tenantId: string,
  args: { title: string; dueAt?: string | null }
): Promise<TaskApi> {
  const result = await request<{ task: TaskApi }>('/api/tasks', {
    method: 'POST', tenantId, body: args,
  });
  return result.task;
}

export async function startTask(tenantId: string, id: string): Promise<TaskApi> {
  const result = await request<{ task: TaskApi }>(`/api/tasks/${id}/start`, { method: 'POST', tenantId });
  return result.task;
}

export async function completeTask(tenantId: string, id: string): Promise<TaskApi> {
  const result = await request<{ task: TaskApi }>(`/api/tasks/${id}/complete`, { method: 'POST', tenantId });
  return result.task;
}

export async function dropTask(tenantId: string, id: string): Promise<TaskApi> {
  const result = await request<{ task: TaskApi }>(`/api/tasks/${id}/drop`, { method: 'POST', tenantId });
  return result.task;
}

/* ------------------------------------------------------------------ *
 * B17c — the People surface's wires (serializeParty/serializeIdentity/
 * serializeTouch in packages/server/src/plugins/parties.ts are the
 * shapes' authority).
 * ------------------------------------------------------------------ */

export interface PartyFirstTouchApi {
  at: string | null;
  arrivalDoor: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  referrer: string | null;
  landingPath: string | null;
}

export interface PartyApi {
  id: string;
  kind: 'person' | 'organization';
  displayName: string;
  firstTouch: PartyFirstTouchApi;
  parkedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PartyIdentityApi {
  id: string;
  partyId: string;
  kind: string;
  value: string;
  verifiedAt: string | null;
  consentType: string | null;
  consentDate: string | null;
  consentSource: string | null;
  unsubscribedAt: string | null;
  claimedAt: string;
  releasedAt: string | null;
  releasedReason: string | null;
}

export interface PartyTouchApi {
  id: string;
  occurredAt: string;
  arrivalDoor: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  referrer: string | null;
  landingPath: string | null;
}

/** GET /api/parties/:id — the whole answer about one party. journeyState
 * and primaryEmail are DERIVED and ship with the party (the plugin's own
 * rule: a caller made to ask separately will cache them stale). */
export interface PartyDetailApi {
  party: PartyApi;
  identities: PartyIdentityApi[];
  journeyState: 'contact' | 'lead' | 'customer';
  primaryEmail: string | null;
}

/** One identity's may-we-message-now answer — DERIVED server-side (three
 * gates: released, unsubscribed, CASL window); the client never re-derives
 * it, it renders `contactable` + the server's own `reason`. */
export interface ContactabilityApi {
  identityId: string;
  kind: string;
  value: string;
  contactable: boolean;
  expiresAt: string | null;
  reason: string;
}

export interface PartyCollisionApi {
  kind: string;
  value: string;
  releasedAt: string | null;
  previousHolder: { partyId: string; displayName: string | null };
  currentHolder: { partyId: string; displayName: string | null };
}

export async function listParties(
  tenantId: string,
  opts: { includeParked?: boolean } = {}
): Promise<PartyApi[]> {
  const qs = opts.includeParked ? '?includeParked=true' : '';
  const result = await request<{ parties: PartyApi[] }>(`/api/parties${qs}`, { tenantId });
  return result.parties;
}

/** G6 C2: one row of a customer's history — a conversation, its channel, and how it came to be theirs. */
export interface PartyHistoryItemApi {
  threadId: string;
  subject: string | null;
  state: string;
  lastMessageAt: string | null;
  channel: string | null;
  /** 'confirmed' = a person said so; 'identity' = they wrote from an address or number on file. */
  route: 'confirmed' | 'identity';
}

export async function getPartyHistory(tenantId: string, id: string): Promise<PartyHistoryItemApi[]> {
  const result = await request<{ history: PartyHistoryItemApi[] }>(`/api/parties/${id}/history`, { tenantId });
  return result.history;
}

export async function getParty(tenantId: string, id: string): Promise<PartyDetailApi> {
  return request<PartyDetailApi>(`/api/parties/${id}`, { tenantId });
}

export async function createParty(
  tenantId: string,
  args: { kind: 'person' | 'organization'; displayName: string }
): Promise<PartyApi> {
  const result = await request<{ party: PartyApi }>('/api/parties', {
    method: 'POST', tenantId, body: args,
  });
  return result.party;
}

export async function setPartyParked(tenantId: string, id: string, parked: boolean): Promise<PartyApi> {
  const result = await request<{ party: PartyApi }>(`/api/parties/${id}/parked`, {
    method: 'PUT', tenantId, body: { parked },
  });
  return result.party;
}

export async function listPartyTouches(tenantId: string, id: string): Promise<PartyTouchApi[]> {
  const result = await request<{ touches: PartyTouchApi[] }>(`/api/parties/${id}/touches`, { tenantId });
  return result.touches;
}

export async function recordPartyTouch(
  tenantId: string,
  id: string,
  args: { occurredAt: string; attribution?: { arrivalDoor?: string } }
): Promise<string> {
  const result = await request<{ touchId: string }>(`/api/parties/${id}/touches`, {
    method: 'POST', tenantId, body: args,
  });
  return result.touchId;
}

export async function claimPartyIdentity(
  tenantId: string,
  id: string,
  args: { kind: string; value: string }
): Promise<{ identity: PartyIdentityApi; displaced: PartyIdentityApi | null; unchanged: boolean }> {
  return request(`/api/parties/${id}/identities`, { method: 'POST', tenantId, body: args });
}

export async function getPartyContactability(
  tenantId: string,
  id: string,
  kind: string
): Promise<ContactabilityApi[]> {
  const result = await request<{ contactability: ContactabilityApi[] }>(
    `/api/parties/${id}/contactability?kind=${encodeURIComponent(kind)}`,
    { tenantId }
  );
  return result.contactability;
}

/* ------------------------------------------------------------------ *
 * B18f — the letter. Drafted into the pen, shipped on approval, answered
 * by the client on their own portal.
 * ------------------------------------------------------------------ */

export interface LetterItemApi {
  taskId: string;
  title: string;
  classification: 'bug' | 'enhancement';
  position: number;
}

export interface LetterApi {
  id: string;
  engagementId: string;
  partyId: string;
  partyName: string;
  status: 'drafted' | 'approved' | 'sent' | 'accepted' | 'changes_asked' | 'withdrawn';
  bodyMd: string;
  timingMd: string | null;
  billingMd: string | null;
  clientResponse: string | null;
  promisedStart: string | null;
  promisedEnd: string | null;
  model: string | null;
  draftedAt: string;
  sentAt: string | null;
  answeredAt: string | null;
  items: LetterItemApi[];
}

/** What a letter CARD carries — the same shape, flattened onto the pen. */
export interface ClientLetterProposedApi {
  letterId: string;
  engagementId: string;
  partyId: string;
  partyName: string;
  bodyMd: string;
  timingMd: string | null;
  billingMd: string | null;
  items: LetterItemApi[];
  /** B18g — the promised window, as DATES. Prefilled from a lever, editable
   *  on the card, and CLEARABLE: empty means no time is held. */
  promisedStart: string | null;
  promisedEnd: string | null;
  model: string | null;
}

export type LetterDraftAnswer =
  | { outcome: 'drafted'; proposalId: string; letter: LetterApi }
  | { outcome: 'nothing_queued' }
  | { outcome: 'already_in_flight'; letterId: string; status: string }
  | { outcome: 'too_soon'; nextDueAt: string };

/** POST /api/engagements/:id/letter — write one now. Every refusal is a
 *  NAMED outcome, because "nothing to say yet" is an ordinary state of a
 *  client relationship and not a fault to go hunting for. */
export async function draftLetter(
  tenantId: string,
  engagementId: string,
  force = true
): Promise<LetterDraftAnswer> {
  return request(`/api/engagements/${engagementId}/letter`, {
    method: 'POST', tenantId, body: { force },
  });
}

/** POST /api/parties/:id/start-client — make somebody Huan already knows a
 *  client: engagement + a party-scoped login, in one transaction. The accept
 *  link is returned ONCE and is a capability — show it to be SENT, never
 *  followed here. */
export async function startClientForParty(
  tenantId: string,
  partyId: string,
  args: { email: string }
): Promise<{ engagementId: string; acceptUrl: string }> {
  return request(`/api/parties/${partyId}/start-client`, { method: 'POST', tenantId, body: args });
}

/** POST /api/parties/:id/letter — the door a SCREEN uses: People knows who
 *  a person is, not which engagement row that is. */
export async function draftLetterForParty(
  tenantId: string,
  partyId: string,
  force = true
): Promise<LetterDraftAnswer | { outcome: 'no_engagement' }> {
  return request(`/api/parties/${partyId}/letter`, { method: 'POST', tenantId, body: { force } });
}

/** GET /api/parties/:id/letters — their letters, plus what is still waiting
 *  to be promised (the number that says whether writing one is worth it). */
export async function listPartyLetters(
  tenantId: string,
  partyId: string
): Promise<{ letters: LetterApi[]; queued: Array<{ taskId: string; title: string }> }> {
  return request(`/api/parties/${partyId}/letters`, { tenantId });
}

export interface LetterSentApi {
  proposalId: string;
  letterId: string;
  to: string | null;
  edited: boolean;
  itemCount: number;
  mailFailed: boolean;
}

/** POST /api/proposals/:id/approve on a letter card — SHIPS it. Every part
 *  is editable at the moment of deciding, and what is stored is what was
 *  approved. */
export async function approveClientLetter(
  tenantId: string,
  proposalId: string,
  edits: {
    bodyMd?: string; timingMd?: string; billingMd?: string;
    promisedStart?: string | null; promisedEnd?: string | null;
  } = {}
): Promise<{ proposalId: string; letter: LetterSentApi }> {
  return request(`/api/proposals/${proposalId}/approve`, { method: 'POST', tenantId, body: edits });
}

/* ---- the CLIENT's side, on the portal ---- */

export interface PortalLetterApi {
  id: string;
  status: string;
  bodyMd: string;
  timingMd: string | null;
  billingMd: string | null;
  items: Array<{ title: string; position: number }>;
  sentAt: string | null;
  answeredAt: string | null;
  yourResponse: string | null;
  /** The server says what they can do about it — the screen never works it
   *  out from a status vocabulary that belongs to us. */
  awaitingYou: boolean;
}

export async function listPortalLetters(tenantId: string): Promise<PortalLetterApi[]> {
  const result = await request<{ letters: PortalLetterApi[] }>('/api/portal/letters', { tenantId });
  return result.letters;
}

export async function answerPortalLetter(
  tenantId: string,
  letterId: string,
  answer: { answer: 'accepted' | 'changes_asked'; response?: string }
): Promise<{ letter: { id: string; status: string } }> {
  return request(`/api/portal/letters/${letterId}/answer`, { method: 'POST', tenantId, body: answer });
}

/** B17e — POST /api/parties/:id/telegram-link. The deep link a person opens
 *  to connect their own Telegram; the conversation then lands on the spine
 *  like any other. Minted fresh each time and shown ONCE — it is a
 *  capability, and it travels through a message. */
export async function mintTelegramLink(
  tenantId: string,
  partyId: string
): Promise<{ link: string; expiresAt: string }> {
  return request(`/api/parties/${partyId}/telegram-link`, { method: 'POST', tenantId });
}

/** Seller-side only — the parties plugin's own header says why this must
 * never reach a visitor-facing surface. */
export async function listPartyCollisions(tenantId: string): Promise<PartyCollisionApi[]> {
  const result = await request<{ collisions: PartyCollisionApi[] }>('/api/party-collisions', { tenantId });
  return result.collisions;
}

/* ------------------------------------------------------------------ *
 * K3 — the Agreement (c4-agreement.md). The Account is presented
 * everywhere as "Account" (slice-1-plan.md's rule) even though the wire
 * endpoint is still `/api/engagements` — K1's `/api/accounts` façade is a
 * sibling, parallel chunk not depended on here.
 * ------------------------------------------------------------------ */

export interface AccountPickerApi {
  id: string;
  name: string;
  phase: string;
}

/** GET /api/engagements — every Account, for the "New agreement" picker.
 *  Named for what it presents (Account), even though today's door is the
 *  underlying engagements route. */
export async function listAccountsForPicker(tenantId: string): Promise<AccountPickerApi[]> {
  const result = await request<{ engagements: { id: string; name: string; phase: string }[] }>(
    '/api/engagements', { tenantId }
  );
  return result.engagements.map((e) => ({ id: e.id, name: e.name, phase: e.phase }));
}

export interface AgreementApi {
  id: string;
  accountId: string;
  engagementId: string | null;
  kind: string;
  version: number;
  title: string;
  summary: string | null;
  status: string;
  documentRef: string | null;
  endCondition: string | null;
  acceptedOn: string | null;
  acceptedBy: string | null;
  acceptedVia: string | null;
  signatureMode: string | null;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  // V13 (v13-agreement-integrity.md).
  supersedesId: string | null;
  offeredRevision: number | null;
  acceptedRevision: number | null;
  acceptedContentSha256: string | null;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  endedOn: string | null;
  endedReason: string | null;
  replacesObligations: boolean;
}

export interface AgreementTermApi {
  id: string;
  agreementId: string;
  side: 'vowric' | 'client';
  kind: string;
  description: string;
  valueText: string | null;
  owner: string | null;
  dueOn: string | null;
  expiresOn: string | null;
  fulfilledOn: string | null;
  fulfilmentNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listAgreements(
  tenantId: string,
  args: { accountId?: string; engagementId?: string }
): Promise<AgreementApi[]> {
  const qs = args.accountId
    ? `?account_id=${encodeURIComponent(args.accountId)}`
    : args.engagementId
    ? `?engagement_id=${encodeURIComponent(args.engagementId)}`
    : '';
  const result = await request<{ agreements: AgreementApi[] }>(`/api/agreements${qs}`, { tenantId });
  return result.agreements;
}

export async function getAgreement(
  tenantId: string,
  id: string
): Promise<{ agreement: AgreementApi; terms: AgreementTermApi[] }> {
  return request(`/api/agreements/${id}`, { tenantId });
}

export async function createAgreement(
  tenantId: string,
  args: { accountId: string; kind: string; title: string; endCondition?: string | null; signatureMode?: string | null }
): Promise<{ agreement: AgreementApi }> {
  return request('/api/agreements', { method: 'POST', tenantId, body: args });
}

export async function addAgreementTerm(
  tenantId: string,
  agreementId: string,
  args: {
    side: 'vowric' | 'client'; kind: string; description: string;
    valueText?: string | null; owner?: string | null; dueOn?: string | null; expiresOn?: string | null;
  }
): Promise<{ term: AgreementTermApi }> {
  return request(`/api/agreements/${agreementId}/terms`, { method: 'POST', tenantId, body: args });
}

export async function sendAgreement(tenantId: string, id: string): Promise<{ agreement: AgreementApi }> {
  return request(`/api/agreements/${id}/send`, { method: 'POST', tenantId });
}

export async function acceptAgreement(
  tenantId: string,
  id: string,
  args: { revision: number; acceptedOn: string; acceptedBy: string; via: string }
): Promise<{
  agreement: AgreementApi; terms: AgreementTermApi[]; commitmentsOpened: number;
  supersededAgreementId: string | null; predecessorCommitmentsCancelled: number;
}> {
  return request(`/api/agreements/${id}/accept`, { method: 'POST', tenantId, body: args });
}

export async function endAgreement(
  tenantId: string,
  id: string,
  args: { endedOn: string; reason: string }
): Promise<{ agreement: AgreementApi }> {
  return request(`/api/agreements/${id}/end`, { method: 'POST', tenantId, body: args });
}

export async function fulfilAgreementTerm(
  tenantId: string,
  agreementId: string,
  termId: string,
  args: { on: string; note?: string | null }
): Promise<{ term: AgreementTermApi }> {
  return request(`/api/agreements/${agreementId}/terms/${termId}/fulfil`, { method: 'POST', tenantId, body: args });
}

/* ------------------------------------------------------------------
 * K1 — the Account (console kernel, c2-account-kernel.md). B9's
 * `engagements` row, presented under its own name — "Account" everywhere
 * on this surface, never "engagement" (that word is reserved for K2's
 * bounded engagements below).
 * ------------------------------------------------------------------ */

export type AccountPhase = 'design_partner' | 'billable' | 'closed';

export interface AccountListRowApi {
  id: string;
  party: { id: string; displayName: string } | null;
  phase: AccountPhase;
  startedOn: string;
  endedOn: string | null;
  daysInPhase: number | null;
}

export interface AccountApi {
  id: string;
  partyId: string;
  name: string;
  productId: string | null;
  phase: AccountPhase;
  startedOn: string;
  endedOn: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountPhaseChangeApi {
  id: string;
  fromPhase: AccountPhase;
  toPhase: AccountPhase;
  effectiveOn: string;
  reason: string | null;
  changedByMembershipId: string | null;
  changedAt: string;
}

/** K2's own summary shape, as seen from an Account: the bounded engagement's
 * OWN lifecycle_state, never the Account's phase. */
export interface AccountEngagementApi {
  id: string;
  kind: string;
  title: string;
  state: string | null;
  sinceDays: number | null;
  startedOn: string | null;
  endedOn: string | null;
}

export interface AccountSummaryApi {
  account: AccountApi;
  party: { id: string; displayName: string } | null;
  identities: Array<{ id: string; kind: string; value: string; verifiedAt: string | null; releasedAt: string | null }>;
  history: AccountPhaseChangeApi[];
  engagements: AccountEngagementApi[];
  counts: { engagements: number; agreements: number; outcomes: number };
}

export async function listAccounts(tenantId: string): Promise<AccountListRowApi[]> {
  const result = await request<{ accounts: AccountListRowApi[] }>('/api/accounts', { tenantId });
  return result.accounts;
}

export async function getAccount(tenantId: string, id: string): Promise<AccountSummaryApi> {
  return request<AccountSummaryApi>(`/api/accounts/${id}`, { tenantId });
}

/** POST /api/accounts — {party_id, started_on, phase} (c2's own body; no
 * separate name — the server derives one from the party). */
export async function startAccount(
  tenantId: string,
  args: { partyId: string; startedOn: string; phase?: AccountPhase; productId?: string | null }
): Promise<AccountApi> {
  const result = await request<{ account: AccountApi }>('/api/accounts', {
    method: 'POST', tenantId, body: args,
  });
  return result.account;
}

export async function changeAccountPhase(
  tenantId: string,
  id: string,
  args: { to: AccountPhase; effectiveOn: string; reason?: string }
): Promise<{ account: AccountApi; change: AccountPhaseChangeApi }> {
  return request(`/api/accounts/${id}/phase`, { method: 'POST', tenantId, body: args });
}

/* ------------------------------------------------------------------ *
 * K2's consumer — the bounded engagement (c3-lifecycle-kernel.md). The
 * RECORD itself (create/read/list) rides the generic /records door above
 * (`createRecord(tenantId, 'engagement', fields)`, `getRecord`,
 * `listRecords` with `recordType: 'engagement'`) — these two are the only
 * things the generic engine has no opinion about.
 * ------------------------------------------------------------------ */

export interface EngagementTransitionApi {
  id: string;
  fromState: string | null;
  toState: string;
  effectiveOn: string;
  changedAt: string;
  changedBy: string | null;
  note: string | null;
}

export async function transitionEngagementLifecycle(
  tenantId: string,
  id: string,
  args: { toState: string; effectiveOn: string; note?: string }
): Promise<{ engagement: RecordRow; transition: EngagementTransitionApi }> {
  return request(`/api/engagements/${id}/transition`, { method: 'POST', tenantId, body: args });
}

export async function listEngagementTransitions(tenantId: string, id: string): Promise<EngagementTransitionApi[]> {
  const result = await request<{ transitions: EngagementTransitionApi[] }>(`/api/engagements/${id}/transitions`, { tenantId });
  return result.transitions;
}

/* ------------------------------------------------------------------ *
 * K4 — the Outcome (c5-outcome.md). The RECORD itself rides the generic
 * /records door (getRecord, transitionRecord for defined -> measuring ->
 * realised/missed/withdrawn); these five are outcomes.ts's own face.
 * ------------------------------------------------------------------ */

export async function listOutcomes(
  tenantId: string,
  scope: { accountId: string } | { engagementId: string }
): Promise<RecordRow[]> {
  const qs =
    'accountId' in scope
      ? `account_id=${encodeURIComponent(scope.accountId)}`
      : `engagement_id=${encodeURIComponent(scope.engagementId)}`;
  const result = await request<{ outcomes: RecordRow[] }>(`/api/outcomes?${qs}`, { tenantId });
  return result.outcomes;
}

export async function createOutcome(tenantId: string, fields: Record<string, unknown>): Promise<RecordRow> {
  const result = await request<{ outcome: RecordRow }>('/api/outcomes', { method: 'POST', tenantId, body: { fields } });
  return result.outcome;
}

export async function updateOutcome(tenantId: string, id: string, fields: Record<string, unknown>): Promise<RecordRow> {
  const result = await request<{ outcome: RecordRow }>(`/api/outcomes/${id}`, { method: 'PATCH', tenantId, body: { fields } });
  return result.outcome;
}

export async function recordOutcomeReading(
  tenantId: string,
  id: string,
  args: { value: number; measuredOn: string; note?: string }
): Promise<RecordRow> {
  const result = await request<{ outcome: RecordRow }>(`/api/outcomes/${id}/reading`, {
    method: 'POST',
    tenantId,
    body: { value: args.value, measured_on: args.measuredOn, note: args.note },
  });
  return result.outcome;
}

export async function confirmOutcomeWithClient(
  tenantId: string,
  id: string,
  args: { on: string; by: string }
): Promise<RecordRow> {
  const result = await request<{ outcome: RecordRow }>(`/api/outcomes/${id}/confirm`, {
    method: 'POST',
    tenantId,
    body: args,
  });
  return result.outcome;
}

/* ------------------------------------------------------------------ *
 * K5 — Plan authorization (c6-plan-authorization.md). The RECORD's own
 * create/read/list rides the generic engine; these are
 * plan/authorize.ts's actions, over plugins/plans.ts.
 * ------------------------------------------------------------------ */

export interface PlanWeekLoadApi {
  weekStart: string;
  weekEnd: string;
  committedHours: number;
}

// V15 (v15-capacity-and-governing-agreement.md) — the full per-week
// breakdown the consequence card shows: committed (other plans' open
// commitments), reserved (other plans' RESERVED holds — hard, blocking),
// tentative (other plans' ordinary HELD holds — shown, never blocking,
// B18g), this plan's own share, the total actually compared to the
// ceiling, and the ceiling itself.
export interface PlanWeekBreakdownApi {
  weekStart: string;
  weekEnd: string;
  committedHours: number;
  reservedHours: number;
  tentativeHours: number;
  thisPlanHours: number;
  totalHours: number;
  ceiling: number | null;
  overCeiling: boolean;
}

export interface PlanConsequenceApi {
  // V15 — the one fact everything else derives from.
  verdict: 'within' | 'outside' | 'not_evaluable';
  withinPolicy: boolean;
  capacity: PlanWeekLoadApi[];
  margin: { computed: boolean; marginPct?: number; floorPct?: number; withinFloor?: boolean; notEvaluableReason?: string };
  policy: PlanPolicyApi | null;
  weeklyHours: number;
  weeks: string[];
  beyondLookahead: string[];
  weekly: PlanWeekBreakdownApi[];
}

export interface PlanOutApi {
  id: string;
  label: string;
}

/** The 409 `plan_refused` body — c6's own "an ask in the machine's sense":
 * `outs` renders straight into buttons, `breach` is the SAME shape
 * `computeConsequence` itself returns. Thrown as a typed error so a caller
 * can `catch` and branch on `.breach`/`.outs` rather than re-parsing
 * `ApiError.body`. */
export class PlanRefusedError extends ApiError {
  breach: PlanConsequenceApi;
  outs: PlanOutApi[];
  constructor(message: string, breach: PlanConsequenceApi, outs: PlanOutApi[], body: unknown) {
    super(409, 'plan_refused', message, body);
    this.breach = breach;
    this.outs = outs;
  }
}

async function planRequest<T>(path: string, opts: RequestOptions): Promise<T> {
  try {
    return await request<T>(path, opts);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'plan_refused' && err.body && typeof err.body === 'object') {
      const body = err.body as { breach?: PlanConsequenceApi; outs?: PlanOutApi[] };
      if (body.breach && body.outs) throw new PlanRefusedError(err.message, body.breach, body.outs, err.body);
    }
    throw err;
  }
}

export async function listPlans(tenantId: string, engagementId: string): Promise<RecordRow[]> {
  const result = await request<{ plans: RecordRow[] }>(
    `/api/plans?engagement_id=${encodeURIComponent(engagementId)}`,
    { tenantId }
  );
  return result.plans;
}

export async function createPlan(tenantId: string, fields: Record<string, unknown>): Promise<RecordRow> {
  const result = await request<{ plan: RecordRow }>('/api/plans', { method: 'POST', tenantId, body: { fields } });
  return result.plan;
}

export async function updatePlan(tenantId: string, id: string, fields: Record<string, unknown>): Promise<RecordRow> {
  const result = await request<{ plan: RecordRow }>(`/api/plans/${id}`, { method: 'PATCH', tenantId, body: { fields } });
  return result.plan;
}

export async function holdPlan(tenantId: string, id: string): Promise<RecordRow> {
  const result = await request<{ plan: RecordRow }>(`/api/plans/${id}/hold`, { method: 'POST', tenantId });
  return result.plan;
}

export async function getPlanConsequence(tenantId: string, id: string): Promise<PlanConsequenceApi> {
  const result = await request<{ consequence: PlanConsequenceApi }>(`/api/plans/${id}/consequence`, { tenantId });
  return result.consequence;
}

export interface ApprovePlanResultApi {
  plan: RecordRow;
  commitmentsCreated: number;
  receipt: string;
}

/** Throws `PlanRefusedError` (never a bare `ApiError`) on the 409 —
 * `plan_refused` — so a caller can render the five outs without its own
 * body-parsing. */
export async function approvePlan(tenantId: string, id: string, effectiveOn: string): Promise<ApprovePlanResultApi> {
  return planRequest<ApprovePlanResultApi>(`/api/plans/${id}/approve`, {
    method: 'POST', tenantId, body: { effectiveOn },
  });
}

export async function overridePlan(tenantId: string, id: string, reason: string): Promise<ApprovePlanResultApi> {
  const result = await request<{ plan: RecordRow; commitmentsCreated: number; receipt: string }>(
    `/api/plans/${id}/override`,
    { method: 'POST', tenantId, body: { reason } }
  );
  return result;
}

export async function displacePlanHold(
  tenantId: string,
  id: string,
  otherPlanId: string
): Promise<{ otherPlan: RecordRow; consequence: PlanConsequenceApi }> {
  return request(`/api/plans/${id}/displace`, { method: 'POST', tenantId, body: { other_plan_id: otherPlanId } });
}

export interface PlanPolicyApi {
  tenantId: string;
  maxCommittedHoursPerWeek: number;
  marginFloorPct: number | null;
  lookaheadWeeks: number;
  reserveHoursPerWeek: number;
}

export async function getPlanPolicy(tenantId: string): Promise<PlanPolicyApi | null> {
  const result = await request<{ policy: PlanPolicyApi | null }>('/api/plan-policy', { tenantId });
  return result.policy;
}

export async function setPlanPolicy(
  tenantId: string,
  args: { maxCommittedHoursPerWeek: number; marginFloorPct: number | null; lookaheadWeeks: number; reserveHoursPerWeek?: number }
): Promise<PlanPolicyApi> {
  const result = await request<{ policy: PlanPolicyApi }>('/api/plan-policy', { method: 'PUT', tenantId, body: args });
  return result.policy;
}

/* -------------------------------------------------------------------
 * V15 (v15-capacity-and-governing-agreement.md) — reservation, the
 * three dates, and the audit read.
 * ------------------------------------------------------------------- */

export async function reservePlan(
  tenantId: string,
  id: string,
  args: { agreementId: string; until: string }
): Promise<{ plan: RecordRow; reservedUntil: string }> {
  return request(`/api/plans/${id}/reserve`, {
    method: 'POST', tenantId, body: { agreement_id: args.agreementId, until: args.until },
  });
}

export interface PlanForecastApi {
  id: string;
  planId: string;
  forecastStart: string;
  forecastEnd: string;
  reason: string | null;
  recordedBy: string | null;
  recordedAt: string;
}

export async function recordForecast(
  tenantId: string,
  id: string,
  args: { forecastStart: string; forecastEnd: string; reason?: string | null }
): Promise<{ plan: RecordRow; forecastCount: number }> {
  return request(`/api/plans/${id}/forecast`, { method: 'POST', tenantId, body: args });
}

export async function listPlanForecasts(tenantId: string, id: string): Promise<PlanForecastApi[]> {
  const result = await request<{ forecasts: PlanForecastApi[] }>(`/api/plans/${id}/forecasts`, { tenantId });
  return result.forecasts;
}

/** The revision ASK (plan.revise, member) — distinct from `updatePlan`
 * (proposed/held only): this is the ONLY way to propose a new window on an
 * already-approved/overridden (frozen) plan. Throws `PlanRefusedError`
 * never for this call (revise only records the ask); left as an ordinary
 * request so a CommandOwnedError/ValidationError surfaces plainly. */
export async function revisePlan(
  tenantId: string,
  id: string,
  args: { proposedStart: string; proposedEnd: string; reason: string }
): Promise<{ plan: RecordRow }> {
  return request(`/api/plans/${id}/revise`, { method: 'POST', tenantId, body: args });
}

/** Throws `PlanRefusedError` on `not_evaluable`/`outside`, same as
 * `approvePlan` — the revised window is re-evaluated under the SAME
 * capacity/margin/governing gate. */
export async function acceptRevision(
  tenantId: string,
  id: string,
  effectiveOn: string
): Promise<{ plan: RecordRow; releasedCommitments: number; createdCommitments: number; receipt: string }> {
  return planRequest(`/api/plans/${id}/accept-revision`, { method: 'POST', tenantId, body: { effectiveOn } });
}

export async function markPlanInternal(tenantId: string, id: string, internal: boolean): Promise<RecordRow> {
  const result = await request<{ plan: RecordRow }>(`/api/plans/${id}/mark-internal`, {
    method: 'POST', tenantId, body: { internal },
  });
  return result.plan;
}

export interface PlanAuthorizationApi {
  id: string;
  planId: string;
  kind: 'approve' | 'override' | 'reserve' | 'release' | 'revise';
  verdict: 'within' | 'outside' | 'not_evaluable';
  inputs: Record<string, unknown>;
  decidedBy: string | null;
  decidedAt: string;
  governingAgreementIds: string[];
}

export async function listPlanAuthorizations(tenantId: string, id: string): Promise<PlanAuthorizationApi[]> {
  const result = await request<{ authorizations: PlanAuthorizationApi[] }>(`/api/plans/${id}/authorizations`, { tenantId });
  return result.authorizations;
}

/* ------------------------------------------------------------------ *
 * B18b — the CLIENT portal (clients.vowric.com). These three doors
 * answer a client-role membership and refuse everyone else, which is
 * also how the shell knows which surface to render: ask, and a 403 means
 * "you are not a client here".
 * ------------------------------------------------------------------ */

export interface PortalMeApi {
  party: { id: string; name: string };
  engagement: { id: string; name: string; phase: string; startedOn: string } | null;
  email: string;
  /** Each section's honest state — 'ready' or 'not_yet'. A section that is
   *  not built is NAMED rather than hidden, so a client is never shown a
   *  door that does nothing and never wonders what they missed. */
  sections: Record<string, string>;
}

export interface PortalMessageApi {
  id: string;
  /** 'you' (the client) or 'vowric'. Deliberately not the engine's own
   *  inbound/outbound vocabulary. */
  who: string;
  body: string;
  occurredAt: string;
}

export interface PortalConversationApi {
  thread: { id: string; subject: string | null; lastMessageAt: string | null } | null;
  messages: PortalMessageApi[];
}

export async function getPortalMe(tenantId: string): Promise<PortalMeApi> {
  return request<PortalMeApi>('/api/portal/me', { tenantId });
}

export async function getPortalConversation(tenantId: string): Promise<PortalConversationApi> {
  return request<PortalConversationApi>('/api/portal/conversation', { tenantId });
}

export async function sayInPortal(tenantId: string, body: string): Promise<{ threadId: string; messageId: string }> {
  return request('/api/portal/conversation', { method: 'POST', tenantId, body: { body } });
}

/* ------------------------------------------------------------------ *
 * B18d — the buckets: what every client is telling us, by topic,
 * across all of them. `listBuckets`/`requestBucketSummary` in
 * packages/core/src/party/ are the shapes' authority.
 * ------------------------------------------------------------------ */

export interface BucketItemApi {
  threadId: string;
  partyId: string;
  partyName: string;
  snippet: string;
  lastMessageAt: string | null;
  messageCount: number;
}

export interface BucketApi {
  kind: string;
  name: string;
  /** Threads, not messages — and it counts EVERYTHING, not just what the
   *  server returned in `items`. */
  threadCount: number;
  items: BucketItemApi[];
}

export interface BucketSummaryApi {
  text: string;
  threadIds: string[];
  parties: string[];
  truncated: boolean;
  model: string;
  promptVersion: number;
}

/** B13b's async contract, third consumer: 'composing' means poll again. */
export interface BucketSummaryAnswerApi {
  status: 'composing' | 'ready' | 'failed' | 'not_needed';
  summary: BucketSummaryApi | null;
}

export async function listBuckets(tenantId: string): Promise<BucketApi[]> {
  const result = await request<{ buckets: BucketApi[] }>('/api/buckets', { tenantId });
  return result.buckets;
}

export async function getBucketSummary(tenantId: string, kind: string): Promise<BucketSummaryAnswerApi> {
  return request<BucketSummaryAnswerApi>(`/api/buckets/${encodeURIComponent(kind)}/summary`, { tenantId });
}

/** What a client-work card carries — the model's reading of one client
 *  conversation, waiting to be authorized. `classification: null` means the
 *  model had no answer, and the card asks for the word. */
export interface ClientWorkProposedApi {
  threadId: string;
  partyId: string;
  partyName: string;
  bucketKind: string;
  bucketName: string;
  title: string;
  classification: 'bug' | 'enhancement' | null;
  rationale: string | null;
  readsAsWork: boolean | null;
  quote: string;
  model: string | null;
}

/** POST /api/buckets/items/:threadId/work — B18e. Stages a card in the pen
 *  and creates NOTHING; `alreadyPending` says a card was already waiting
 *  rather than a second one being made. */
export async function proposeClientWork(
  tenantId: string,
  threadId: string
): Promise<{ proposalId: string; alreadyPending: boolean; proposed: ClientWorkProposedApi }> {
  return request(`/api/buckets/items/${encodeURIComponent(threadId)}/work`, { method: 'POST', tenantId });
}

export interface ClientWorkDecisionApi {
  proposalId: string;
  taskId: string;
  classification: 'bug' | 'enhancement';
  title: string;
  edited: boolean;
}

/** POST /api/proposals/:id/approve on a client-work card — the task is born
 *  classified, carrying its conversation. Passing the other word is the
 *  reclassify, and it is the same single tap as approving. */
export async function approveClientWork(
  tenantId: string,
  proposalId: string,
  decision: { classification?: 'bug' | 'enhancement'; title?: string } = {}
): Promise<{ proposalId: string; work: ClientWorkDecisionApi }> {
  return request(`/api/proposals/${proposalId}/approve`, { method: 'POST', tenantId, body: decision });
}

/* ------------------------------------------------------------------ *
 * B18e — bug or enhancement. A LEDGER: `classifyTask` appends, and the
 * history is what a client is shown when their bug is re-read.
 * ------------------------------------------------------------------ */

export interface ClassificationRowApi {
  id: string;
  taskId: string;
  classification: 'bug' | 'enhancement';
  reason: string | null;
  sourceThreadId: string | null;
  createdAt: string;
}

/** POST /api/tasks/:id/classification — classify, or RE-classify. Appends;
 *  the previous call and its reason survive. */
export async function classifyTask(
  tenantId: string,
  taskId: string,
  args: { classification: 'bug' | 'enhancement'; reason?: string }
): Promise<ClassificationRowApi> {
  const result = await request<{ classification: ClassificationRowApi }>(
    `/api/tasks/${taskId}/classification`,
    { method: 'POST', tenantId, body: args }
  );
  return result.classification;
}

/** GET /api/tasks/:id/classification — the whole story, never only the
 *  current word. */
export async function getTaskClassification(
  tenantId: string,
  taskId: string
): Promise<{ current: 'bug' | 'enhancement' | null; history: ClassificationRowApi[] }> {
  return request(`/api/tasks/${taskId}/classification`, { tenantId });
}

// ---------------------------------------------------------------------------
// THE COACH SURFACE (rung 1) — /api/pro/*
//
// ⚠ EVERY CALL HERE IS DELIBERATELY TENANT-FREE. A coach belongs to no
// household, so no call in this section may ever pass `tenantId` to
// request() — doing so sets X-Tenant-Id, and the server REFUSES /api/pro
// requests carrying one (400 tenant_not_accepted) rather than ignoring it.
// That refusal is the guard; this comment is only the reason.
//
// Unlike the reviewer portal above, these ARE session-authed — the coach is
// signed in. Same origin, same cookie, no tenant.
// ---------------------------------------------------------------------------

export interface CoachProfileApi {
  userId: string;
  displayName: string;
  createdAt: string;
  /** Whether a "send me your tape" link exists (rung 4). Never the hash. */
  hasIntakeLink: boolean;
  intakeRevokedAt: string | null;
}

/** GET /api/pro/me — the signed-in user's coach profile, or null.
 *
 * ⚠ null is a STATE, not an error: every parent on the product is not a
 * coach and gets exactly this. Callers must render the "become a coach"
 * step, never an error. */
export async function getCoachMe(): Promise<CoachProfileApi | null> {
  const result = await request<{ coach: CoachProfileApi | null }>('/api/pro/me');
  return result.coach;
}

/** POST /api/pro/profile — become a coach, or rename yourself. Idempotent. */
export async function saveCoachProfile(displayName: string): Promise<CoachProfileApi> {
  const result = await request<{ coach: CoachProfileApi }>('/api/pro/profile', {
    method: 'POST',
    body: { displayName },
  });
  return result.coach;
}

// ---------------------------------------------------------------------------
// THE PLAN — what this household's tier includes.
//
// Spec: `spec-calltowrap-s-plan-model-three-tiers-storage-as-the-meter-email-as-the-ladde`
//
// ⚠ THIS IS A HINT, NEVER A GATE. The real gate is `requireFeature` on the
// server, at the write. This exists so a screen does not OFFER what the tier
// does not include — a button that always errors reads as broken, not as an
// upsell, and framework §5's "soft + LOUD… a named banner" is impossible
// without knowing the tier.
// ---------------------------------------------------------------------------

/** Every feature key a tier can switch on. Mirrors core's FeatureKey — the
 * server sends whatever it resolved, so an unknown string here is a
 * client that is behind, not an error. */
export type PlanFeature = string;

export interface PlanApi {
  key: string;
  /** The tier's identity — the story, not the SKU ("the craft, developed"). */
  identity: string;
  features: PlanFeature[];
  /**
   * ⚑ WHOSE NAME THIS HOUSEHOLD'S DOCUMENTS WEAR (P4, `2f377423`).
   *
   * The household's OWN name when their tier includes Pro's de-branding —
   * "Your name, not ours". `null` on every other tier, and on a tier the
   * server could not resolve: the fallback is the PRODUCT's name, because
   * guessing the other way hands out a paid feature. Same contract as
   * `ReviewPortalContext.brand`, deliberately.
   *
   * ⚠ A SCREEN ASKS THIS, NOT `planHas(plan, 'branding.debranded')`. The tier
   * and the household's name are one answer here; deriving the flag on the
   * client and fetching the name from somewhere else is two facts that drift.
   */
  brand: string | null;
  limits: {
    storageBytes: number;
    /** ⚠ 0 means UNLIMITED. Read `performersUnlimited` instead of testing 0 —
     * treating 0 as a cap would tell a paying household it may have no
     * performers. */
    performersMax: number;
    performersUnlimited: boolean;
    /** ⚠ 0 means FOREVER, for the same reason. */
    retentionDays: number;
    retentionForever: boolean;
  };
  /** The meter of the product. capBytes is EFFECTIVE — performer multiplier
   * and any purchased add-on already included, the same number the server
   * refuses uploads against. */
  storage: {
    usedBytes: number;
    capBytes: number;
  };
  /** ⚠ NULL when the deployment's pack meters nothing — read that as "no cap
   * to show", never as zero remaining. */
  usage: {
    unitLabel: string;
    unitLabelPlural: string;
    used: number;
    /** The question a screen actually asks; already accounts for unlimited. */
    canAddAnother: boolean;
  } | null;
  /** B1 — what this household is paying, and what the banner says. Rides the
   * SAME payload as the tier deliberately (see the server plugin's own note):
   * `past_due` and `active` are the same tier, and the difference between them
   * is the whole banner. */
  billing: BillingApi;
}

/** ⚑ `none` IS NOT A LAPSE. A free household has no subscription and must
 * never be shown a billing banner — plan model §2: Free is a tier that just
 * sits there, not a trial that expires. */
export type BillingStatus =
  | 'none'
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceling'
  | 'lapsed'
  | 'incomplete';

export interface BillingApi {
  status: BillingStatus;
  /** 'month' | 'year', or null when nothing is subscribed. */
  interval: 'month' | 'year' | null;
  /** ISO. When the current paid period runs out — the date a `canceling`
   * household keeps its tier until. */
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** +100 GB units bought (`2f5ee92e`), and what they grant. */
  addonUnits: number;
  addonBytes: number;
  /** True when there is a Stripe customer to open the Manage door for. */
  manageable: boolean;
  /** ⚠ FALSE MEANS DO NOT OFFER, never "the purchase failed". True only where
   * this deployment has Stripe configured — which is nowhere, yet. */
  available: boolean;
  /** Whether this tier may buy storage at all. */
  canBuyAddon: boolean;
  /** True when this household carries beta-invite provenance (`e3e6b965`'s
   * founding lock). What lets a screen say the founding rate applies BEFORE
   * Checkout, rather than letting a family discover the discount there.
   *
   * Optional because it is the newest field on this payload and a client can
   * meet a server that predates it — absent reads as "no founding lock",
   * which is the safe direction: it withholds a claim rather than making one. */
  isFoundingInvitee?: boolean;
  /** The block size one purchased storage unit adds. Optional for the same
   * server-age reason; absent, a screen falls back to naming no amount. */
  addonUnitBytes?: number;
}

/** GET /api/plan — tenant-scoped. */
export async function getPlan(tenantId: string): Promise<PlanApi> {
  const result = await request<{ plan: PlanApi }>('/api/plan', { tenantId });
  return result.plan;
}

// ---------------------------------------------------------------------------
// RETENTION — the 90-day ritual (F3, plan model §3).
//
// ⚑ ONLY A PERSON DESTROYS A FILE, NEVER A CLOCK. Nothing in the app removes
// anything on its own; the state below is DERIVED from a file's age against
// the household's tier, and the only removal is `chooseRetention('delete')`
// with ids a parent itemised and confirmed.
// ---------------------------------------------------------------------------

/** fresh — nothing approaching · warned — a choice is coming · due — it is
 * here, and the file is waiting, untouched, for a person. */
export type RetentionState = 'fresh' | 'warned' | 'due';

export interface RetentionFileApi {
  fileId: string;
  originalFilename: string | null;
  mimeType: string | null;
  mediaClass: string | null;
  sizeBytes: number;
  createdAt: string;
  anchorAt: string;
  noticeSentAt: string | null;
  ageDays: number;
  state: RetentionState;
}

export interface RetentionApi {
  /** ⚠ 0 means FOREVER — read `forever`, never this. */
  retentionDays: number;
  forever: boolean;
  warnDays: number;
  storage: { usedBytes: number; capBytes: number };
  /** ⚑ THE ANSWER, ALREADY DECIDED SERVER-SIDE. Never re-derive it from
   * used-vs-cap: core is what actually refuses, and a second derivation is
   * how a screen ends up offering a button that always errors. */
  canRecycle: boolean;
  counts: { fresh: number; warned: number; due: number };
  bytes: { warned: number; due: number };
  /** ⚠ WARNED AND DUE ONLY. A fresh file needs no badge and a free household
   * may hold thousands of them; `counts` is where "nothing is approaching"
   * is read from, never this array's emptiness alone. */
  files: RetentionFileApi[];
}

/** GET /api/retention — tenant-scoped. A tier that keeps uploads forever
 * answers `forever: true` with an empty list, so a surface needs no tier
 * name of its own to stay free-tier-only. */
export async function getRetention(tenantId: string): Promise<RetentionApi> {
  const result = await request<{ retention: RetentionApi }>('/api/retention', { tenantId });
  return result.retention;
}

export interface RetentionChoiceResult {
  choice: 'recycle' | 'delete';
  recycledFileIds?: string[];
  deletedFileIds?: string[];
  freedBytes?: number;
  unknownFileIds: string[];
  storage: { usedBytes: number; capBytes: number };
}

/**
 * POST /api/retention/choice — the parent's decision on an itemised batch.
 *
 * `recycle` restarts the clock and touches no bytes. It is REFUSED at or
 * over the storage cap with `ApiError.code === 'recycle_over_quota'` — not a
 * permission problem, a full household — and the surface owes them the two
 * doors that are still open rather than a generic failure.
 *
 * `delete` requires `confirm: true` and is the one-way door. The server
 * refuses without it (`confirmation_required`), so a mis-wired retry cannot
 * clear a household's material by omission.
 */
export async function chooseRetention(
  tenantId: string,
  input: { choice: 'recycle'; fileIds: string[] } | { choice: 'delete'; fileIds: string[]; confirm: true }
): Promise<RetentionChoiceResult> {
  return request<RetentionChoiceResult>('/api/retention/choice', {
    method: 'POST',
    tenantId,
    body: input,
  });
}

// ---------------------------------------------------------------------------
// BILLING — the two doors (B1).
//
// ⚠ NEITHER IS A GATE. The server gates both on the membership's role
// (owner/admin) and on `storage.addon` for the add-on; these exist so a screen
// can OPEN a hosted page, and so it does not offer one where billing is not
// configured (`plan.billing.available`).
//
// ⚑ AND NEITHER RENDERS A CARD FIELD. Both answer with a URL on Stripe's own
// domain, which is the entire payment surface — no card number ever reaches
// this application.
// ---------------------------------------------------------------------------

/**
 * POST /api/billing/checkout — a hosted Stripe Checkout page for this purchase.
 *
 * `extraPerformers` is performers BEYOND THE FIRST (plan model §2's +50%), not
 * the household's total. `addonUnits` is +100 GB units (`2f5ee92e`); asking for
 * any on a tier without `storage.addon` is refused with
 * `ApiError.code === 'feature_not_in_plan'` — the family buys the tier first.
 */
export async function createBillingCheckout(
  tenantId: string,
  input: {
    tier: 'regular' | 'pro';
    interval: 'month' | 'year';
    extraPerformers?: number;
    addonUnits?: number;
  }
): Promise<{ checkoutUrl: string; sessionId: string }> {
  return request<{ checkoutUrl: string; sessionId: string }>('/api/billing/checkout', {
    method: 'POST',
    tenantId,
    body: input,
  });
}

/**
 * POST /api/billing/change — move an EXISTING subscription.
 *
 * ⚠ NOT THE SAME DOOR AS CHECKOUT, AND THE TWO REFUSE EACH OTHER. Checkout
 * 409s (`subscription_exists`) for a household that already pays, and this
 * one 409s (`no_subscription`) for a household that does not — so a screen
 * reads `plan.billing.status` and picks. Tier, interval and add-on units all
 * move here; the extra-performer quantity is the server's own count and is
 * never sent.
 */
export async function changeBillingPlan(
  tenantId: string,
  input: {
    tier: 'regular' | 'pro';
    interval: 'month' | 'year';
    addonUnits?: number;
  }
): Promise<{
  changed: boolean;
  planKey: string;
  status: string;
  tier: string;
  interval: string;
  extraPerformers: number;
  addonUnits: number;
}> {
  return request('/api/billing/change', { method: 'POST', tenantId, body: input });
}

/**
 * POST /api/billing/portal — Stripe's own billing portal.
 *
 * ⚠ 404 (`no_billing_account`) IS A REAL ANSWER, NOT A FAULT: plan model §7
 * means a household that has never paid has no Stripe customer and nothing to
 * manage. Read `plan.billing.manageable` first so this only ever fires where
 * the door is genuinely there.
 */
export async function openBillingPortal(tenantId: string): Promise<{ portalUrl: string }> {
  return request<{ portalUrl: string }>('/api/billing/portal', { method: 'POST', tenantId });
}

/** Does this household's tier include the feature?
 *
 * Defaults to FALSE when the plan is not loaded yet — the safe direction. A
 * screen that assumes yes while loading flashes a control the family may not
 * have, which is worse than a beat of absence. */
export function planHas(plan: PlanApi | null, feature: PlanFeature): boolean {
  return plan?.features.includes(feature) ?? false;
}

/**
 * GET /api/mailbox — the household's own inbound address, or null.
 *
 * ⚠ NULL MEANS SHOW NOTHING. It is returned when this deployment has no
 * inbound mail wired, or the household has no usable slug. A screen must not
 * substitute a placeholder: a printed address that bounces is worse than no
 * address, because a parent hands it to casting and then hears nothing back.
 *
 * The string is composed SERVER-side (`inboundAddressFor`) beside the pattern
 * the ingest path parses — the client never assembles it, so the two cannot
 * drift.
 */
export async function getMailbox(tenantId: string): Promise<string | null> {
  const result = await request<{ address: string | null }>('/api/mailbox', { tenantId });
  return result.address;
}

/**
 * PATCH /api/mailbox — settle the household's inbound handle.
 *
 * ⚠ ONLY WHILE SETTING UP. The server refuses with `address_settled` once the
 * owner has finished first run (Huan, 2026-08-28: "Once set it shouldn't be
 * changeable in settings"), so no screen outside onboarding should offer this.
 * `slug_taken` and `invalid_slug` are the two a person can act on.
 */
export async function setMailboxSlug(tenantId: string, slug: string): Promise<string | null> {
  const result = await request<{ address: string | null }>('/api/mailbox', {
    method: 'PATCH',
    tenantId,
    body: { slug },
  });
  return result.address;
}

// ---------------------------------------------------------------------------
// W1 — THE WORK ADDRESS (decision `80350c67`, plan model §4: email is the
// ladder).
//
// ⚑ THIS IS NOT `getMailbox` ABOVE. That one answers the household's INBOUND
// handle (`<slug>@calltowrap.com`), free on every tier, a forwarding route
// and not a mailbox. This one answers the WORK ADDRESS — the SAME slug on a
// DIFFERENT domain (`<slug>@mail.calltowrap.com`), Working and up, a real account with IMAP and
// SMTP that the family may hand to a casting director and that the app sends
// from when a draft is approved.
//
// ⚠ EVERY ROUTE HERE 404s ON A DEPLOYMENT WITH NO MAILBOX BROKER, which is
// every deployment except CallToWrap. A screen must read `planHas(plan,
// 'email.outbound')` first and simply not offer the door — the 404 is the belt
// behind the braces, never something a family should meet.
//
// ⚠ AND THE PASSWORD IS SHOWN ONCE. It comes back on the two POSTs below and
// on nothing else, ever; there is no read verb that returns it. A screen that
// discards it without showing it has lost it for good — the only recovery is a
// reset, which mints a different one.
// ---------------------------------------------------------------------------

export interface WorkAddressApi {
  address: string;
  imapHost: string;
  imapPort: number;
  smtpHost: string;
  smtpPort: number;
  /** ⚠ 'unusable' MEANS SENDING IS OFF. The mail host refused the stored
   * password, so approvals will not send until it is reset. Show
   * `stateReason` — a family cannot act on a state name. */
  state: 'active' | 'unusable';
  stateReason: string | null;
  provisionedAt: string;
  lastResetAt: string | null;
}

export interface WorkAddressStatusApi {
  /** ⚠ NULL MEANS NOT SET UP YET — a door to open, never an error. */
  mailbox: WorkAddressApi | null;
  /** False when the household has no address handle at all (`996b486`: a name
   * in a non-Latin script folds to nothing and no slug is invented). Offering
   * the button anyway would produce a refusal the family could do nothing
   * about. */
  slugAvailable: boolean;
}

/** The one crossing: the address AND the password, together, once. */
export interface WorkAddressCredentialApi {
  mailbox: WorkAddressApi;
  password: string;
}

/** GET /api/work-address */
export async function getWorkAddress(tenantId: string): Promise<WorkAddressStatusApi> {
  return request<WorkAddressStatusApi>('/api/work-address', { tenantId });
}

/** POST /api/work-address — create the mailbox on the mail host.
 *
 * ⚠ THE RESPONSE IS THE ONLY TIME THE PASSWORD EXISTS ON THIS CLIENT. Render
 * it before anything can navigate away. 409 `already_provisioned` means the
 * household already has one and the reset door is the right one. */
export async function provisionWorkAddress(tenantId: string): Promise<WorkAddressCredentialApi> {
  return request<WorkAddressCredentialApi>('/api/work-address', { method: 'POST', tenantId });
}

/** POST /api/work-address/password — mint a new password for the SAME
 * mailbox. Same one-crossing rule as provisioning. */
export async function resetWorkAddressPassword(tenantId: string): Promise<WorkAddressCredentialApi> {
  return request<WorkAddressCredentialApi>('/api/work-address/password', { method: 'POST', tenantId });
}

// ---------------------------------------------------------------------------
// P5 — THE MAILBOX THE FAMILY ALREADY HAD (plan model §6, `f7de88d6`).
//
// ⚑ THIS IS THE THIRD ADDRESS AND IT IS NOT EITHER OF THE OTHER TWO.
// `getMailbox` answers the free INBOUND handle (`<slug>@calltowrap.com`, a
// forwarding route). `getWorkAddress` answers the WORK ADDRESS
// (`<slug>@mail.calltowrap.com`, Working and up, the one we SEND from). This
// one answers a mailbox on somebody else's host that the household connected
// so their agents can keep mailing the address they always used.
//
// ⚑ READ-ONLY, FOREVER. There is no send verb here and there is none on the
// server either. Nothing ever leaves through a connected mailbox.
//
// ⚠ THE PASSWORD GOES ONE WAY ONLY. It is sent once, on connect, and no
// response ever carries it back — unlike the work address, where the product
// minted the credential and had to show it once. This one is theirs already.
// ---------------------------------------------------------------------------

export interface ByoMailboxApi {
  address: string;
  imapHost: string;
  imapPort: number;
  /** ⚠ 'unusable' MEANS READING HAS STOPPED. The provider refused the stored
   * app password. Show `stateReason` — a family cannot act on a state name. */
  state: 'active' | 'unusable';
  stateReason: string | null;
  /** Null until the first poll: a family who connects at 10:01 should read
   * "not yet", never a fabricated timestamp. */
  lastPolledAt: string | null;
  /** What went wrong last time, if anything did. Cleared by the next clean
   * poll — a stale error line teaches a family to ignore the line. */
  lastError: string | null;
  messagesIngested: number;
  connectedAt: string;
}

/* ---------------------------------------------------------------- *
 * P6.1's missing door (2026-09-01) — the per-user ICS calendar feed.
 * Server: plugins/ics.ts. The raw token comes back ONCE from mint and
 * is never retrievable again; status is what a reloaded screen reads.
 * ---------------------------------------------------------------- */

export interface IcsFeedStatusApi {
  active: boolean;
  createdAt: string | null;
  /** Bumped every time a calendar app fetches the feed — "null while
   * active" means created but never pulled yet. */
  lastUsedAt: string | null;
}

export async function getIcsFeedStatus(tenantId: string): Promise<IcsFeedStatusApi> {
  return request<IcsFeedStatusApi>('/api/ics/token', { tenantId });
}

export interface IcsFeedMintApi {
  token: string;
  feedUrl: string;
}

/** Mints (or replaces — the server revokes any prior token first) the
 * caller's feed link. baseUrl is this origin: the server builds the full
 * URL so what the person copies is exactly what their calendar will GET. */
export async function mintIcsFeed(tenantId: string): Promise<IcsFeedMintApi> {
  return request<IcsFeedMintApi>('/api/ics/token', {
    method: 'POST',
    tenantId,
    body: { baseUrl: window.location.origin },
  });
}

export async function revokeIcsFeed(tenantId: string): Promise<void> {
  await request('/api/ics/token/revoke', { method: 'POST', tenantId });
}

export interface ByoMailboxStatusApi {
  /** ⚠ NULL MEANS NOTHING IS CONNECTED — a door to open, never an error. */
  mailbox: ByoMailboxApi | null;
  /** Whether we are actually reading it right now. False for three different
   * reasons and `stoppedReason` says which. */
  polling: boolean;
  stoppedReason: string | null;
  /** Whether the CONNECT door would be accepted, so a surface can offer the
   * form without first earning a 403 to find out. */
  connectable: boolean;
}

/** GET /api/byo-mailbox */
export async function getByoMailbox(tenantId: string): Promise<ByoMailboxStatusApi> {
  return request<ByoMailboxStatusApi>('/api/byo-mailbox', { tenantId });
}

/** POST /api/byo-mailbox — connect an existing mailbox.
 *
 * ⚠ THE SERVER PROVES THE LOGIN BEFORE IT STORES ANYTHING, so a rejection here
 * means nothing was saved and the family can simply fix what they typed. 422
 * `mailbox_refused` is the app-password case and its message says so.
 *
 * ⚑ `imapPort` TAKES THE FIELD'S OWN STRING (2026-09-16, the Day 3–4 walk,
 * item 7). It used to take a number, so the screen ran `Number(draft) || 993`
 * to satisfy the type — and that `||` turned "notaport" into 993 and opened a
 * connection to it. The server has always refused a bad port in its own words
 * ("that is not a port number", byoMailbox.ts's readConnectBody, which also
 * reads '' as the default), so the fix is to stop the client inventing a value
 * and let the one refusal that exists do its job. */
export async function connectByoMailbox(
  tenantId: string,
  input: { address: string; imapHost: string; imapPort?: string | number; password: string }
): Promise<ByoMailboxStatusApi> {
  return request<ByoMailboxStatusApi>('/api/byo-mailbox', { method: 'POST', tenantId, body: input });
}

/** DELETE /api/byo-mailbox — stop reading, and destroy the stored credential.
 * ⚑ Mail that already arrived stays: disconnecting ends the reading, nothing
 * else. */
export async function disconnectByoMailbox(tenantId: string): Promise<void> {
  await request<{ disconnected: boolean }>('/api/byo-mailbox', { method: 'DELETE', tenantId });
}

/** One tape waiting for this coach's notes. */
export interface CoachQueueItemApi {
  grantId: string;
  /** When the link stops working. The queue is ordered by this, soonest
   * first — a review request is an obligation with a DATE. */
  expiresAt: string;
  role: string | null;
  project: string | null;
  tapePresent: boolean;
  alreadySubmitted: boolean;
}

/** GET /api/pro/queue — tapes awaiting notes, across every household that
 * granted to this coach. Tenant-free like the rest of /api/pro. */
export async function getCoachQueue(): Promise<{ items: CoachQueueItemApi[]; pageSize: number }> {
  return request('/api/pro/queue');
}

/** One note this coach wrote, as THEY recorded it. */
export interface CoachHistoryEntryApi {
  id: string;
  reviewedAt: string;
  student?: string | null;
  project: string | null;
  role: string | null;
  notes: string;
}

/** GET /api/pro/history — everything this coach has written, newest first. */
export async function getCoachHistory(): Promise<{ entries: CoachHistoryEntryApi[] }> {
  return request('/api/pro/history');
}

/** GET /api/pro/students/:label — one student's history.
 *
 * ⚠ Reads `coach_review_log` ALONE on the server, which is what makes it keep
 * working after every grant has lapsed and after a household has left. A view
 * assembled from live grants would empty itself over time, invisibly. */
export async function getCoachStudent(
  label: string
): Promise<{ student: string; entries: CoachHistoryEntryApi[] }> {
  return request(`/api/pro/students/${encodeURIComponent(label)}`);
}

/** One student's line on the coach's report. */
export interface CoachReportRowApi {
  student: string | null;
  reviews: number;
  firstReviewedAt: string;
  lastReviewedAt: string;
}

/** GET /api/pro/report — reviews completed, grouped by student.
 *
 * ⚠ A COUNT, NEVER AN INVOICE (spec §9). There is no rate, amount or total in
 * this payload beyond the sum of the same rows, and adding one is a product
 * decision: billing a coach's students is tenant→client money, System 2,
 * deferred. We count; they invoice. */
export async function getCoachReport(range: { from?: string; to?: string } = {}): Promise<{
  rows: CoachReportRowApi[];
  totalReviews: number;
}> {
  const q = new URLSearchParams();
  if (range.from) q.set('from', range.from);
  if (range.to) q.set('to', range.to);
  const suffix = q.toString() ? `?${q}` : '';
  return request(`/api/pro/report${suffix}`);
}

/** The same numbers as a download. A plain link, not a fetch: letting the
 * browser do it keeps the Content-Disposition filename the server chose, which
 * a blob download would throw away. */
export function coachReportCsvHref(range: { from?: string; to?: string } = {}): string {
  const q = new URLSearchParams({ format: 'csv' });
  if (range.from) q.set('from', range.from);
  if (range.to) q.set('to', range.to);
  return `/api/pro/report?${q}`;
}

// ---------------------------------------------------------------------------
// THE COACH'S INTAKE HANDLE (rung 4) — decision `828b8687`.
//
// ⚑ A PUBLIC HANDLE ON A REAL PATH, not a token on a hash. `/c/dana-reyes` is
// meant to be printed, read aloud and typed; the per-tape reviewer links stay
// hash-routed because those ARE capabilities and a fragment never reaches a
// server. The rule: path for a handle, hash for a capability.
// ---------------------------------------------------------------------------

export interface CoachIntakeApi {
  slug: string | null;
  revoked: boolean;
  /** A PATH, never an absolute URL — the host is whatever the request arrived
   * on, and baking one in is how a link ends up pointing at staging. */
  path: string | null;
}

/** GET /api/pro/intake — the coach's own handle. */
export async function getCoachIntake(): Promise<CoachIntakeApi> {
  return request('/api/pro/intake');
}

/** POST /api/pro/intake — claim a handle, or change it. Omit `slug` to have
 * one proposed from the display name. */
export async function claimCoachIntake(slug?: string): Promise<{ slug: string; path: string; wasSuffixed: boolean }> {
  return request('/api/pro/intake', { method: 'POST', body: slug ? { slug } : {} });
}

/** POST /api/pro/intake/revoke — turn the link off, or back on.
 * ⚠ Never revokes grants already made from it (spec §7). */
export async function setCoachIntakeRevoked(revoked: boolean): Promise<{ revoked: boolean }> {
  return request('/api/pro/intake/revoke', { method: 'POST', body: { revoked } });
}

// ---------------------------------------------------------------------------
// R3b / R3b-2 — THE AI RADAR'S WIRES. Shapes mirror packages/server/src/
// plugins/radar.ts's serializers exactly (that file is the authority — see
// its own header: the tools grew a cadence/notify writer, a full last_run
// object plus verdict_counts on the topics list, next_due_at on every
// topic, a clusters count on every run, and immediate-command receipts).
// ---------------------------------------------------------------------------

export interface RadarLastRunApi {
  kind: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  sourcesPolled: number | null;
  sourcesFailed: number | null;
  sourcesStale: number | null;
  staleNames: string[] | null;
  // R5 (C) — sources the per-class cadence chose not to poll this run,
  // never counted as failed (see lib/radarFreshness.ts).
  sourcesSkipped: number | null;
  skippedNames: string[] | null;
  seen: number | null;
  clusters: number | null;
  triaged: number | null;
  shown: number | null;
  error: string | null;
}

export interface RadarVerdictCountsApi {
  adopt: number;
  adapt: number;
  watch: number;
  skip: number;
}

// R3b-3 (P2/C1): the newest queued/running radar_commands row for a topic,
// or null — appears the instant "Research now" is tapped, before any run
// row exists at all (the runner only picks a queued command up on its own
// poll, so a run row can lag the tap by minutes). Read by lib/radarFreshness's
// isResearching.
export interface RadarPendingCommandApi {
  id: string;
  kind: string;
  status: string;
  requestedAt: string | null;
  startedAt: string | null;
}

export interface RadarTopicListApi {
  slug: string;
  name: string;
  state: string;
  cadence: Record<string, unknown>;
  nextDueAt: string | null;
  newSince: number;
  proposalsProposed: number;
  verdictCounts: RadarVerdictCountsApi;
  lastPolledAt: string | null;
  lastViewedAt: string | null;
  lastRun: RadarLastRunApi | null;
  // R3b-3 (P1/C1): the freshness line's real source — see lib/radarFreshness's
  // topicFreshnessLine. `lastRun` stays for the failed-run line only.
  lastDiscover: RadarLastRunApi | null;
  lastReview: RadarLastRunApi | null;
  pendingCommand: RadarPendingCommandApi | null;
}

// R3b-3 — reconciled against pythia-eden's actual `topic_get`/`topic_write`
// shapes (src/tools/radar.ts, commit 20565a8): `pending_command` (topic_get
// only) rides as a SIBLING of `topic`, never nested inside it — see
// getRadarTopic below, whose return type carries it at the top level.
export interface RadarTopicApi {
  slug: string;
  name: string;
  consequenceMd: string;
  queries: string[];
  exclusions: string[];
  geo: string | null;
  languages: string[] | null;
  state: string;
  cadence: Record<string, unknown>;
  notify: boolean | null;
  nextDueAt: string | null;
  lastPolledAt: string | null;
  lastViewedAt: string | null;
}

export interface RadarSourceApi {
  id: string;
  kind: string;
  name: string;
  state: string;
  addedOn: string;
  addedBecause: string;
  lastItemAt: string | null;
  lastOkAt: string | null;
  lastError: string | null;
  // R3d (P2/C2) — the sources rail's own fields (see plugins/radar.ts's
  // serializeSource, the authority for this shape).
  lastItemTitle: string | null;
  lastItemUrl: string | null;
  items30d: number;
  url: string | null;
  // R5 (C) — who put the row there ('seed' | 'human' | 'discovery'), when
  // it entered probation (= addedOn while state is 'probation', else
  // null), and the 30-day accepted count promotion/demotion reads.
  addedBy: 'seed' | 'human' | 'discovery';
  probationSince: string | null;
  accepted30d: number;
}

// R5 (C) — "found, not yet trusted" (pythia_radar_source_candidates):
// hosts/channels/profiles the citation harvest has seen cited but not yet
// admitted as a real source.
export interface RadarSourceCandidateApi {
  id: string;
  kind: string;
  name: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  citingGroups: string[];
  citations: number;
  queryHits: number;
  originHits: number;
  state: string;
  probeUrl: string | null;
  probeError: string | null;
}

export interface RadarRunApi {
  id: string;
  kind: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  sourcesPolled: number | null;
  sourcesFailed: number | null;
  sourcesStale: number | null;
  staleNames: string[] | null;
  // R5 (C) — same per-class-cadence fields as RadarLastRunApi above.
  sourcesSkipped: number | null;
  skippedNames: string[] | null;
  seen: number | null;
  clusters: number | null;
  triaged: number | null;
  shown: number | null;
  error: string | null;
  // R3d (P3/C3) — "earlier days"' own field: the wiki page review() wrote
  // for this run, or null.
  pageSlug: string | null;
}

export interface RadarItemApi {
  id: string;
  title: string | null;
  url: string;
  publishedAt: string | null;
  firstSeenAt: string;
  verdict: string | null;
  readingMd: string | null;
  status: string | null;
  fileId: string | null;
  sourceId: string | null;
  relevance: number | null;
  // R3d (P1/C1) — the kind chip's own fields: 'article'/'video'/'audio'
  // (or null against a build that predates the widening) and the item's
  // declared runtime in seconds, or null.
  kind: string | null;
  durationSeconds: number | null;
}

export interface RadarProposalApi {
  id: string;
  topicId: string | null;
  title: string;
  bodyMd: string;
  fromItemIds: string[];
  status: string | null;
  decidedAt: string | null;
  coreTaskRef: string | null;
  // R3d (P5/C5) — null means "raised by the review"; 'huan' means raised
  // straight from an item (the "raised by you" chip reads this).
  raisedBy: string | null;
}

export interface RadarCommandApi {
  id: string;
  status: string;
  receipt: string | null;
}

// R3d (P4/C4) — a typed hit from GET /api/radar/search, over proposals
// (title, body) and items (title). `status` is set for a proposal hit,
// `verdict` for an item hit — never both.
export interface RadarSearchHitApi {
  kind: 'proposal' | 'item';
  id: string;
  title: string;
  topicSlug: string | null;
  status: string | null;
  verdict: string | null;
  decidedAt: string | null;
}

export async function listRadarTopics(tenantId: string): Promise<RadarTopicListApi[]> {
  const r = await request<{ topics: RadarTopicListApi[] }>('/api/radar/topics', { tenantId });
  return r.topics;
}

export interface RadarTopicGetResult {
  topic: RadarTopicApi;
  sources: RadarSourceApi[];
  runs: RadarRunApi[];
  lastDiscover: RadarLastRunApi | null;
  lastReview: RadarLastRunApi | null;
  pendingCommand: RadarPendingCommandApi | null;
  // R5 (C) — the current month's source report wiki page, or null when
  // none has been written yet.
  sourceReportSlug: string | null;
}

export async function getRadarTopic(tenantId: string, slug: string): Promise<RadarTopicGetResult> {
  return request(`/api/radar/topics/${encodeURIComponent(slug)}`, { tenantId });
}

// R5 (C) — "found, not yet trusted": the candidates fold on a topic's
// sources rail, read-only.
export async function listRadarSourceCandidates(
  tenantId: string,
  slug: string,
  limit?: number
): Promise<RadarSourceCandidateApi[]> {
  const qs = limit ? `?limit=${limit}` : '';
  const r = await request<{ candidates: RadarSourceCandidateApi[] }>(
    `/api/radar/topics/${encodeURIComponent(slug)}/candidates${qs}`,
    { tenantId }
  );
  return r.candidates;
}

export async function listRadarItems(
  tenantId: string,
  slug: string,
  // R3d (P3/C3) — `runId` scopes "earlier days" to one run's own items.
  opts: { since?: string; status?: string; limit?: number; runId?: string } = {}
): Promise<RadarItemApi[]> {
  const qs = new URLSearchParams();
  if (opts.since) qs.set('since', opts.since);
  if (opts.status) qs.set('status', opts.status);
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.runId) qs.set('run', opts.runId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const r = await request<{ items: RadarItemApi[] }>(`/api/radar/topics/${encodeURIComponent(slug)}/items${suffix}`, { tenantId });
  return r.items;
}

export async function getRadarItem(
  tenantId: string,
  id: string
): Promise<{ item: RadarItemApi; source: RadarSourceApi | null }> {
  return request(`/api/radar/items/${id}`, { tenantId });
}

/** "Read our copy" — the item's kept file, via a signed R2 URL the server
 *  resolves and 302s to. A plain `<a href>` cannot carry X-Tenant-Id, so
 *  this fetches the redirect itself (following it) and opens the RESOLVED
 *  url in a tab opened synchronously on the click (before the await), so a
 *  browser's popup blocker sees it as a direct response to the click. */
export async function openRadarItemOriginal(tenantId: string, id: string): Promise<void> {
  const win = window.open('', '_blank', 'noopener');
  try {
    const resp = await fetch(`/api/radar/items/${id}/original`, {
      headers: { 'X-Tenant-Id': tenantId },
      credentials: 'same-origin',
      redirect: 'follow',
    });
    if (!resp.ok) throw extractError(resp.status, await resp.json().catch(() => null));
    if (win) win.location.href = resp.url;
  } catch (err) {
    win?.close();
    throw err;
  }
}

export async function listRadarProposals(
  tenantId: string,
  opts: { slug?: string; status?: string } = {}
): Promise<RadarProposalApi[]> {
  const qs = new URLSearchParams();
  if (opts.slug) qs.set('slug', opts.slug);
  if (opts.status) qs.set('status', opts.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const r = await request<{ proposals: RadarProposalApi[] }>(`/api/radar/proposals${suffix}`, { tenantId });
  return r.proposals;
}

export async function getRadarProposal(
  tenantId: string,
  id: string
): Promise<{ proposal: RadarProposalApi; items: Array<{ item: RadarItemApi | null; source: RadarSourceApi | null }> }> {
  return request(`/api/radar/proposals/${id}`, { tenantId });
}

export interface RadarTopicWriteArgs {
  name: string;
  consequenceMd: string;
  queries?: string[];
  exclusions?: string[];
  geo?: string;
  languages?: string[];
  state: 'tracking' | 'paused' | 'once' | 'archived';
  cadence?: { every: 'daily' | 'weekly' | 'monthly'; at: string; day?: number };
  notify?: boolean;
}

// R3b-3 (P4/C1): the write now carries the tool's own receipt too —
// "archived — <name>; its items and runs stay in the record" / "saved —
// <name>, <cadence in words>, next run <when>" — so a caller never composes
// that sentence itself. `null` when the Pythia build behind this deployment
// predates P4 (plugins/radar.ts's own fallback).
export interface RadarTopicWriteResult {
  topic: RadarTopicApi;
  receipt: string | null;
}

export async function createRadarTopic(tenantId: string, args: RadarTopicWriteArgs): Promise<RadarTopicWriteResult> {
  const r = await request<{ topic: RadarTopicApi; receipt: string | null }>('/api/radar/topics', { method: 'POST', tenantId, body: args });
  return { topic: r.topic, receipt: r.receipt ?? null };
}

export async function updateRadarTopic(tenantId: string, slug: string, args: RadarTopicWriteArgs): Promise<RadarTopicWriteResult> {
  const r = await request<{ topic: RadarTopicApi; receipt: string | null }>(`/api/radar/topics/${encodeURIComponent(slug)}`, {
    method: 'PUT', tenantId, body: args,
  });
  return { topic: r.topic, receipt: r.receipt ?? null };
}

export async function researchRadarTopicNow(tenantId: string, slug: string): Promise<RadarCommandApi> {
  const r = await request<{ command: RadarCommandApi }>(`/api/radar/topics/${encodeURIComponent(slug)}/research-now`, {
    method: 'POST', tenantId,
  });
  return r.command;
}

export async function setRadarTopicTracking(tenantId: string, slug: string, on: boolean): Promise<RadarCommandApi> {
  const r = await request<{ command: RadarCommandApi }>(`/api/radar/topics/${encodeURIComponent(slug)}/tracking`, {
    method: 'POST', tenantId, body: { on },
  });
  return r.command;
}

export async function approveRadarProposal(
  tenantId: string,
  id: string
): Promise<{ task: TaskApi; proposal: RadarProposalApi; receipt: string | null }> {
  return request(`/api/radar/proposals/${id}/approve`, { method: 'POST', tenantId });
}

export async function declineRadarProposal(tenantId: string, id: string): Promise<RadarCommandApi> {
  const r = await request<{ command: RadarCommandApi }>(`/api/radar/proposals/${id}/decline`, { method: 'POST', tenantId });
  return r.command;
}

export async function markRadarTopicViewed(tenantId: string, slug: string): Promise<void> {
  await request(`/api/radar/topics/${encodeURIComponent(slug)}/viewed`, { method: 'POST', tenantId });
}

// R3d (C4) — over proposals (title, body) and items (title), for the topics
// the caller can see.
export async function searchRadar(tenantId: string, q: string, limit?: number): Promise<RadarSearchHitApi[]> {
  const qs = new URLSearchParams({ q });
  if (limit) qs.set('limit', String(limit));
  const r = await request<{ hits: RadarSearchHitApi[] }>(`/api/radar/search?${qs.toString()}`, { tenantId });
  return r.hits;
}

// R3d (C4) — declined -> proposed; the tool's own "reopened —" receipt.
export async function reopenRadarProposal(
  tenantId: string,
  id: string
): Promise<{ command: RadarCommandApi; proposal: RadarProposalApi | null }> {
  return request(`/api/radar/proposals/${id}/reopen`, { method: 'POST', tenantId });
}

// R3d (C4) — approved -> proposed, and drops the Core to-do it named; the
// receipt is Pythia's own "withdrawn —" sentence, with a second sentence
// appended (still a real server sentence, never client-composed) only when
// the to-do drop itself failed.
export async function withdrawRadarProposal(
  tenantId: string,
  id: string
): Promise<{ command: RadarCommandApi; proposal: RadarProposalApi | null }> {
  return request(`/api/radar/proposals/${id}/withdraw`, { method: 'POST', tenantId });
}

// R3d (C2) — drop ('dormant') or restore ('active') a source; the tool's
// own receipt.
export async function setRadarSourceState(
  tenantId: string,
  id: string,
  state: 'active' | 'dormant'
): Promise<{ source: RadarSourceApi; receipt: string | null }> {
  return request(`/api/radar/sources/${id}/state`, { method: 'POST', tenantId, body: { state } });
}

// R3d (C5) — "Add to build list", on any item; the tool's own "added to
// Worth building —" receipt, and the created proposal.
// Reconciled against pythia-eden's actual raise_from_item handler (src/
// tools/radar.ts, commit 8105aeb): it settles like every other kind —
// {command_id, status:'done', receipt} — and never echoes the created
// proposal row; `proposal` is null in the ordinary case. The receipt text
// itself names the title, and the proposal appears in Waiting on the next
// list — never a second round trip to fetch it here.
export async function raiseRadarItem(
  tenantId: string,
  itemId: string,
  note?: string
): Promise<{ proposal: RadarProposalApi | null; receipt: string | null }> {
  return request(`/api/radar/items/${itemId}/raise`, { method: 'POST', tenantId, body: note ? { note } : {} });
}

// ─── newsletter (R3e C) — the queue, the issue, the store ──────────────────
// Topic-agnostic (spec: "the same queue serves any topic"); every mutating
// call answers the FRESH full queue plus the tool's own receipt, verbatim.

export interface RadarNewsletterItemApi {
  position: number;
  item: {
    id: string;
    kind: string | null;
    durationSeconds: number | null;
    title: string | null;
    url: string;
    sourceName: string;
    publishedAt: string | null;
    verdict: string | null;
  };
  readingExcerpt: string | null;
  note: string | null;
}

export interface RadarNewsletterIssueApi {
  id: string;
  title: string;
  status: 'draft' | 'published';
}

export interface RadarNewsletterQueueApi {
  issue: RadarNewsletterIssueApi;
  items: RadarNewsletterItemApi[];
  previewMd: string;
  receipt: string | null;
}

export interface RadarNewsletterIssueSummaryApi {
  id: string;
  title: string;
  publishedAt: string | null;
  noteSlug: string | null;
  fileId: string | null;
  itemCount: number;
}

export async function getRadarNewsletter(tenantId: string): Promise<RadarNewsletterQueueApi> {
  return request('/api/radar/newsletter', { tenantId });
}

export async function addToRadarNewsletter(
  tenantId: string,
  itemId: string,
  note?: string
): Promise<RadarNewsletterQueueApi> {
  return request('/api/radar/newsletter/items', { method: 'POST', tenantId, body: note ? { itemId, note } : { itemId } });
}

export async function removeFromRadarNewsletter(tenantId: string, itemId: string): Promise<RadarNewsletterQueueApi> {
  return request(`/api/radar/newsletter/items/${itemId}`, { method: 'DELETE', tenantId });
}

export async function reorderRadarNewsletter(tenantId: string, itemIds: string[]): Promise<RadarNewsletterQueueApi> {
  return request('/api/radar/newsletter/order', { method: 'PUT', tenantId, body: { itemIds } });
}

export async function setRadarNewsletterItemNote(
  tenantId: string,
  itemId: string,
  note: string
): Promise<RadarNewsletterQueueApi> {
  return request(`/api/radar/newsletter/items/${itemId}/note`, { method: 'PUT', tenantId, body: { note } });
}

// No mail, no recipients — who receives it is Publish's (GAP 53), a later
// decision. The answered queue is the fresh empty draft the tool just opened.
export async function publishRadarNewsletter(tenantId: string, title?: string): Promise<RadarNewsletterQueueApi> {
  return request('/api/radar/newsletter/publish', { method: 'POST', tenantId, body: title ? { title } : {} });
}

export async function listRadarNewsletterIssues(
  tenantId: string,
  limit?: number
): Promise<RadarNewsletterIssueSummaryApi[]> {
  const qs = limit ? `?limit=${limit}` : '';
  const r = await request<{ issues: RadarNewsletterIssueSummaryApi[] }>(`/api/radar/newsletter/issues${qs}`, { tenantId });
  return r.issues;
}

export async function getRadarNewsletterIssue(
  tenantId: string,
  id: string
): Promise<{ issue: RadarNewsletterIssueSummaryApi; title: string; bodyMd: string }> {
  return request(`/api/radar/newsletter/issues/${id}`, { tenantId });
}

// ---------------------------------------------------------------------------
// C1 (2026-09-28) — the Record window: what was generated on Huan's behalf,
// by whom, and the door to read or download it. Wire shapes mirror
// packages/server/src/plugins/record.ts's own serializers exactly (that
// file is the authority — see its own header for the "not found is 404,
// never 403" posture this door deliberately chooses, and console/
// pythiaClient.ts's own C1 header for why every shape here is the spec's
// own text rather than a reconciled observation: Part P (pythia-eden)
// carried no src/tools/record.ts yet when this was written).

export interface RecordActivityItemApi {
  kind: 'note' | 'file' | 'decision' | 'radar' | 'commit';
  id: string;
  slug: string | null;
  title: string;
  at: string;
  actor: string;
  what: string;
  summary: string | null;
  ref: string;
}

export interface RecordActorCountApi {
  actor: string;
  count: number;
}

export interface RecordActivityApi {
  items: RecordActivityItemApi[];
  actors: RecordActorCountApi[];
  nextUntil: string | null;
}

export type RecordBrowseAxisApi = 'kind' | 'month' | 'para' | 'note_type' | 'media_class';

export interface RecordBrowseBucketApi {
  value: string;
  label: string;
  count: number;
}

export interface RecordNoteRevisionApi {
  revision: number;
  editedBy: string;
  editSummary: string | null;
  createdAt: string;
}

export interface RecordFileApi {
  id: string;
  title: string;
  originalFilename: string | null;
  mediaClass: string | null;
  docType: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  createdAt: string;
  ingestedBy: string;
  sourceUri: string | null;
  summary: string | null;
  capsule: string | null;
  canonicalStatus: string | null;
  isStreamable: boolean;
  hasBytes: boolean;
  hasReadableText: boolean;
}

export interface RecordFileTextApi {
  text: string;
  truncated: boolean;
  totalChars: number;
}

export interface RecordReadingListItemApi {
  flagId: string;
  kind: 'note' | 'file' | 'decision';
  id: string;
  slug: string | null;
  title: string;
  why: string | null;
  flaggedBy: string;
  flaggedAt: string;
  readAt: string | null;
}

export interface RecordPageNoteApi {
  id: string;
  slug: string;
  title: string;
  bodyMd: string;
  updatedAt: string;
  noteType: string | null;
  paraCategory: string | null;
}

export interface RecordPageOutlinkApi {
  slug: string;
  title: string;
}

export interface RecordPageApi {
  note: RecordPageNoteApi;
  backlinks: RecordPageOutlinkApi[];
  outlinks: RecordPageOutlinkApi[];
  unresolvedLinks: string[];
  revisionCount: number;
}

export interface RecordSearchHitApi {
  kind: 'note' | 'file';
  id: string;
  slug: string | null;
  title: string;
  snippet: string | null;
  ref: string;
}

export interface RecordRegisterApi {
  slug: string;
  title: string | null;
  revision: number | null;
  updatedAt: string | null;
  error: string | null;
}

export async function getRecordActivity(
  tenantId: string,
  opts: { since?: string; until?: string; kinds?: string[]; actor?: string; limit?: number } = {}
): Promise<RecordActivityApi> {
  const qs = new URLSearchParams();
  if (opts.since) qs.set('since', opts.since);
  if (opts.until) qs.set('until', opts.until);
  if (opts.kinds && opts.kinds.length > 0) qs.set('kinds', opts.kinds.join(','));
  if (opts.actor) qs.set('actor', opts.actor);
  if (opts.limit) qs.set('limit', String(opts.limit));
  const suffix = qs.toString() ? `?${qs}` : '';
  return request(`/api/record/activity${suffix}`, { tenantId });
}

export async function searchRecord(tenantId: string, q: string, limit?: number): Promise<RecordSearchHitApi[]> {
  const qs = new URLSearchParams({ q });
  if (limit) qs.set('limit', String(limit));
  const r = await request<{ hits: RecordSearchHitApi[] }>(`/api/record/search?${qs}`, { tenantId });
  return r.hits;
}

export async function getRecordBrowseBuckets(
  tenantId: string,
  axis: RecordBrowseAxisApi
): Promise<RecordBrowseBucketApi[]> {
  const r = await request<{ buckets: RecordBrowseBucketApi[] }>(`/api/record/browse?axis=${axis}`, { tenantId });
  return r.buckets;
}

export async function getRecordBrowseItems(
  tenantId: string,
  axis: RecordBrowseAxisApi,
  value: string
): Promise<RecordActivityItemApi[]> {
  const r = await request<{ items: RecordActivityItemApi[] }>(
    `/api/record/browse?axis=${axis}&value=${encodeURIComponent(value)}`,
    { tenantId }
  );
  return r.items;
}

export async function getRecordPage(tenantId: string, slug: string): Promise<RecordPageApi> {
  return request(`/api/record/pages/${encodeURIComponent(slug)}`, { tenantId });
}

export async function getRecordPageRevisions(
  tenantId: string,
  slug: string,
  limit?: number
): Promise<RecordNoteRevisionApi[]> {
  const qs = limit ? `?limit=${limit}` : '';
  const r = await request<{ revisions: RecordNoteRevisionApi[] }>(
    `/api/record/pages/${encodeURIComponent(slug)}/revisions${qs}`,
    { tenantId }
  );
  return r.revisions;
}

export async function getRecordFile(tenantId: string, id: string): Promise<RecordFileApi> {
  const r = await request<{ file: RecordFileApi }>(`/api/record/files/${id}`, { tenantId });
  return r.file;
}

/** C1b item 2 ("Read here") — the file's extracted text, bounded. */
export async function getRecordFileText(tenantId: string, id: string): Promise<RecordFileTextApi> {
  return request(`/api/record/files/${id}/text`, { tenantId });
}

/** The download door — a 302 to a signed URL, same shape as the radar's
 *  own openRadarItemOriginal (see that function's own header): a plain
 *  `<a href>` cannot carry the X-Tenant-Id header this tenant-scoped door
 *  requires (tenant.ts's requireActiveTenantMembership), so this fetches
 *  the redirect itself (with the header, following it) and opens the
 *  RESOLVED url in a tab opened synchronously on the click — a popup
 *  blocker sees it as a direct response to the click. */
export async function openRecordFileDownload(tenantId: string, id: string): Promise<void> {
  const win = window.open('', '_blank', 'noopener');
  try {
    const resp = await fetch(`/api/record/files/${id}/download`, {
      headers: { 'X-Tenant-Id': tenantId },
      credentials: 'same-origin',
      redirect: 'follow',
    });
    if (!resp.ok) throw extractError(resp.status, await resp.json().catch(() => null));
    if (win) win.location.href = resp.url;
  } catch (err) {
    win?.close();
    throw err;
  }
}

export async function getRecordReadingList(tenantId: string, includeRead?: boolean): Promise<RecordReadingListItemApi[]> {
  const qs = includeRead ? '?include_read=true' : '';
  const r = await request<{ items: RecordReadingListItemApi[] }>(`/api/record/reading-list${qs}`, { tenantId });
  return r.items;
}

export async function markRecordFlagRead(tenantId: string, flagId: string): Promise<{ receipt: string }> {
  return request(`/api/record/reading-list/${flagId}/read`, { method: 'POST', tenantId });
}

export async function getRecordRegisters(tenantId: string): Promise<RecordRegisterApi[]> {
  const r = await request<{ registers: RecordRegisterApi[] }>('/api/record/registers', { tenantId });
  return r.registers;
}

/** GET /api/c/:slug — the PUBLIC lookup a stranger's browser makes.
 *
 * ⚠ No session, no tenant. Returns null for a handle nobody holds; a REVOKED
 * handle resolves with `accepting: false`, because a coach who turned their
 * link off must not look like one who never existed. */
export async function lookupCoachHandle(
  slug: string
): Promise<{ displayName: string; accepting: boolean; brand: string | null } | null> {
  try {
    const r = await request<{
      coach: { displayName: string };
      accepting: boolean;
      /** P4 — the COACH's household name when their tier includes Pro's
       * de-branding; null on every other tier and on an unresolved one, meaning
       * "wear the deployment's product name". Same contract as the reviewer
       * portal's `brand`. Optional on the wire so a client ahead of a server
       * that has not shipped this reads null rather than undefined. */
      brand?: string | null;
    }>(`/api/c/${encodeURIComponent(slug)}`);
    return { displayName: r.coach.displayName, accepting: r.accepting, brand: r.brand ?? null };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// THE MAIL SURFACE (2026-09-02) — what reached the household, and what left.
//
// ⚑ A FOURTH ADDRESS QUESTION, AND IT IS NONE OF THE OTHER THREE. `getMailbox`
// answers the free inbound HANDLE; `getWorkAddress` answers the WORK address;
// `getByoMailbox` answers a mailbox the family already had. These two read what
// actually CAME THROUGH them and what actually went out — the surface those
// three addresses existed for and never had.
//
// ⚠ THE TWO READS ARE SEPARATE BECAUSE THEIR TIERS ARE. Arrivals are free (the
// inbound handle is), the sent half is Working (`email.outbound`). One route
// would mean a below-tier household lost the arrivals list to a 403 about a
// feature it was not asking for.
// ---------------------------------------------------------------------------

/** One message that reached the household, and what it became.
 *
 * ⚠ FACTS, NOT SENTENCES. The server sends what happened; the words on the
 * screen are HouseholdMail's, because they are this pack's words and the
 * server serves three packs. */
export interface MailArrivalApi {
  /** The proposal the message raised — the arrival's identity. */
  id: string;
  from: string | null;
  subject: string | null;
  arrivedAt: string;
  /** 'forwarded' (the household's own address) or 'connected_mailbox' (the one
   * they connected on Pro). Null on a row written before the bridge named it. */
  carrier: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  /** The plan does not include reading this kind of document: it was kept in
   * full and nothing read it. A fact about a plan, never a failure. */
  keptOnly: boolean;
  /** Kept in full and NOT READ YET — the reading could not run, or the day's
   * reading was used up. Something is coming back for it. Mutually exclusive
   * with `keptOnly`: one ends in an upgrade, the other in a retry. */
  notReadYet: boolean;
  summary: string | null;
  /** The server's own sentence about why — including the not-included teaser,
   * which was written from the first day and shown on no screen. */
  rationale: string | null;
  /** What it became once somebody approved it. Null while it is still waiting,
   * and null when the approval created no record. */
  became: { recordType: string; recordId: string; name: string } | null;
  /** The original, as it arrived. Every tier can open this: it is the
   * household's own document. */
  sourceId: string | null;
}

/** GET /api/household/mail — free on every tier, like the address itself. */
export async function listHouseholdMail(tenantId: string): Promise<MailArrivalApi[]> {
  const result = await request<{ received: MailArrivalApi[] }>('/api/household/mail', { tenantId });
  return result.received;
}

/**
 * GET /api/household/mail/ever — B7 (2026-09-24, `defa9aed`, item 4): has
 * this household EVER received a notice. A single yes/no, not the full
 * arrivals list `listHouseholdMail` above reads — Today asks this on every
 * visit, to decide whether its empty "Needs you" state should offer the
 * household's own forwarding address instead. Free on every tier, same as
 * the address itself.
 */
export async function hasEverReceivedMail(tenantId: string): Promise<boolean> {
  const result = await request<{ everReceived: boolean }>('/api/household/mail/ever', { tenantId });
  return result.everReceived;
}

export interface MailSentApi {
  id: string;
  subject: string | null;
  sentAt: string;
}

/**
 * GET /api/household/mail/sent — Working and up.
 *
 * ⚠ 403 `feature_not_in_plan` BELOW TIER, and the screen renders that as the
 * pack's own line rather than a refusal — the family is not being told off.
 *
 * ⚠ THERE IS NO RECIPIENT ON A SENT ROW, and that is the thread spine's shape:
 * `messages` records who SAID a thing and has no recipient column. The address
 * is named in the receipt at the moment of sending, where it is known.
 */
export async function listHouseholdMailSent(
  tenantId: string
): Promise<{ workAddress: string | null; sent: MailSentApi[] }> {
  return request<{ workAddress: string | null; sent: MailSentApi[] }>('/api/household/mail/sent', {
    tenantId,
  });
}

/**
 * POST /api/household/mail/review-link — send one reviewer link from the work
 * address.
 *
 * ⚑ THE TOKEN CROSSES, NEVER A URL. Only `token_hash` is stored, so the server
 * cannot rebuild a link for a grant it already minted — the raw token exists
 * once, in the mint's own answer, which the panel is still holding. Sending the
 * token lets the server prove the link is live and this household's, and
 * compose the address itself; sending a URL would make this a door that mails
 * anything anywhere.
 *
 * `recorded: false` means THE MAIL WENT and only the thread row lagged. It is
 * never a reason to send again.
 */
export async function emailReviewLink(
  tenantId: string,
  input: { token: string; to: string; note?: string }
): Promise<{ sentTo: string; sentFrom: string; subject: string; recorded: boolean }> {
  const body: Record<string, unknown> = { token: input.token, to: input.to };
  if (input.note?.trim()) body['note'] = input.note.trim();
  return request('/api/household/mail/review-link', { method: 'POST', tenantId, body });
}

// ---------------------------------------------------------------------------
// THE PUBLIC PRODUCTION LOOKUP (S, 2026-09-08)
//
// Where a production's facts and its picture come from. WIKIDATA ONLY, with
// the art coming from Wikimedia Commons through the Wikidata item — Huan's
// ruling the same day: the commercial catalogues are not feasible for this
// product on licensing, and IMDb never was.
//
// ⚑ THE ATTRIBUTION IS DATA, NOT DECORATION. Two sentences, and they are
// separate because they carry different licences: the FACTS are
// "Data from Wikidata (CC0)", and the ART has its own line naming its author
// and its own licence. A surface that rendered one sentence for both would be
// wrong about one of them.
// ---------------------------------------------------------------------------

export type ProductionKindApi = 'series' | 'feature' | 'commercial' | 'short' | 'other';

/** WHAT the art is. A logo must be CONTAINED on a tile and a still may be
 * cropped, so the tile is told rather than left to guess. */
export type ProductionImageRoleApi = 'poster' | 'still' | 'logo';

export interface ProductionImageApi {
  role: ProductionImageRoleApi;
  /** The Commons file title, e.g. 'File:Reacher series title card.png'. */
  file: string;
  license: string | null;
  author: string | null;
  /** The Commons page — where a person checks the licence for themselves. */
  creditUrl: string;
  /** The credit line to print wherever this image is shown. */
  attribution: string;
}

export interface ProductionCandidateApi {
  source: 'wikidata';
  /** The Wikidata item id, e.g. 'Q109901438'. */
  id: string;
  title: string;
  year: number | null;
  kind: ProductionKindApi;
  company: string | null;
  synopsis: string | null;
  /** Present when this production has a freely-licensed image. Absent is the
   * COMMON case and a designed state — never a placeholder, never a hotlink. */
  image?: ProductionImageApi & { url: string; width: number | null };
  attribution: string;
  score: number;
  /** ⚑ OUR OWN PATH, never Wikimedia's URL. The app's CSP is
   * `img-src 'self'`, so a third-party <img> is blocked with NO visible error
   * — and widening the header would tell Wikimedia which productions a family
   * is looking up. The server proxies instead. */
  thumbPath?: string;
}

export interface ProductionLookupApi {
  title: string;
  year: number | null;
  candidates: ProductionCandidateApi[];
  /** What could not be asked, with the reason in a sentence. EMPTY IS NOT THE
   * SAME AS UNAVAILABLE: an empty candidate list means Wikidata knows of no
   * such production, and this array is how the screen tells the two apart. */
  unavailable: { source: string; reason: string }[];
  /** Always present: "Data from Wikidata (CC0)". */
  attribution: string;
}

/** GET /api/household/projects/:id/lookup — READ-ONLY. Browsing candidates
 * cannot change a record; nothing is written until somebody confirms one. */
export async function lookupProduction(
  tenantId: string,
  projectId: string,
  input: { title?: string; year?: number | null } = {}
): Promise<ProductionLookupApi> {
  const qs = new URLSearchParams();
  if (input.title?.trim()) qs.set('title', input.title.trim());
  if (input.year !== undefined && input.year !== null) qs.set('year', String(input.year));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<ProductionLookupApi>(`/api/household/projects/${projectId}/lookup${suffix}`, { tenantId });
}

export interface ProductionApplyApi {
  project: RecordRow;
  matched: {
    source: 'wikidata';
    id: string;
    title: string;
    year: number | null;
    kind: ProductionKindApi;
    company: string | null;
    genres: string[];
    episodeCount: number | null;
    url: string;
    attribution: string;
  };
  /** Null when this production has no freely-licensed art. */
  image: ProductionImageApi | null;
  imageAttached: boolean;
  /** The server's own sentence, naming both sides of the match and what
   * happened to the art. Rendered verbatim — the screen does not rewrite a
   * receipt. */
  receipt: string;
}

/** POST /api/household/projects/:id/lookup/apply — the ONLY write. */
export async function applyProductionLookup(
  tenantId: string,
  projectId: string,
  chosen: { source: 'wikidata'; id: string }
): Promise<ProductionApplyApi> {
  return request<ProductionApplyApi>(`/api/household/projects/${projectId}/lookup/apply`, {
    method: 'POST',
    tenantId,
    body: chosen,
  });
}

/** A candidate thumbnail an <img> can be pointed at. `?tenant=` rides along
 * for the same reason it does on the record-file door: a tag cannot set
 * X-Tenant-Id, and the session cookie is still what proves who is asking. */
export function productionThumbUrl(tenantId: string, thumbPath: string): string {
  const joiner = thumbPath.includes('?') ? '&' : '?';
  return `${thumbPath}${joiner}tenant=${encodeURIComponent(tenantId)}`;
}

/* ------------------------------------------------------------------ *
 * B20a — SUBMISSIONS: the front door before the audition.
 *
 * "opportunity → materials sent → invited → audition → engagement" — the
 * spine everyone without an agent lives on permanently (design of record
 * `calltowrap-the-front-door-…`, §3/§4.1). The engine and the F1/F2/F3
 * routes below are already real (packages/server/src/plugins/
 * actorSubmissions.ts, actorSubmissionComposer.ts, actorSubmissionLinkShare
 * .ts). The CONTRACT's new/changed routes — `from-link`, `:id/provenance`,
 * and `GET :id`'s `provenance`/`canSend`/`replies` fields — are B20b's
 * (packages/server/src/plugins/actorSubmissions.ts again, extended in a
 * parallel worktree): this file codes against that contract and every
 * B20a test mocks it at this boundary.
 * ------------------------------------------------------------------ */

export interface SubmissionProvenanceApi {
  grade: 'url' | 'artifact' | 'statement';
  url?: string;
  statement?: string;
  capturedAt: string;
}

/** B20b contract §3: `{ok:true}` or a named refusal with the server's own
 * sentence — shown verbatim, never re-worded. */
export type SubmissionCanSendApi = { ok: true } | { ok: false; reason: string; message: string };

export interface SubmissionReplyApi {
  sourceId: string;
  from: string;
  receivedAt: string;
  subject: string;
}

/** GET /api/actor/submissions/:id (and each row of the list) —
 * actorSubmissions.ts's own `SubmissionView`, plus the three contract
 * fields B20b adds to the detail read. Those three are OPTIONAL here
 * (undefined on a server that has not shipped them yet): a screen coded
 * against an absent `canSend` treats sending as not-yet-decidable rather
 * than crashing. */
export interface SubmissionApi {
  id: string;
  opportunity: string | null;
  channel: string | null;
  sentAt: string | null;
  outcome: string | null;
  notes: string | null;
  lifecycleState: string | null;
  materialCount: number;
  convertedToAuditionId: string | null;
  createdAt: string;
  provenance?: SubmissionProvenanceApi | null;
  canSend?: SubmissionCanSendApi;
  replies?: SubmissionReplyApi[];
}

/** POST /api/actor/submissions — creatable from nothing but an opportunity
 * string (F1 §1's own rule). B20d: `contactName`/`contactEmail`/`roleName`/
 * `projectTitle`/`dueAt` are the manual sheet's own fields, resolved
 * server-side (`attachSubmissionDefaults`) — never composed into `notes`
 * here. */
export async function createSubmission(
  tenantId: string,
  fields: {
    opportunity?: string;
    channel?: string;
    notes?: string;
    contactName?: string;
    contactEmail?: string;
    roleName?: string;
    projectTitle?: string;
    dueAt?: string;
  }
): Promise<SubmissionApi> {
  const result = await request<{ submission: SubmissionApi }>('/api/actor/submissions', {
    method: 'POST',
    tenantId,
    body: fields,
  });
  return result.submission;
}

/** GET /api/actor/submissions/:id */
export async function getSubmission(tenantId: string, id: string): Promise<SubmissionApi> {
  const result = await request<{ submission: SubmissionApi }>(`/api/actor/submissions/${id}`, { tenantId });
  return result.submission;
}

/** POST /api/actor/submissions/:id/send — "I sent it myself": records
 * `sent_at` and moves draft -> sent. No mail, no link (F1's own header). */
export async function markSubmissionSentByHand(tenantId: string, id: string): Promise<SubmissionApi> {
  const result = await request<{ submission: SubmissionApi }>(`/api/actor/submissions/${id}/send`, {
    method: 'POST',
    tenantId,
    body: {},
  });
  return result.submission;
}

export interface SubmissionConvertResultApi {
  source: RecordRow;
  target: RecordRow;
  link: LinkRow;
}

/** POST /api/actor/submissions/:id/convert. The server refuses (422
 * `submission_role`) when no role is linked yet — the caller shows that
 * sentence and offers to pick a part, never a second guess at the rule. */
export async function convertSubmissionToAudition(
  tenantId: string,
  id: string
): Promise<SubmissionConvertResultApi> {
  return request(`/api/actor/submissions/${id}/convert`, { method: 'POST', tenantId, body: {} });
}

// ---- F2: the portfolio, and the composer ----

export type PortfolioGroupApi = 'headshot' | 'reel' | 'resume' | 'other';

export interface PortfolioItemApi {
  id: string;
  kind: string | null;
  group: PortfolioGroupApi;
  label: string;
  addedAt: string;
  durationSeconds: number | null;
  hasFile: boolean;
}

export interface PortfolioApi {
  subjectId: string;
  items: PortfolioItemApi[];
  groups: Record<PortfolioGroupApi, PortfolioItemApi[]>;
}

/** GET /api/actor/subjects/:id/portfolio — every sendable piece, regardless
 * of age and regardless of the profile's current pointers (F2's own
 * ruling: portfolio, not profile). */
export async function getPortfolio(tenantId: string, subjectId: string): Promise<PortfolioApi> {
  return request(`/api/actor/subjects/${subjectId}/portfolio`, { tenantId });
}

export interface SetSubmissionMaterialsResultApi {
  submissionId: string;
  materialIds: string[];
  materialCount: number;
  receipt: string;
}

/** PUT /api/actor/submissions/:id/materials — REPLACE, not append: the
 * artist's last word wins. `mediaIds` order IS the chosen order. */
export async function setSubmissionMaterials(
  tenantId: string,
  id: string,
  mediaIds: string[]
): Promise<SetSubmissionMaterialsResultApi> {
  return request(`/api/actor/submissions/${id}/materials`, {
    method: 'PUT',
    tenantId,
    body: { mediaIds },
  });
}

/** B20c contract: ONE composer. `message` is the editable cover message
 * (the artist's own `cover_note`, or the server's template when unset) —
 * NEVER the whole email. `preview` is the whole email exactly as the send
 * transport composes it (`composeSubmissionBody`, server-side) — this is
 * what the screen shows AS the email, never `message` alone. `copyText` is
 * what "Copy the email instead" copies: the message, the link, the expiry
 * — no unsubscribe line, no attribution. `linkToken` is the raw token of
 * the ONE link this draft mints (or reuses, on a second draft) — the SAME
 * token `send-email` takes; nothing mints a second one. */
export interface SubmissionDraftApi {
  subject: string;
  message: string;
  recipient: string | null;
  linkToken: string;
  linkUrl: string;
  linkExpiresAt: string;
  materialsCount: number;
  preview: string;
  copyText: string;
}

/** POST /api/actor/submissions/:id/draft — a PROPOSAL, never a send. Mints
 * an F3 link (or reuses the still-valid, unspent one from a prior draft)
 * and composes from a template unless the artist wrote their own
 * (`cover_note`) — never a model, F2's own rule. */
export async function draftSubmissionMessage(tenantId: string, id: string): Promise<SubmissionDraftApi> {
  const result = await request<{ draft: SubmissionDraftApi }>(`/api/actor/submissions/${id}/draft`, {
    method: 'POST',
    tenantId,
    body: {},
  });
  return result.draft;
}

/** PUT /api/actor/submissions/:id/cover-note — saves the artist's OWN
 * message. Gated to Working and above (`submissions.cover_note`); Free
 * gets `feature_not_in_plan` and stays on the template. */
export async function saveSubmissionCoverNote(
  tenantId: string,
  id: string,
  coverNote: string
): Promise<{ submissionId: string; coverNote: string | null }> {
  return request(`/api/actor/submissions/${id}/cover-note`, {
    method: 'PUT',
    tenantId,
    body: { coverNote },
  });
}

// ---- F3: the links — fourteen days, real receipts ----

export interface SubmissionLinkReceiptApi {
  event: 'opened' | 'downloaded';
  fileId: string | null;
  occurredAt: string;
}

export interface SubmissionLinkSummaryApi {
  grantId: string;
  status: 'active' | 'expired' | 'revoked';
  expiresAt: string;
  createdAt: string;
  fileIds: string[];
  receipts: SubmissionLinkReceiptApi[];
}

export interface MintedSubmissionLinkApi {
  grantId: string;
  /** Returned ONCE, at mint or reissue, and never again. */
  token: string;
  url: string;
  expiresAt: string;
  files: { id: string; filename: string | null; mimeType: string }[];
}

/** POST /api/actor/submissions/:id/link */
export async function mintSubmissionLinkApi(
  tenantId: string,
  id: string,
  fileIds: string[]
): Promise<MintedSubmissionLinkApi> {
  return request(`/api/actor/submissions/${id}/link`, { method: 'POST', tenantId, body: { fileIds } });
}

/** GET /api/actor/submissions/:id/links — every link + its receipts. NEVER
 * a token. */
export async function listSubmissionLinksApi(tenantId: string, id: string): Promise<SubmissionLinkSummaryApi[]> {
  const result = await request<{ links: SubmissionLinkSummaryApi[] }>(`/api/actor/submissions/${id}/links`, {
    tenantId,
  });
  return result.links;
}

/** POST /api/actor/submissions/links/:grantId/revoke — idempotent. */
export async function revokeSubmissionLinkApi(tenantId: string, grantId: string): Promise<void> {
  await request(`/api/actor/submissions/links/${grantId}/revoke`, { method: 'POST', tenantId, body: {} });
}

/** POST /api/actor/submissions/links/:grantId/reissue — "Send a fresh
 * link": a NEW token for the same files. Never revives the old one. */
export async function reissueSubmissionLinkApi(tenantId: string, grantId: string): Promise<MintedSubmissionLinkApi> {
  return request(`/api/actor/submissions/links/${grantId}/reissue`, { method: 'POST', tenantId, body: {} });
}

// ---- The CONTRACT's new/changed routes — B20b builds these; mocked here ----

export interface SubmissionFromLinkPrefillApi {
  opportunity: string | null;
  projectTitle?: string;
  roleName?: string;
  discipline?: string;
  contactName?: string;
  contactEmail?: string;
  dueAt?: string;
}

export interface SubmissionFromLinkResultApi {
  submission: SubmissionApi;
  provenance: SubmissionProvenanceApi;
  prefilled: SubmissionFromLinkPrefillApi;
}

/** POST /api/actor/submissions/from-link {url} — 201 with the draft and its
 * `url`-graded provenance, or 422 `link_unreadable` (the caller falls back
 * to the manual fields — see SubmissionStartSheet). */
export async function createSubmissionFromLink(tenantId: string, url: string): Promise<SubmissionFromLinkResultApi> {
  return request('/api/actor/submissions/from-link', { method: 'POST', tenantId, body: { url } });
}

// ---- F4 — the send itself (already real: actorSubmissionSending.ts).
// `submissions.sending_enabled` gates it OFF by default; a refusal here is
// `sending_disabled` until an operator flips that lever, which `canSend`
// already names. ----

export interface SendSubmissionEmailResultApi {
  sentTo: string[];
  /** TEST MODE: every send went to this address instead. */
  testRedirectTo?: string;
  failed?: { address: string; message: string }[];
  untraceable?: string[];
  from: string;
  replyTo: string;
  subject: string;
  sentToday: number;
  dailyMax: number;
  attachments: number;
}

/** POST /api/actor/submissions/:id/send-email. `linkToken` is F3's RAW
 * token (never a URL) — B20c fix: it is the SAME token the draft already
 * minted (`SubmissionDraftApi.linkToken`), never a second one minted here.
 * One link per submission, reused by the send. */
export async function sendSubmissionEmail(
  tenantId: string,
  id: string,
  body: { to: string[]; subject: string; message: string; replyTo?: string; linkToken?: string }
): Promise<SendSubmissionEmailResultApi> {
  return request(`/api/actor/submissions/${id}/send-email`, { method: 'POST', tenantId, body });
}

export type SetSubmissionProvenanceBody =
  | { grade: 'artifact'; mediaId: string }
  | { grade: 'statement'; statement: string };

/** POST /api/actor/submissions/:id/provenance — "Where did you find it?",
 * for the Free-tier send gate (`259f0564`). */
export async function setSubmissionProvenance(
  tenantId: string,
  id: string,
  body: SetSubmissionProvenanceBody
): Promise<{ provenance: SubmissionProvenanceApi }> {
  return request(`/api/actor/submissions/${id}/provenance`, { method: 'POST', tenantId, body });
}

// ---------------------------------------------------------------------------
// Audience — the ninth cockpit tab (decision `4804fc96`, spec
// `the-audience-build-spec-2026-10-08`). Marketing lives here; customers
// live in Conversations — only a PROMOTED inbox item reaches that thread.
//
// Shapes pinned to the API CONTRACT the server chunk (AUD-S) owns
// (`packages/server/src/plugins/audience.ts`, built in a sibling worktree).
// This web chunk mocks every one of these in its own tests rather than
// waiting on that worktree to land.
// ---------------------------------------------------------------------------

/** The contract's own account shape — deliberately narrower than
 * ConnectedAccountApi: the summary answers exactly these five fields, and a
 * type that claimed the rest would be a guess this screen never checks. */
export interface AudienceAccountApi {
  id: string;
  platform: string;
  handle: string;
  status: string;
  providerAccountRef: string;
}

export interface AudiencePlatformReceiptApi {
  platform: string;
  status: string;
  publishedAt: string | null;
  url: string | null;
}

export interface AudiencePostedApi {
  key: string;
  title: string;
  imageUrl: string | null;
  createdAt: string;
  source: 'core' | 'zernio';
  proposalId: string | null;
  platforms: AudiencePlatformReceiptApi[];
}

export interface AudienceWaitingApi {
  proposalId: string;
  title: string;
  createdAt: string;
}

export interface AudienceSummaryApi {
  tiles: {
    waitingForYes: number;
    scheduled: number;
    postedThisWeek: number;
    unanswered: number;
  };
  accounts: AudienceAccountApi[];
  posted: AudiencePostedApi[];
  waiting: AudienceWaitingApi[];
}

/** GET /api/audience/summary — the home tiles, the Posted list, the Doors
 * list and the waiting-for-yes line. */
export async function getAudienceSummary(tenantId: string): Promise<AudienceSummaryApi> {
  return request('/api/audience/summary', { tenantId });
}

export type SocialItemState = 'needs_reply' | 'answered' | 'promoted' | 'hidden' | 'not_customer';

export interface SocialItemApi {
  id: string;
  platform: string;
  kind: 'comment' | 'dm';
  accountHandle: string;
  authorName: string;
  authorUsername: string;
  body: string;
  postTitle: string | null;
  postPermalink: string | null;
  receivedAt: string;
  state: SocialItemState;
  draftReply: string | null;
  repliedAt: string | null;
  replyText: string | null;
  promotedPartyId: string | null;
  promotedThreadId: string | null;
  promotedAt: string | null;
}

export interface AudienceInboxCountsApi {
  needs_reply: number;
  answered: number;
  promoted: number;
  hidden: number;
  not_customer: number;
}

export type AudienceInboxFilter = SocialItemState | 'all';

/** GET /api/audience/inbox — `state` omitted (or 'all') is the contract's
 * own "every state" query. */
export async function listAudienceInbox(
  tenantId: string,
  state: AudienceInboxFilter = 'all'
): Promise<{ items: SocialItemApi[]; counts: AudienceInboxCountsApi }> {
  const qs = state === 'all' ? '' : `?state=${state}`;
  return request(`/api/audience/inbox${qs}`, { tenantId });
}

/** GET /api/audience/inbox/:id — one item, for a direct link/reload. */
export async function getAudienceInboxItem(tenantId: string, id: string): Promise<SocialItemApi> {
  const result = await request<{ item: SocialItemApi }>(`/api/audience/inbox/${id}`, { tenantId });
  return result.item;
}

/** POST /api/audience/inbox/:id/draft — fills the reply BOX, never sends.
 * A 501 (no model helper wired) is the server's own sentence; this throws
 * ApiError straight through so the screen can show it verbatim rather than
 * inventing a second one. */
export async function draftAudienceReply(tenantId: string, id: string): Promise<string> {
  const result = await request<{ draft: string }>(`/api/audience/inbox/${id}/draft`, {
    method: 'POST', tenantId, body: {},
  });
  return result.draft;
}

/** POST /api/audience/inbox/:id/reply — the one door that actually sends.
 * A person pressed Send; replies never send themselves. */
export async function sendAudienceReply(
  tenantId: string,
  id: string,
  text: string
): Promise<{ item: SocialItemApi; receipt: string }> {
  return request(`/api/audience/inbox/${id}/reply`, { method: 'POST', tenantId, body: { text } });
}

export async function hideAudienceItem(
  tenantId: string,
  id: string
): Promise<{ item: SocialItemApi; receipt: string }> {
  return request(`/api/audience/inbox/${id}/hide`, { method: 'POST', tenantId, body: {} });
}

export async function markAudienceNotCustomer(
  tenantId: string,
  id: string
): Promise<{ item: SocialItemApi; receipt: string }> {
  return request(`/api/audience/inbox/${id}/not-customer`, { method: 'POST', tenantId, body: {} });
}

export async function reopenAudienceItem(tenantId: string, id: string): Promise<{ item: SocialItemApi }> {
  return request(`/api/audience/inbox/${id}/reopen`, { method: 'POST', tenantId, body: {} });
}

/** POST /api/audience/inbox/:id/promote — ONE person reaching Conversations
 * for the first time. `displayName`/`threadSubject` are both optional
 * overrides; the server names them when absent. */
export async function promoteAudienceItem(
  tenantId: string,
  id: string,
  body: { displayName?: string; threadSubject?: string } = {}
): Promise<{ item: SocialItemApi; partyId: string; threadId: string; receipt: string }> {
  return request(`/api/audience/inbox/${id}/promote`, { method: 'POST', tenantId, body });
}

/** POST /api/audience/inbox/:id/unpromote — the Undo on a promote receipt.
 * The server refuses past its own 10-minute window with a 409 and its own
 * sentence (ApiError.serverMessage) — this never guesses a replacement. */
export async function unpromoteAudienceItem(
  tenantId: string,
  id: string
): Promise<{ item: SocialItemApi; receipt: string }> {
  return request(`/api/audience/inbox/${id}/unpromote`, { method: 'POST', tenantId, body: {} });
}

export interface AudienceWeekCellApi {
  title: string;
  status: string;
  at: string;
  url: string | null;
}

export interface AudienceWeekRowApi {
  platform: string;
  cells: AudienceWeekCellApi[][];
}

export interface AudienceWeekApi {
  start: string;
  days: string[];
  rows: AudienceWeekRowApi[];
}

/** GET /api/audience/week — `start` is an ISO date (YYYY-MM-DD), the
 * week's first day. */
export async function getAudienceWeek(tenantId: string, start: string): Promise<AudienceWeekApi> {
  return request(`/api/audience/week?start=${start}`, { tenantId });
}

// ---------------------------------------------------------------------------
// Programmes — Chunk PRG-W (plates: Plan 03, Programme, PhoneProgramme,
// PhoneReview). Server half (PRG-S) builds in the sibling worktree
// `wt-programme-core` against the SAME contract (SPEC.md's "API CONTRACT —
// programmes"); this chunk mocks every one of these in its own tests rather
// than waiting on that worktree to land. Shapes are pinned to the contract
// exactly — see this file's own header note on why that posture is safe
// (Audience did the same thing first).
//
// ⚑ ONE CONTRACT ASSUMPTION, NAMED (the server builder owns the real
// answer): the pending proposal `propose.ts` creates (kind/target_type
// `programme`) carries a `proposed` jsonb whose shape the contract text
// describes only in prose ("the does/doesNot and a step summary"). This
// chunk assumes it matches `ProgrammeProposedApi` below — the same fields
// Plan.dc.html's card shows, nothing invented past them. If the server
// lands a different shape, ProgrammeProposed in Review.tsx is the one
// place that needs to change.
// ---------------------------------------------------------------------------

export type ProgrammeKind = 'make' | 'publish' | 'send' | 'approach' | 'record' | 'measure' | 'review' | 'other';
export type ProgrammeOwner = 'huan' | 'atlas' | 'dell' | 'zernio' | 'mail' | 'other';
export type ProgrammeGate = 'none' | 'yes' | 'consented';
export type ProgrammeRunState = 'planned' | 'drafting' | 'waiting_yes' | 'live' | 'done' | 'skipped' | 'missed' | 'failed';
export type ProgrammeState = 'proposed' | 'running' | 'paused' | 'completed' | 'withdrawn' | 'declined';

export interface ProgrammeScorecardColumnApi {
  key: string;
  label: string;
  source: 'typed' | 'door' | 'ledger' | 'computed';
}

export interface ProgrammeScorecardCellApi {
  value: string | number | null;
  source: 'typed' | 'door' | 'ledger' | 'computed';
  by: string | null;
  at: string | null;
}

export interface ProgrammeStepApi {
  id: string;
  key: string;
  title: string;
  kind: ProgrammeKind;
  owner: ProgrammeOwner;
  ownerLabel: string | null;
  gate: ProgrammeGate;
  rrule: string | null;
  dtstart: string | null;
  dueAt: string | null;
  timezone: string;
  status: 'proposed' | 'active' | 'paused' | 'retired';
  notes: string | null;
}

export interface ProgrammeApi {
  id: string;
  title: string;
  state: ProgrammeState;
  accountId: string;
  objective: string;
  guards: string[];
  capCents: number | null;
  capCurrency: string | null;
  capUntil: string | null;
  reviewDates: string[];
  dayN: number | null;
  dayTotal: number | null;
  startedOn: string | null;
  proposalId: string | null;
  does: string;
  doesNot: string;
  notYetReady: string[];
  steps: ProgrammeStepApi[];
}

export interface ProgrammeCardApi {
  id: string;
  title: string;
  state: ProgrammeState;
  dayN: number | null;
  dayTotal: number | null;
  waitingForYes: number;
  missed: number;
}

export interface ProgrammeOutcomeTileApi {
  measure: string;
  unit: string | null;
  baselineValue: number | null;
  targetValue: number | null;
  targetBy: string | null;
  currentValue: number | string | null;
  // Set when this outcome's current value is typed by a person rather than
  // read from a door — Plan/Programme plates' own "typed by you until <door>"
  // caption (never shown as a plain number with no source word).
  typedUntil: string | null;
}

export interface ProgrammeTilesApi {
  waitingForYes: number;
  liveThisWeek: { done: number; planned: number };
  budget: { spentCents: number; capCents: number | null; currency: string };
  hoursThisWeek: number | null;
  missed: number;
}

export interface ProgrammeDetailApi {
  programme: ProgrammeApi;
  outcomes: ProgrammeOutcomeTileApi[];
  tiles: ProgrammeTilesApi;
}

export interface ProgrammeRunApi {
  id: string;
  stepId: string;
  stepKey: string;
  title: string;
  kind: ProgrammeKind;
  owner: ProgrammeOwner;
  ownerLabel: string | null;
  gate: ProgrammeGate;
  dueAt: string;
  state: ProgrammeRunState;
  proposalId: string | null;
  proposalStatus: string | null;
  documentId: string | null;
  taskId: string | null;
  note: string | null;
  receipt: string | null;
  doneAt: string | null;
}

export interface ProgrammeWeekByOwnerApi {
  owner: ProgrammeOwner;
  ownerLabel: string;
  lines: string[];
}

export interface ProgrammeWeekApi {
  start: string;
  days: string[];
  runs: ProgrammeRunApi[];
  byOwner: ProgrammeWeekByOwnerApi[];
  guards: { refused: number; promotionalReplies: number; spendCents: number; notes: string[] };
}

export interface ProgrammeScorecardApi {
  columns: ProgrammeScorecardColumnApi[];
  rows: Array<{ weekStart: string; cells: Record<string, ProgrammeScorecardCellApi> }>;
}

/** The does/doesNot/step-summary shape this chunk assumes the `programme`
 * proposal's `proposed` jsonb carries (this file's own header note). */
export interface ProgrammeProposedApi {
  programmeId: string;
  title: string;
  objective: string;
  measuresSummary: string;
  whoDoesWhat: string;
  cadenceSummary: Array<{ day: string; time: string | null; title: string; gate: ProgrammeGate }>;
  guards: string[];
  notYetReady: string[];
  does: string;
  doesNot: string;
}

/** GET /api/programmes — the Delivery list, one card per programme. */
export async function listProgrammes(tenantId: string): Promise<ProgrammeCardApi[]> {
  const result = await request<{ programmes: ProgrammeCardApi[] }>('/api/programmes', { tenantId });
  return result.programmes;
}

/** GET /api/programmes/:id — the programme screen's header + outcome tiles. */
export async function getProgramme(tenantId: string, id: string): Promise<ProgrammeDetailApi> {
  return request(`/api/programmes/${id}`, { tenantId });
}

/** GET /api/programmes/:id/week — the week grid + work-by-owner + guards. */
export async function getProgrammeWeek(tenantId: string, id: string, start: string): Promise<ProgrammeWeekApi> {
  return request(`/api/programmes/${id}/week?start=${start}`, { tenantId });
}

/** GET /api/programmes/:id/scorecard */
export async function getProgrammeScorecard(tenantId: string, id: string): Promise<ProgrammeScorecardApi> {
  return request(`/api/programmes/${id}/scorecard`, { tenantId });
}

/** PUT /api/programmes/:id/scorecard/:weekStart/:colKey — a typed cell only;
 * door/ledger cells are read-only here and carry their source word instead
 * of an input. */
export async function putProgrammeScorecardCell(
  tenantId: string,
  id: string,
  weekStart: string,
  colKey: string,
  value: string | number | null
): Promise<{ cell: ProgrammeScorecardCellApi }> {
  return request(`/api/programmes/${id}/scorecard/${weekStart}/${colKey}`, { method: 'PUT', tenantId, body: { value } });
}

/** POST /api/programmes/:id/approve — admin session only, never a service
 * key. The one door the Plan card's "Approve the programme" button opens. */
export async function approveProgramme(tenantId: string, id: string): Promise<{ programme: ProgrammeApi; receipt: string }> {
  return request(`/api/programmes/${id}/approve`, { method: 'POST', tenantId, body: {} });
}

/** POST /api/programmes/:id/decline {reason} — "Not now", reasoned. */
export async function declineProgramme(
  tenantId: string,
  id: string,
  reason: string
): Promise<{ programme: ProgrammeApi; receipt: string }> {
  return request(`/api/programmes/${id}/decline`, { method: 'POST', tenantId, body: { reason } });
}

async function programmeLifecycleCall(
  tenantId: string,
  id: string,
  action: 'pause' | 'resume' | 'complete' | 'withdraw',
  reason?: string
): Promise<{ programme: ProgrammeApi; receipt: string }> {
  return request(`/api/programmes/${id}/${action}`, { method: 'POST', tenantId, body: reason ? { reason } : {} });
}

export const pauseProgramme = (tenantId: string, id: string) => programmeLifecycleCall(tenantId, id, 'pause');
export const resumeProgramme = (tenantId: string, id: string) => programmeLifecycleCall(tenantId, id, 'resume');
export const completeProgramme = (tenantId: string, id: string) => programmeLifecycleCall(tenantId, id, 'complete');
export const withdrawProgramme = (tenantId: string, id: string, reason?: string) =>
  programmeLifecycleCall(tenantId, id, 'withdraw', reason);

/** PATCH /api/programmes/:id/steps/:stepId — admin only. */
export async function patchProgrammeStep(
  tenantId: string,
  id: string,
  stepId: string,
  patch: Partial<Pick<ProgrammeStepApi, 'title' | 'rrule' | 'dueAt' | 'owner' | 'ownerLabel' | 'gate' | 'status'>>
): Promise<{ step: ProgrammeStepApi }> {
  return request(`/api/programmes/${id}/steps/${stepId}`, { method: 'PATCH', tenantId, body: patch });
}

// ---------------------------------------------------------------------------
// Systems — Chunk SYS-W (plate: Systems). Server half (SYS-S) builds in the
// sibling worktree `wt-systems-core` against the SAME contract (SPEC.md's
// "API CONTRACT — systems"); the shapes below are pinned to
// `packages/core/src/systems/types.ts` and the plugin's own serialisation
// exactly, read from that worktree's `3360c33b` base rather than guessed.
// ---------------------------------------------------------------------------

// H3b (migration 0936) — 'projects' MUST stay equal to core's own
// SYSTEM_SECTIONS (packages/core/src/systems/types.ts); pinned by
// systemSections.test.ts.
export const SYSTEM_SECTIONS = ['agents', 'meters', 'fleet', 'doors', 'compliance', 'recovery', 'projects'] as const;
export type SystemSection = (typeof SYSTEM_SECTIONS)[number];

export type SystemFactState =
  | 'running' | 'idle' | 'parked' | 'up' | 'down' | 'pending' | 'none' | 'green'
  | 'matches' | 'drift' | 'open' | 'done' | 'waived' | 'proven' | 'unproven' | 'unknown';

export type SystemMeter = 'ok' | 'near' | 'at_cap' | null;

export interface SystemFactApi {
  section: SystemSection;
  key: string;
  label: string;
  state: SystemFactState;
  detail: string | null;
  valueNum: number | null;
  capNum: number | null;
  unit: string | null;
  dueOn: string | null;
  link: string | null;
  asOf: string;
  reportedBy: string;
  stale: boolean;
  staleSince: string | null;
  meter: SystemMeter;
}

export interface SystemsSummaryApi {
  sections: Record<SystemSection, SystemFactApi[]>;
  atCap: SystemFactApi[];
  stale: SystemFactApi[];
}

export interface FactHistoryEventApi {
  at: string;
  state: string;
  detail: string | null;
  reportedBy: string;
}

/** GET /api/systems/summary — every section the Systems screen reads. */
export async function getSystemsSummary(tenantId: string): Promise<SystemsSummaryApi> {
  return request('/api/systems/summary', { tenantId });
}

/** GET /api/systems/facts/:section/:key/history — a row's side sheet. */
export async function getFactHistory(
  tenantId: string,
  section: SystemSection,
  key: string,
  limit?: number
): Promise<FactHistoryEventApi[]> {
  const q = limit ? `?limit=${limit}` : '';
  const result = await request<{ events: FactHistoryEventApi[] }>(`/api/systems/facts/${section}/${key}/history${q}`, {
    tenantId,
  });
  return result.events;
}

/** POST /api/systems/compliance {label, dueOn?, detail?} — admin session
 * only (the plugin refuses a bearer key outright, this chunk's own route
 * table). */
export async function addComplianceFact(
  tenantId: string,
  args: { label: string; dueOn?: string | null; detail?: string | null }
): Promise<{ fact: SystemFactApi }> {
  return request('/api/systems/compliance', { method: 'POST', tenantId, body: args });
}

/** POST /api/systems/compliance/:key/close {note?} — admin session only. */
export async function closeComplianceFact(
  tenantId: string,
  key: string,
  note?: string | null
): Promise<{ fact: SystemFactApi; receipt: string }> {
  return request(`/api/systems/compliance/${key}/close`, { method: 'POST', tenantId, body: { note: note ?? null } });
}

// ---------------------------------------------------------------------------
// G1 — connected Google accounts (Personal › Accounts). Spec: wiki
// `spec-google-connections-in-vowric-core-calendar-contacts-tasks-one-drive-folder-`.
// The consent itself is a full-page navigation to the server's
// `/auth/google/connect` (Google must see the browser, not a fetch); these are
// the reads and writes around it.
// ---------------------------------------------------------------------------

export type GoogleFeatureApi = 'calendar' | 'contacts' | 'tasks' | 'drive' | 'mail';

export interface GoogleConnectionApi {
  id: string;
  email: string;
  status: 'active' | 'needs_reconnect' | 'revoked';
  statusReason: string | null;
  features: GoogleFeatureApi[];
  scopesGranted: string[];
  calendarsIn: Array<{
    id: string;
    summary: string;
    accessRole: string;
    primary: boolean;
    selected: boolean;
    /** S1b: Google's calendarList swatch, already validated by Core
     * (validateCalendarColor) to a strict `#rrggbb` or null. Validated AGAIN
     * before use here (useCalendarColor below) — two independent gates. */
    color?: string | null;
  }>;
  connectedAt: string;
  lastOkAt: string | null;
  revokedAt: string | null;
}

export interface GoogleConnectionsApi {
  /** A Google client is installed on this instance. */
  configured: boolean;
  /** This tenant's owner turned connections on. */
  enabled: boolean;
  /** G4: Mail may be turned on here (the instance allows Gmail AND this tenant's owner switched it on).
   * Absent on an older server — read as false. */
  mailAvailable?: boolean;
  connections: GoogleConnectionApi[];
  /** Where "Connect a Google account" sends the browser. */
  connectPath: string;
}

export async function getGoogleConnections(tenantId: string): Promise<GoogleConnectionsApi> {
  return request<GoogleConnectionsApi>('/api/google/connections', { tenantId });
}

export async function updateGoogleConnection(
  tenantId: string,
  id: string,
  body: { features?: GoogleFeatureApi[]; calendarsSelected?: string[] }
): Promise<GoogleConnectionApi> {
  const r = await request<{ connection: GoogleConnectionApi }>(`/api/google/connections/${id}`, { tenantId, method: 'PUT', body });
  return r.connection;
}

export async function reconnectGoogleConnection(tenantId: string, id: string): Promise<string> {
  const r = await request<{ connectPath: string }>(`/api/google/connections/${id}/reconnect`, { tenantId, method: 'POST' });
  return r.connectPath;
}

export async function disconnectGoogleConnection(
  tenantId: string,
  id: string
): Promise<{ connection: GoogleConnectionApi; revokedAtGoogle: boolean }> {
  return request(`/api/google/connections/${id}/disconnect`, { tenantId, method: 'POST' });
}

// ── Google events changed from the cockpit (atlas's brief 2026-10-12, d1cd7c74) ──

/** What a change to a Google event came to: done (with its receipt and the
 * change id its Undo takes), or proposed and waiting in Review. */
export type GoogleEventOutcome =
  | { outcome: 'done'; id: string; changeId: string; sendUpdates: 'none' | 'all'; writtenThroughOrganizer: string | null; guests: string[]; receipt: string }
  | { outcome: 'proposed'; id: string; proposalId: string; guests: string[]; receipt: string };

/** S1b: every write route can answer this instead of `event` — a structured
 * "say whether to tell them" the screen asks on the CODE, never by reading
 * the message. `tellGuests` is never defaulted anywhere past this type. */
export interface TellGuestsRequiredApi {
  code: 'tell_guests_required';
  message: string;
  guests: number;
}

export interface GoogleEventEditInput {
  title?: string;
  location?: string | null;
  notes?: string | null;
  start?: string;
  end?: string | null;
  timezone?: string;
  allDay?: boolean;
  tellGuests?: boolean;
}

/** PUT /api/calendar/google/:id — write a Google event back to Google. */
export async function editGoogleCalendarEvent(tenantId: string, id: string, edit: GoogleEventEditInput): Promise<{ event: GoogleEventOutcome }> {
  return request(`/api/calendar/google/${encodeURIComponent(id)}`, { method: 'PUT', tenantId, body: edit });
}

/** POST /api/calendar/google/:id/cancel {tellGuests?} — delete at Google. */
export async function cancelGoogleCalendarEvent(tenantId: string, id: string, tellGuests?: boolean): Promise<{ event: GoogleEventOutcome }> {
  return request(`/api/calendar/google/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    tenantId,
    body: tellGuests === undefined ? {} : { tellGuests },
  });
}

/** POST /api/calendar/google/:id/propose-to-organizer — item B's way forward
 * when the event is someone else's and we cannot write it: a note in
 * Review, nothing sent. */
export async function proposeGoogleEventToOrganizer(
  tenantId: string,
  id: string,
  edit: GoogleEventEditInput
): Promise<{ proposal: { proposalId: string; to: string; receipt: string } }> {
  const { tellGuests: _ignored, ...change } = edit;
  return request(`/api/calendar/google/${encodeURIComponent(id)}/propose-to-organizer`, { method: 'POST', tenantId, body: change });
}

/** POST /api/calendar/google/proposals/:id/approve {tellGuests} — the
 * assistant's proposed change, run as the person's own act with their answer
 * to "Tell the guests?". */
export async function approveGoogleEventChange(tenantId: string, proposalId: string, tellGuests: boolean | undefined): Promise<{ event: GoogleEventOutcome }> {
  return request(`/api/calendar/google/proposals/${proposalId}/approve`, {
    method: 'POST',
    tenantId,
    body: tellGuests === undefined ? {} : { tellGuests },
  });
}

/** POST /api/calendar/google/changes/:id/undo {tellGuests?} — the receipt's Undo. */
export async function undoGoogleEventChange(tenantId: string, changeId: string, tellGuests?: boolean): Promise<{ event: GoogleEventOutcome }> {
  return request(`/api/calendar/google/changes/${changeId}/undo`, {
    method: 'POST',
    tenantId,
    body: tellGuests === undefined ? {} : { tellGuests },
  });
}
